import "./style.css";

import { MODEL_IDS, getModel } from "./models";
import { defaultPlan } from "./models/initial-state";
import {
  FX_CHANNEL_NODE_INDEX,
  FX_EFFECT_TYPE_PARAM,
  fxTypeDefaults,
  resolveFxEffectType,
} from "./core/control/fx-effect";
import { takeFxReseeds } from "./app/fx-reseed";
import type { DeviceModel, ModelId } from "./models/types";
import { parseRef, ref } from "./models/types";
import {
  alignLinkedSendPans,
  panLinkedSendPan,
  applyPairTransition,
  INSERT_FX_PAIR_KEYS,
  mirrorLinkedPair,
  mirrorLinkedInsertFx,
  mixSendLocks,
  pairSharesNodeKey,
  isStereoLinkedPair,
  partnerChannel,
  resetMixSendBank,
  sendPansToSources,
  withLinkedPartner,
} from "./core/routing";
import {
  decodePlanParam,
  deserializeDocument,
  emptyPlan,
  encodePlanParam,
  ensureFixedConnections,
  normalizeNodeName,
  PlanError,
  processorOn,
  SDREC_NODE_ID,
  LEVEL_MIN_DB,
  serialize,
  changePlanSampleRate,
  insertsAboveRate,
  setPlanSampleRate,
  type InsertOffAtRate,
  SSMCS_INITIAL,
  supplyRequiredSources,
} from "./core/plan";
import {
  applySceneExternal,
  captureSceneExternal,
  isSceneExternalConnection,
  sceneExternalParamNames,
} from "./core/scene-scope";
import { getSettings } from "./core/settings";
import type { ConnParams, JackSide, NodeParams, Plan, SerializeOptions } from "./core/plan";
import { resetDisplayedSides, setDisplayedSide } from "./core/stereo-side";
import {
  applyPatch,
  clonePlanState,
  connectionContestKey,
  connParamContestKey,
  diffPlans,
  nodeParamContestPath,
  PlanWriteWitness,
  type PlanWriteWatch,
  type PatchTouch,
  patchContestNames,
  patchTouch,
  type PlanPatch,
} from "./core/plan-history";
import { formatRate, rateConstraints, SAMPLE_RATES, trackCountDrop } from "./core/constraints";
import {
  applyParamRange,
  isRefusal,
  needsDecision,
  paramRangeProblems,
  planProblems,
  prepareLoadedPlan,
} from "./core/plan-validate";
import { jackName, phantomHiZBothOn, switchAddr, switchOfKey } from "./core/input-lock";
import type { InputSwitch, SwitchSession } from "./core/input-lock";
import type { LoadProblem } from "./core/plan-validate";
import {
  baseName,
  downloadText,
  loadRecent,
  openBinaryDocument,
  openTextDocument,
  readTextByPath,
  rememberRecent,
  removeRecent,
  saveTextDocument,
} from "./core/storage";
import type { RecentEntry } from "./core/storage";
import {
  BUS_TYPE_FIXED,
  BUS_TYPE_VARI,
  COMP_EQ_SSMCS,
  INSERT_FX_ANNOUNCED,
  MIC_LINE_JACK_BASE,
  PARAMS,
  REC_POINT_PRE_COMP,
  REC_POINT_PRE_EQ,
} from "./core/control/params";
import { Graph } from "./ui/graph";
import type { ConnectOrigin, LabelSource, Selection, ThemeName } from "./ui/graph";
import { compositionGate, inspectorNodes, markClipSafe, renderInspector, syncInspectorCue } from "./ui/inspector";
import { copyText, focusables, preserveFocus } from "./ui/dom";
import { setSaid, setSpokenText, setTip, spoken } from "./ui/glyph";
import { ownsNativeUndo } from "./ui/keys";
import { Console } from "./ui/console";
import { SimView } from "./ui/sim-view";
import type { SimEdit, SimValue } from "./core/sim-map";
import { fxHeldWrites } from "./core/sim-map-processors";
import { rateHold, rateHoldReason, simLocks, type SimLockReasons, type SimLockState } from "./core/sim-locks";
import { MidiControl } from "./ui/midi";
import { showConsent } from "./ui/consent";
import { initDropzone } from "./ui/dropzone";
import { initFineMode } from "./ui/fine";
import { installEditMenu } from "./ui/edit-menu";
import { PlanHistory } from "./ui/history";
import { installKeyProbe } from "./ui/keyprobe";
import { paramWatch } from "./ui/param-watch";
import { closeLoadReport, showLoadReport } from "./ui/load-report";
import { showErrorBox } from "./ui/error-box";
import { showLicenses } from "./ui/licenses";
import { PrefsPanel } from "./ui/prefs";
import { DynScreen } from "./ui/dyn-screen";
import type { MidiLearnHooks } from "./ui/midi-learn";
import { DYN_PROCESSORS } from "./ui/dyn-registry";
import type { DynKind } from "./ui/dyn-registry";
import type { ThemeMode, UpdateCheckOutcome } from "./ui/prefs";
import { FileFlowLatch, singleFlight } from "./app/flow-latch";
import { nodeParamEffects } from "./app/node-param-effects";
import { changesLinkState } from "./app/link-state-change";
import { undoRefusal } from "./app/undo-refusal";
import {
  detectHideOffSends,
  detectLabelSource,
  detectModel,
  detectRate,
  detectSimScale,
  detectThemeMode,
  detectView,
  loadHidden,
  rememberHideOffSends,
  rememberHidden,
  rememberLabelSource,
  rememberModel,
  rememberRate,
  rememberSimScale,
  rememberThemeMode,
  resetStorageFromUrl,
  rememberView,
  resolveTheme,
  seedEmptyRequested,
  SIM_SCALES,
} from "./app/view-state";
import type { SimScale, ViewName } from "./app/view-state";
import { errorCode, errorText, getLang, LANG_NAMES, onLangChange, t } from "./i18n";
import { DEMO, TRACE } from "./core/env";
import { installTraceProbe } from "./ui/trace-probe";
import type { WriteSource } from "./ui/trace-probe";
import {
  checkUpdate,
  confirmDialog,
  errorDialog,
  exitApp,
  installUpdate,
  isTauri,
  prepareForExit,
  restartApp,
  experimentalEnabled,
  selfTestRequested,
  prepareModifiedRequested,
  resetStorageRequested,
  setKeepAwake,
  thirdPartyLicenses,
  vdConnect,
  vdDisconnect,
  vdGet,
  vdSet,
  vdWatchLink,
  type Connection,
  type DeviceSummary,
  type UpdateInfo,
} from "./core/platform";
import {
  applyDeviceState,
  applyDirect,
  applyNodeState,
  applySilentState,
  applySourceState,
  formatReadbackReport,
  heldByHold,
  insertFxHoldKeys,
  readIntoPlan,
  sourceChoiceHoldKeys,
} from "./core/control/readback";
import type { MergedRead, ParamSource, ReadbackResult } from "./core/control/readback";
import type { PendingWrites } from "./core/control/settle";
import { parseUrxf, paramSourceOf, UrxfError } from "./core/control/urxf";
import {
  compareCounts,
  compareNames,
  comparePlan,
  diffNames,
  diffPlan,
  dryRun,
  formatCompareReport,
  formatWriteReport,
  FOLLOW_USB_ADDR,
  LCD_SELECTION_ADDR,
  reachedAndFailed,
  rateAction,
  readClockState,
  readSdRecorderHold,
  type SdRecorderHold,
  readFollowUsb,
  readLcdSelection,
  readTrackCount,
  confirmedAddrs,
  newConfirmLedger,
  sendConverging,
  sendNames,
  sendPresetsAndReconverge,
  setFollowUsb,
  writeLcdSelection,
  type CommandDiff,
  type ConvergeResult,
  type SendOutcome,
} from "./core/control/client";
import { askRateChoice } from "./ui/rate-choice";
import {
  cmdAddr,
  collisionOwners,
  cueAddrOf,
  cueAddrs,
  cueAt,
  cueNotifyAddrs,
  planToCommands,
  planToNameWrites,
  unnamedNodes,
  unverifiedGuessAt,
  unverifiedGuessNote,
} from "./core/control/translate";
import { decodeLcdSelection, encodeStereoSide, LcdEchoes } from "./core/control/lcd-selection";
import {
  decodeSendsTarget,
  encodeSendsTarget,
  readSendsTarget,
  SENDS_TARGET_ADDR,
  writeSendsTarget,
  type SendsTarget,
} from "./core/control/sends-target";
import { CueEchoes, CueSet, readCues, writeCueFlag } from "./core/control/cue";
import {
  dawIntegrationNotifyAddrs,
  decodeIntegrationSend,
  encodeIntegrationSend,
  INTEGRATION_FIELDS,
  integrationFieldOf,
  integrationGlassValues,
  integrationSlotAt,
  mergeIntegrationRead,
  readDawIntegration,
  withIntegrationNotify,
  writeIntegrationSend,
  type DawIntegration,
  type IntegrationField,
} from "./core/control/daw-integration";
import { boundByRead, confirmedAdoptions } from "./app/adopt-writes";
import { unauthoredWriteNodes } from "./app/unauthored-writes";
import {
  markAuthored,
  markParamSource as markSource,
  noteSeededDefaults,
  type SeededDefault,
} from "./app/param-source";
import type { SharedOwners, WriteScope } from "./core/control/translate";
import { LiveSync } from "./core/control/live";
import { DeviceFollow } from "./core/control/follow";
import { version } from "../package.json";
import { LinkLedgerTracker } from "./core/control/link-stats";
import type { LinkSessionEnd } from "./core/control/link-stats";
import { LinkStatsView } from "./ui/link-stats";
import { firmwareMismatch, SUPPORTED_SYSTEM_FIRMWARE } from "./core/control/firmware";
import { cancelledBeforeWriting, formatSelfTestReport, runSelfTest, summarizeVerdicts } from "./core/control/selftest";
import { runPrepareModified } from "./core/control/prepare";
import { DeviceSetupPanel } from "./ui/device-setup";
import {
  diffDeviceSetup,
  readDeviceSetup,
  sendDeviceSetup,
  UDK_SLOTS,
  withSetupWrites,
} from "./core/control/device-setup";
import {
  isSetupGlassPath,
  setupGlassEdit,
  setupGlassPaths,
  setupGlassValues,
  withSetupEdit,
} from "./core/sim-map-setup";
import { SCENE_CURSOR_PATH, SCENE_TAB_PATH, sceneGlassValues, sceneTab, type SceneTab } from "./core/sim-map-scene";
import type { DeviceSetup, SetupWrite } from "./core/control/device-setup";

// Clear persisted UI state (theme / model / meter points / consent gate / recent
// files / inspector sections / user preferences) when the browser dev app is opened with ?reset (or
// #reset) — done synchronously here, before anything below reads localStorage, and
// the flag is stripped so a later manual reload doesn't clear again. The desktop
// app uses the --reset-storage launch flag instead, whose answer arrives async.
resetStorageFromUrl();

// The desktop flag's query is started here, ahead of every reader below, and boot()
// awaits it. A stored value that makes the synchronous init below throw then still gets
// its clear and its reload: the query is already in flight, and its answer runs the clear
// whether or not the rest of this module finished.
const RESET_DONE_KEY = "urx-reset-done";
const resetGate = resetStorageIfRequested();

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const picker = $<HTMLSelectElement>("model-picker");
const ratePicker = $<HTMLSelectElement>("rate-picker");
const followUsbBadge = $<HTMLButtonElement>("follow-usb");
const graphHost = $<HTMLElement>("graph-host");
const inspectorHost = $<HTMLElement>("inspector");
const consoleHost = $<HTMLElement>("console-host");
const statusbar = $<HTMLElement>("statusbar");
const statusbarSaid = $<HTMLElement>("statusbar-said");

// The device's SETUP > Follow USB state, as far as this session has seen it. Null
// until a read of the unit succeeds, and back to null when the link it was read over
// drops (a live session's, or a fetch's part-way through) or the plan switches to
// another model; a read that fails leaves it as it was. Unknown is never drawn as
// "off", since "off" is the state in which the rate picker is trusted to stick.
//
// Session-scoped on purpose: unlike the model and the rate, this is not a choice the
// operator makes in the app and carries between sessions. It is the device's own
// clock policy, so a remembered value would be a claim about hardware that may not
// even be attached.
let followUsbState: boolean | null = null;

// Paint the badge from the state above. Separate from the setter so the language
// switch can re-label it without pretending to change the state (applyStaticI18n).
//
// Three states, not two. Unknown is drawn as its own thing (dashed, aria-pressed
// "mixed") rather than hidden: the badge exists to warn that the rate picker will
// not stick, and hiding it until a device action meant the warning only ever
// arrived after the operator had already committed to one. It must still never be
// drawn as "off", which is the state in which the picker IS trusted.
function renderFollowUsbBadge(): void {
  const m = t().toolbar;
  const state = followUsbState;
  followUsbBadge.dataset.state = state === null ? "unknown" : state ? "on" : "off";
  followUsbBadge.setAttribute("aria-pressed", state === null ? "mixed" : String(state));
  followUsbBadge.textContent = m.followUsb;
  followUsbBadge.title = state === null ? m.followUsbUnknownHint : state ? m.followUsbOnHint : m.followUsbOffHint;
}

function setFollowUsbBadge(state: boolean | null): void {
  followUsbState = state;
  renderFollowUsbBadge();
  // The rate picker opens during Live sync only while Follow USB is off.
  syncDeviceActionUi();
  simView?.unitStateChanged();
}

/** Whether a rate chosen in the app goes to the unit while Live sync is up: the session's device
 *  scope carries the rate (Scene only leaves it out), and Follow USB is off, so the unit keeps a
 *  rate written to it rather than taking the computer's back. */
function liveRateOpen(): boolean {
  return liveSessionUp && getSettings().deviceScope === "all" && followUsbState === false;
}

/** What decides whether a rate chosen in the app reaches the unit, as the toolbar's rate and the
 *  LCD's Sampling Frequency both read it (`rateHoldReason`). */
function rateHoldState(): Pick<SimLockState, "linked" | "live" | "rateOpen" | "rateScope"> {
  return {
    linked: deviceLinkHolder !== null,
    live: liveSessionUp,
    rateOpen: liveRateOpen(),
    rateScope: liveSessionUp && getSettings().deviceScope !== "all",
  };
}

/** The reasons the rate is held for, in the language on screen. */
function rateHoldReasons(): Pick<SimLockReasons, "rateLinked" | "rateScope" | "rateFollowUsb"> {
  return {
    rateLinked: t().sim.lockRateLinked,
    rateScope: t().sim.lockRateScope,
    rateFollowUsb: t().sim.lockRateFollowUsb,
  };
}

// Theme mode (ThemeMode, ui/prefs.ts): auto follows the OS color scheme. A
// fresh install defaults to auto; an explicit light/dark choice (including ones
// saved before auto existed) is honored.
// E2E pins an empty starting board (just the fixed wires) via this flag so
// routing/hide assertions are not perturbed by the factory-seed sends; the seed
// data itself is verified by initial-state.test.ts.
const seedEmpty = seedEmptyRequested();
// Shelved (hidden) nodes are persisted globally per model so the canvas/console
// layout survives an app restart, independent of any saved plan file. The id set
// is model-specific, so store a per-model map under one key. Best-effort: storage
// may be unavailable, in which case hidden state simply does not carry across.
// Fresh plans (startup / new / model switch) restore the last hidden layout for
// that model; a loaded file overrides it via loadPlan (which then re-records it).
const newPlan = (id: ModelId): Plan => {
  const p = seedEmpty ? emptyPlan(id) : defaultPlan(id);
  p.hidden = loadHidden(id);
  return p;
};

let modelId: ModelId = detectModel();
let plan: Plan = newPlanAtLastRate(modelId);
ensureFixedConnections(getModel(modelId), plan);
let dirty = false;
// Which plan keys an edit funnel wrote while a device read was in flight. Every read
// goes through readIntoPlan with it, and markChanged is the one site that reports: a
// funnel that authored a key and then put it back where the read found it is otherwise
// indistinguishable from an untouched key, and the read would write the device's
// mid-gesture sample over it. Armed only while a read is open, so an edit outside one
// costs a null check.
const planWrites = new PlanWriteWitness(() => plan);
let selection: Selection = null;
let recent: RecentEntry[] = loadRecent();
let themeMode: ThemeMode = detectThemeMode();
let theme: ThemeName = resolveTheme(themeMode);
document.documentElement.dataset.theme = theme;

let labelSource: LabelSource = detectLabelSource();

for (const id of MODEL_IDS) {
  const opt = document.createElement("option");
  opt.value = id;
  opt.textContent = id;
  picker.append(opt);
}
picker.value = modelId;

for (const rate of SAMPLE_RATES) {
  const opt = document.createElement("option");
  opt.value = String(rate);
  opt.textContent = formatRate(rate);
  ratePicker.append(opt);
}
ratePicker.value = String(plan.sampleRate);

// Live sync: mirror each edit to the connected device. The model
// and plan are read through getters because loadPlan reassigns `plan`. A write
// failure stops sync and drops the connection (deactivateLive). Declared before the
// graph because the graph's onChange callback schedules a live flush. Null in the
// demo build (DEMO folds the ternary to null), so the control layer tree-shakes
// out exactly as the other device features do.

// Tracks whether a live session's resources (held connection, follow, live UI)
// are up — independent of the LiveSync.active flag, which a flush clears the
// instant it errors. deactivateLive guards on this so an error-path teardown
// (where active is already false) still drops the connection and resets the UI.
let liveSessionUp = false;
/** FX channels whose EFFECT TYPE the Inspector set since the last write of it, with the type: the
 *  plan holds that type's factory values for the keys it uses, which the park in front of the
 *  type write must leave standing. */
const fxReseeds = new Map<string, number>();
// Generation of the connection the live session holds open, captured at connect.
// deactivateLive disconnects with exactly this epoch, so a teardown that lands
// late (the disconnect is fire-and-forget) cannot close a newer connection a
// later device action opened in the meantime — the Rust side no-ops the mismatch.
let liveEpoch = 0;
// Holds the running self-test's controller (experimental); null when idle. Module
// scope so applyStaticI18n keeps the button's "Cancel" label across a language
// switch mid-run, instead of reverting it to "Self-test" while a run is in flight.
let selfTestAbort: AbortController | null = null;
// Same for the fetch / write device actions: each holds its in-flight controller
// (a long read/write of the whole device can stall when the link drops), so a
// second menu click cancels and applyStaticI18n keeps the "Cancel" label.
let fetchAbort: AbortController | null = null;
let writeAbort: AbortController | null = null;
// The experimental read-only compare (device vs plan): same cancel-on-second-click
// pattern as fetch, so its serial diff sweep can be stopped when the link stalls.
let compareAbort: AbortController | null = null;
// Whether the --experimental gate resolved on (set in the !DEMO block below); the
// Preferences device-scope note names the diagnostics' coverage only when the
// diagnostics themselves are reachable.
let experimentalOn = false;
// A remembered SIM view still waiting for the experimental gate; any view chosen before
// the gate resolves takes its place.
let simViewPending = false;
// The SIM view, built once the experimental gate resolves on (an experimental launch only).
// Every hook below asks through `?.`, since a funnel runs without one.
let simView: SimView | null = null;
// Name the collapse the way the canvas names a node, and count the rest. Several
// surfaces print this (the live status line, the write confirm, the "no changes"
// status), so the label/count rule lives here once. The first group's first
// dropped owner is the one named — the complete list goes to the console warning
// the collapse already logs.
function sharedSettingText(owners: SharedOwners[]): string {
  const first = owners[0];
  const more = owners.reduce((n, o) => n + o.dropped.length, 0) - 1;
  return t().status.sharedSetting(graph.labelOf(first.dropped[0]), graph.labelOf(first.kept ?? ""), more);
}
/** The confirm's line about what the write changes beyond what the operator authored, or "".
 *
 *  Every strip by name, not the first few and a count: the list is bounded by the model, and a
 *  name is the only part of this the operator can act on — a count tells them something is
 *  wrong somewhere and leaves them the whole board to look through. */
function unauthoredNoteFor(changing: ReadonlySet<number>, scope: WriteScope, renaming?: ReadonlySet<string>): string {
  const nodes = unauthoredWriteNodes(getModel(modelId), plan, scope, changing, renaming);
  if (!nodes.length) return "";
  return t().confirm.unauthoredWrite(nodes.map((id) => graph.labelOf(id)).join(", "));
}
// The link ledger — what a session asks of the Device Center broker, and what the
// broker fails to answer (core/control/link-stats.ts says why these values and not
// latency). COLLECTED AND LOGGED in every desktop build, deliberately: the symptom it
// exists for turns up after the app is gone, and a record that only exists when the
// operator happened to launch with a flag records nothing. Only the on-screen readout
// is gated behind --experimental.
const linkLedger = DEMO ? null : new LinkLedgerTracker(version);
let linkStatsView: LinkStatsView | null = null;

// A log line that could not be written is not a device failure and does not stop a
// session — but it is not swallowed either: it says so once per session (the tracker's
// own latch) and every time in the console (see architecture.md "Aborting on failure").
function warnLinkLog(message: string): void {
  console.warn("link ledger:", message);
  setStatus(t().status.linkLogFailed(errorText(message)));
}

/** Start the ledger for a session. Called on the connect rather than once the session
 *  counts as up, so the elapsed time and the Rust-side command counts — which start at
 *  the connect — measure the same window. The tracker owns the interval and the opening
 *  line; this is only the two things the app knows, the device label and where a
 *  failure gets reported. */
function beginLinkLedger(device: string): void {
  if (!linkLedger) return;
  linkLedger.begin(device, warnLinkLog);
  linkStatsView?.setSession(true);
}

/** Close the ledger, then the connection, then hand the link holder back — in that
 *  order. A reading taken after the disconnect reports a session that did nothing and the
 *  counters go to zero with the link, and the holder is what keeps another action from
 *  connecting over a read this is still waiting out. THE one release: every exit from a
 *  live session goes through here, so the ordering is a property of the code rather than
 *  a rule each exit re-implements.
 *
 *  Chained rather than awaited so callers stay synchronous where they were: the
 *  disconnect was already fire-and-forget, and its epoch guard is what makes a late one
 *  safe. */
function releaseLive(epoch: number, reason: LinkSessionEnd): Promise<void> {
  linkStatsView?.setSession(false);
  paramWatch?.stop(reason);
  const ledger = linkLedger?.active ? linkLedger.end(reason) : Promise.resolve();
  // A follow read already in flight goes on reading over this connection: a session that
  // merely ends lets its read finish rather than abandoning it (abandonFollowWork says
  // why), and the link it reads over is this session's. Disconnecting under it answers its
  // next round trip "not-connected", which stops it exactly where abandoning it would have
  // — the document left holding a mix of device and plan values with nothing marking which
  // is which — and reports nothing, since the session is already down and stopLiveOnError
  // returns on that. No caller can add to the set while it is waited on: the two that can
  // have reads in flight stop the follow layer first, and the start's own cancels are all
  // in front of it beginning. allSettled rather than all: a ledger that rejects still has
  // to release the link.
  const reads = Promise.all([...followReads].map((r) => r.done));
  // …and so does the session's own flush: end() stops it at its next round trip, and the
  // command it has on the wire then is answered over this connection rather than over one
  // another action installs after the disconnect.
  const flush = live?.idle() ?? Promise.resolve();
  // …and the LCD selection write the session has on the wire, for the same reason.
  const lcd = lcdInFlight ?? Promise.resolve();
  // …and the [CUE] writes it has queued, with the [Sends] destination's queued behind them.
  const cue = cueWrites;
  return (
    Promise.allSettled([ledger, reads, flush, lcd, cue])
      .then(() => vdDisconnect(epoch))
      // The holder goes back HERE and not in deactivateLive, because it is what stops
      // another action from connecting: an install replaces the worker, and the read still
      // running above has no epoch of its own — its next round trip would go to whatever is
      // installed by then, and its epilogue would re-base and flush through the session
      // that replaced it. Ahead of the catch rather than behind it, so a disconnect that
      // failed cannot leave the app locked out of its own device actions: behind one that
      // absorbs the rejection, a `finally` and a `then` do the same thing and neither says
      // which was meant. The Preferences device scope, held while the session held the link,
      // is drawn open again with it.
      .finally(() => {
        releaseDeviceLink("live");
        prefs.refresh();
      })
      // …and a disconnect that could not be sent leaves the app unable to say whether the
      // unit is still attached to a worker, which is a dialog rather than a status line.
      // Not on a session ending in ERROR: stopLiveOnError has already raised one naming
      // the same drop, and the disconnect failing is how a dropped link answers.
      .catch((err) => {
        if (reason !== "error") showError(t().status.liveError(errorText(err)));
      })
  );
}

const live = DEMO
  ? null
  : new LiveSync({
      getModel: () => getModel(modelId),
      getPlan: () => plan,
      onError: (message, guess) =>
        stopLiveOnError(errorText(message) + (guess ? ` — ${t().status.unverifiedGuess(guess)}` : "")),
      // The adoption's own count rides on this line rather than writing one of its own: the
      // converge that produces it runs INSIDE the flush, so a line written there is overwritten
      // by this one a moment later — the status bar is last-writer-wins, and a converge always
      // implies something was sent, so this always follows.
      onSent: (n) => {
        const taken = liveAdopted;
        liveAdopted = 0;
        const notes = flushReadNotes;
        flushReadNotes = [];
        setStatus([...notes, t().status.liveSynced(n) + takeBoundedNote(taken)].join(" — "));
      },
      onConfirmed: (confirmed, sent) => {
        liveAdopted += adoptConfirmedWrites(confirmed, sent);
      },
      onCollapsed: (owners) => setStatus(sharedSettingText(owners)),
      // One bidirectional scope for the session: the same filter shapes the
      // snapshot, the flush, and the follow notify registration.
      getScope: () => getSettings().deviceScope,
      // A "refetch" sideEffect went out (the EQ 1-knob): the device has just recomputed
      // values the plan only mirrors, so read the owner node back rather than pushing.
      // Scoped rather than the full reflect `reconcileNodes` takes: the nodes are known and
      // the change is node-local (the recomputed bands), and live.ts has already re-based
      // its own snapshot — a full reflect would re-translate the whole plan a second time
      // and rebuild the console on every flush of a 1-knob drag. The abort rule is the
      // shared one: a read that cannot complete leaves the plan claiming values the device
      // does not hold.
      //
      // `pending` is what the flush that triggered this refetch just put on the unit.
      // The read waits those writes out and answers their addresses from what the UNIT
      // announced (see readback / settle), and it does both INSIDE this merged read, so
      // an edit made while it waits is covered by the same clone and witness that cover
      // one made while it reads. Without it the read wrote the value the click had just
      // replaced back into the plan, this hook absorbed it into the undo baseline,
      // live.ts's re-base recorded it as device truth, and the unit's own notify for our
      // write then read as a device-side change. That also widens the window undo is
      // refused in, since the wait is inside followReads membership like the read it
      // belongs to. Left that way: the clone and the witness are open for the whole of
      // it, so committing an entry there would freeze this read's own writes into it —
      // and the refusal is a deferral, bounded by the settle's own window.
      //
      // `edits` is the watch the flush opened where it took the values it sent: an edit made
      // while those writes were on the wire is carried by none of them, and the read skips it
      // as it skips one made while it runs.
      watchEdits: () => planWrites.watch(),
      refetchNodes: async (nodeIds, pending, edits) => {
        // Named for the hook, not for a member of it: the EQ 1-knob was the only
        // sideEffect: "refetch" param when this was written, and SSMCS morphing now takes
        // the same path. A diagnostic that names one of them puts the wrong cause in the
        // log for the other.
        const merged = await followRead(
          "side-effect refetch",
          (into, signal) =>
            // The one caller that skips the names, and it says so itself — the reconciles
            // below carry pending writes too, and reading names is what makes a rename
            // made on the unit arrive (readback.ts's name section).
            applyNodeStateScoped(into, nodeIds, signal, pending, true),
          edits,
        );
        // The plan this read was issued for is gone (a file flow replaced it): its
        // values belong to a document nothing shows, and no snapshot can describe it.
        if (!merged) return null;
        // This read pulls each named node's whole body, and a pair primary's body carries
        // the Signal Type — so a link can arrive here as well as at the two reconciles. This
        // is the one seat whose reflect is the fine-grained branch, which repaints the nodes
        // this read named and draws no tie, so the full one is asked for on the LINK moving
        // rather than on the snap having moved a node: an already adjacent pair has nothing
        // to snap, and its tie would then wait for whatever re-rendered next.
        const snapped = snapLinkedPairs();
        if (snapped || changesLinkState(merged.devicePatch)) followFull = true;
        traceProbe?.sample("refetch");
        noteMergeConflicts(merged);
        flushReadNotes.push(...refusalNotes(merged));
        for (const id of nodeIds) followDirtyNodes.add(id);
        // The device recomputed values the plan only mirrors (the 1-knob bands), so they
        // are its authorship, not an edit: take exactly those keys into the history
        // baseline. A whole-plan re-base takes the drag's own moves with them and drops
        // the entry it has open, which is what made one 1-knob level drag undo as 0
        // entries or as 1 describing only its tail.
        planHistory?.absorb(merged.devicePatch);
        requestReflect();
        assertReadComplete(merged, "side-effect refetch issues:");
        return merged.deviceView;
      },
      // A write is about to push the plan over addresses the unit announces nothing for, so
      // they are read first — see live.ts's `parkSilent`, which calls this at either of its
      // two boundaries, each under its own condition. The epilogue is one for all of them:
      // the guard keeps the merge off an edit this flush has not sent, `absorb` takes what
      // it authored into the baseline without spending the operator's open gesture, and a
      // read that FAILS ends the session rather than letting the write behind it go out over
      // values it could not confirm.
      parkSilent: async (scope) => {
        // The keys an Inspector EFFECT TYPE selection put at the incoming type's defaults, for
        // the park in front of that type's write (app/fx-reseed.ts).
        const keepFx = takeFxReseeds(fxReseeds, plan, scope, (id, type) =>
          holdsSent(FX_EFFECT_TYPE_PARAM[FX_CHANNEL_NODE_INDEX[id]], 0, 0, type),
        );
        const merged = await followRead("silent-address park", (into, signal) =>
          applySilentState(getModel(modelId), into, signal, live?.recentPending(), holdsSent, { ...scope, keepFx }),
        ).catch((err: unknown) => {
          stopLiveOnError(errorText(err));
          return null;
        });
        if (!merged) return;
        // Ahead of the empty-patch exit, because a read that could not reach some of these
        // nodes produces no patch for them: taken second, an incomplete read would look
        // like a read that found nothing to change and the converge would go out on it.
        // Ending the session here is what stops it — the flush's own generation check.
        try {
          assertReadComplete(merged, "silent-address park issues:");
        } catch (err) {
          stopLiveOnError(errorText(err));
          return;
        }
        if (!merged.devicePatch.length) return;
        traceProbe?.sample("follow-scoped");
        noteMergeConflicts(merged);
        planHistory?.absorb(merged.devicePatch);
        for (const e of merged.devicePatch) if (e.field === "nodeParams") followDirtyNodes.add(e.key);
        // Drained here rather than left on `requestReflect`'s timer. That timer is for
        // coalescing a 20 Hz stream and this is one read — and its arrival AFTER the rebuild
        // below is what makes the difference: a reflect landing then asks the panel to
        // rebuild, the gate holds that for the select the operator chose in, and their next
        // press anywhere is spent releasing the hold.
        reflectFollow();
        // Said on the same line a reconcile says it on, and by the park that runs inside the
        // operator's OWN gesture: they moved a head, and what this found is the unit's answer
        // to it — a reverb tuned on the panel appearing in the app. The park the converge
        // takes is not that gesture, and the flush reports what it sends on the same line, so
        // saying it there would announce one repair twice.
        if (scope.only) setStatus(withSwitchNotes(t().status.liveFollowed(merged.applied) + takeBoundedNote(), merged));
        // Past the gate, because this arrives inside the operator's OWN gesture: a park runs
        // in front of a head write, and the head is a selector they have just chosen in — so
        // the control the gate would hold the rebuild for is the one showing the stale value,
        // and their next press elsewhere is spent releasing the hold rather than doing what
        // they pressed for.
        rebuildInspectorNow();
      },
      // The flush's capture rebuilt the follow address set — re-register against it. Only a
      // STRUCTURAL edit moves that set (a mode change, a wire), so this is a no-op on the
      // ordinary flush and DeviceFollow.refresh is what decides. Fire-and-forget on purpose:
      // the flush has returned, and refresh routes its own failures to onError rather than
      // leaving a floating rejection.
      reregister: () => void follow?.refresh(),
    });

// The unit's [CUE] flags (core/control/cue.ts): state of the live session, outside the plan, so
// nothing here is saved, dirtied or undoable. While a session is up a change made in the app is
// written to the unit and a change on the unit arrives as a notify; a session's start takes what
// the unit holds and its end forgets every cue, so with no session nothing is cued and nothing
// switches one.
let cues = new CueSet(getModel(modelId));
const cueEchoes = new CueEchoes();
// One flag write at a time, in the order the changes were made, with the reads and writes of
// HOME's [Sends] destination in the same line; a session's release waits for the tail before it
// disconnects.
let cueWrites: Promise<void> = Promise.resolve();
let cueWritesPending = 0;

/** The other member of the STEREO-linked mono pair `node` belongs to, which shares its flag. */
function cuePartner(node: string): string | undefined {
  const model = getModel(modelId);
  return isStereoLinkedPair(model, plan, node) ? partnerChannel(model, node) : undefined;
}

/** Turn one strip's [CUE] on or off: the one funnel the CONSOLE chip, the status bar's clear,
 *  a MIDI control and a notify from the unit all reach. A change made in the app is refused with
 *  no session up, and written to the unit with one. */
function setCue(node: string, on: boolean, origin: "app" | "device"): void {
  // A notify can arrive while a starting session registers, before it counts as up.
  if (origin === "app" && !liveSessionUp) return;
  const changed = cues.apply(node, on, cuePartner(node));
  // A press on the unit is read back once the presses settle: it can put back output cues the
  // unit was holding from before this session, which the set has no record of, and the unit
  // announces none of them.
  if (origin === "device") scheduleCueRead();
  if (!changed) return;
  if (origin === "app" && liveSessionUp) {
    writeCue(node, on);
    // Read back behind the write: turning the last input cue off puts back output cues the unit
    // was holding from before this session, which the set has no record of, and the unit
    // announces none of them.
    rereadCues();
  }
  renderCues();
}

// The settle a read of the flags waits for after a press on the unit, as device follow's.
const CUE_READ_SETTLE_MS = 300;
let cueReadTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleCueRead(): void {
  if (cueReadTimer !== null) clearTimeout(cueReadTimer);
  cueReadTimer = setTimeout(() => {
    cueReadTimer = null;
    rereadCues();
  }, CUE_READ_SETTLE_MS);
}

/** Read the flags back behind every write queued so far, and take what the unit holds unless
 *  the flags changed while the read ran. */
function rereadCues(): void {
  if (!liveSessionUp) return;
  const model = getModel(modelId);
  cueWrites = cueWrites
    .then(async () => {
      // Behind the plan flush too: a write there can move a flag (a link copies one) that a read
      // taken ahead of it answers with the value it replaces.
      await live?.idle();
      if (!liveSessionUp) return;
      const set = cues;
      const since = set.version;
      const got = await readCues(model);
      // The set a session's end or a model change replaced starts again at version 0, so the
      // set itself is compared as well as its version. A read taken between two writes answers
      // ahead of the later one, so it is taken only once no write is waiting; the read behind
      // the last one takes the unit's answer.
      if (liveSessionUp && cues === set && set.version === since && cueWritesPending === 0 && set.replace(got))
        renderCues();
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

function writeCue(node: string, on: boolean): void {
  const model = getModel(modelId);
  const addr = cueAddrOf(model, node);
  if (!addr) return;
  const value = on ? 1 : 0;
  cueWritesPending++;
  cueWrites = cueWrites
    .then(async () => {
      // A session that ended while this waited has nothing to write to.
      if (!liveSessionUp) return;
      cueEchoes.note(addr.param, addr.y, value);
      await writeCueFlag(addr, on);
    })
    .catch((err: unknown) => {
      const guess = unverifiedGuessAt(model, addr.param, addr.y);
      stopLiveOnError(errorText(err) + (guess ? ` — ${t().status.unverifiedGuess(guess)}` : ""));
    })
    .finally(() => {
      cueWritesPending--;
    });
}

/** Turn every cue off: the input cues first, which puts back any output cue the unit was
 *  holding — including ones held from before this session, which the set has no record of — and,
 *  once the flags have been read back behind those writes, every cue that read found. */
function clearCues(): void {
  if (!liveSessionUp) return;
  const set = cues;
  if (set.group() !== "input") {
    for (const n of set.nodes()) setCue(n, false, "app");
    return;
  }
  for (const n of set.nodes()) setCue(n, false, "app");
  // Queued behind the writes and the read each of them queued, so it sees what the unit put back.
  cueWrites = cueWrites.then(() => {
    if (!liveSessionUp || cues !== set) return;
    for (const n of set.nodes()) setCue(n, false, "app");
  });
}

/** Take a read of the unit's flags into the set it was started for, unless that set was
 *  replaced, its flags changed since the read started, or a write the read may have answered
 *  ahead of is still waiting. */
function adoptCueRead(nodes: readonly string[], set: CueSet, since: number): void {
  if (cues !== set || set.version !== since || cueWritesPending > 0) return;
  if (set.replace(nodes)) renderCues();
}

/** Forget every cue: the session that knew them ended, or the model changed. */
function resetCues(): void {
  cueEchoes.clear();
  if (cueReadTimer !== null) clearTimeout(cueReadTimer);
  cueReadTimer = null;
  const model = getModel(modelId);
  const stale = cues.size > 0;
  cues = new CueSet(model);
  if (stale) renderCues();
  else renderCueBar();
}

// HOME's [Sends] destination (core/control/sends-target.ts): UI state of the unit outside the
// plan, shown on the SIM view's glass, so an experimental launch's alone. There a session's start
// reads it, a change on the unit arrives as a notify, and a destination chosen on the glass is
// written to the unit. With no session the glass keeps a destination of its own, which reaches
// nothing.
let sendsTarget: SendsTarget | null = null;
const sendsEchoes = new LcdEchoes();
// How long after an announced rate above 96 kHz 763 is read again. The unit moves FX 2 to FX 1
// at that rate and announces nothing, and has moved it by this long after the rate write.
const SENDS_REREAD_AFTER_RATE_MS = 1000;
let sendsRereadTimer: ReturnType<typeof setTimeout> | null = null;

/** Take a 763 value the unit holds. */
function takeSendsTarget(raw: number): void {
  const next = decodeSendsTarget(getModel(modelId), raw);
  if (next === sendsTarget) return;
  sendsTarget = next;
  simView?.unitStateChanged();
}

/** A destination chosen on the glass: written to the unit while a session is up. False when it
 *  is refused — with no session, for a destination the model has no bus for, and for FX 2 at a
 *  rate that takes FX 2 out. */
function chooseSendsTarget(target: string): boolean {
  if (!liveSessionUp) return false;
  const raw = encodeSendsTarget(getModel(modelId), target, plan.sampleRate);
  if (raw === null) return false;
  sendsTarget = decodeSendsTarget(getModel(modelId), raw);
  cueWrites = cueWrites
    .then(async () => {
      if (!liveSessionUp) return;
      sendsEchoes.note(raw);
      await writeSendsTarget(raw);
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
  return true;
}

/** Read 763 behind every write queued so far, the plan flush and the follow reads running, and
 *  take what the unit holds. An experimental launch only, where the SIM view shows it. */
function rereadSendsTarget(): void {
  if (!liveSessionUp || !experimentalOn) return;
  cueWrites = cueWrites
    .then(async () => {
      await live?.idle();
      await Promise.all([...followReads].map((r) => r.done));
      if (!liveSessionUp) return;
      const raw = await readSendsTarget();
      if (liveSessionUp) takeSendsTarget(raw);
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

/** A rate change the unit announced: above 96 kHz the unit may have moved the destination. */
function onAnnouncedRate(hz: number): void {
  if (!experimentalOn || !(hz > 96000)) return;
  if (sendsRereadTimer !== null) clearTimeout(sendsRereadTimer);
  sendsRereadTimer = setTimeout(() => {
    sendsRereadTimer = null;
    rereadSendsTarget();
  }, SENDS_REREAD_AFTER_RATE_MS);
}

/** Forget the unit's destination: the session that knew it ended, or the model changed. */
function resetSendsTarget(): void {
  sendsEchoes.clear();
  if (sendsRereadTimer !== null) clearTimeout(sendsRereadTimer);
  sendsRereadTimer = null;
  if (sendsTarget === null) return;
  sendsTarget = null;
  simView?.unitStateChanged();
}

// The unit's own settings and states the SIM view shows beside the plan: SETUP > GENERAL
// (core/control/device-setup.ts), the operation mode, the microSD card and each MIC/LINE jack's
// engaged Clip Safe.
// SETUP is known from the last reading of it — a session's start, the Device setup screen, a SETUP
// edit on the glass — and kept until the model changes, as Follow USB is. The operation mode, the
// scene pointers (the current scene, SCENE LIST's cursor) and the microSD card are a session's
// reading, and the engaged Clip Safe what the unit announced during one.
let unitSetup: DeviceSetup | null = null;
let unitOperationMode: number | null = null;
// Whether a card is in the unit's microSD slot, and a count of the unit's announcements of it,
// which a read taken while one lands does not override.
let unitCardIn: boolean | null = null;
let cardVersion = 0;
// The scene pointers keep the tab SCENE LIST's cursor last needed, which a move onto a user scene
// (on both tabs) leaves as it is, and a count of the unit's announcements of each, which a read taken
// while one lands does not override.
let unitScene: { current: number | null; cursor: number | null; tab: SceneTab | null } = {
  current: null,
  cursor: null,
  tab: null,
};
const sceneVersions = { current: 0, cursor: 0 };
// SETUP > Software Integration, as a session read it and the unit announced it since, and a count
// of the changes made to each of its values, which a read taken while one lands does not override.
let unitIntegration: DawIntegration | null = null;
const integrationVersions: Record<IntegrationField, number> = { active: 0, fx1Send: 0, fx2Send: 0 };
const clipSafeEngaged = new Set<number>();
// A Follow USB press on the glass, handled as a press of the toolbar's FOLLOW USB; set where that
// button is wired.
let pressFollowUsb: (() => void) | null = null;

const OPERATION_MODES = ["Standard", "Simple"];

/** The unit's values the glass shows in place of its own, by the glass's path. */
function unitGlassValues(): Map<string, SimValue> {
  const model = getModel(modelId);
  const out = unitSetup ? setupGlassValues(model, unitSetup) : new Map<string, SimValue>();
  // Off until the unit's is read, so the glass takes the plan's rate.
  out.set("setup.followUsb", followUsbState ?? false);
  const mode = unitOperationMode === null ? undefined : OPERATION_MODES[unitOperationMode];
  if (mode !== undefined) out.set("setup.operationMode", mode);
  for (const [path, value] of sceneGlassValues(unitScene.current, unitScene.cursor, unitScene.tab))
    out.set(path, value);
  if (unitIntegration) for (const [path, send] of integrationGlassValues(unitIntegration)) out.set(path, send);
  if (unitCardIn !== null) out.set("sd.mounted", unitCardIn);
  return out;
}

/** An edit of one of the unit's values on the glass. Follow USB is the toolbar's press. A SETUP
 *  setting is written while a session is up, behind every unit write queued so far, and refused
 *  without one. */
function writeUnitGlassValue(path: string, value: SimValue): Promise<SimValue> {
  const shown = (): SimValue => unitGlassValues().get(path) ?? value;
  if (path === "setup.followUsb") {
    if (value !== followUsbState) pressFollowUsb?.();
    return Promise.resolve(followUsbState ?? !value);
  }
  // A Software Integration setting is the session's, and the unit takes one only while a
  // compatible DAW holds it.
  const slot = integrationSlotAt(path);
  if (slot) {
    const raw = encodeIntegrationSend(String(value));
    if (!liveSessionUp || !unitIntegration?.active || raw === null) return Promise.reject(new Error("refused"));
    unitIntegration = { ...unitIntegration, [slot]: decodeIntegrationSend(raw) };
    integrationVersions[slot]++;
    cueWrites = cueWrites
      .then(async () => {
        if (!liveSessionUp) return;
        await live?.idle();
        if (!liveSessionUp) return;
        // A DAW that let go of the unit while this waited takes it with it: the send is not
        // written, and the glass is shown what the unit holds.
        if (unitIntegration?.active) await writeIntegrationSend(slot, raw);
        else rereadDawIntegration();
      })
      .catch((err: unknown) => stopLiveOnError(errorText(err)));
    return Promise.resolve(shown());
  }
  const model = getModel(modelId);
  const edit = setupGlassEdit(model, path, value);
  if (edit === null || edit === "refused") return Promise.reject(new Error("refused"));
  if (liveSessionUp && unitSetup) {
    const writes = diffDeviceSetup(model, unitSetup, withSetupEdit(unitSetup, edit));
    unitSetup = withSetupWrites(unitSetup, writes);
    cueWrites = cueWrites
      .then(async () => {
        if (!liveSessionUp) return;
        await live?.idle();
        if (liveSessionUp) await sendDeviceSetup(writes);
      })
      .catch((err: unknown) => stopLiveOnError(errorText(err)));
    return Promise.resolve(shown());
  }
  return Promise.reject(new Error("refused"));
}

// The settle a re-read of the unit's settings waits for after the unit announces one, as a
// [CUE] re-read's.
let unitSetupReadTimer: ReturnType<typeof setTimeout> | null = null;

/** Read the unit's settings again behind every unit write queued so far, once announcements
 *  settle. */
function scheduleUnitSetupRead(): void {
  if (unitSetupReadTimer !== null) clearTimeout(unitSetupReadTimer);
  unitSetupReadTimer = setTimeout(() => {
    unitSetupReadTimer = null;
    if (!liveSessionUp || !experimentalOn) return;
    const model = getModel(modelId);
    cueWrites = cueWrites
      .then(async () => {
        await live?.idle();
        if (!liveSessionUp) return;
        const read = await readDeviceSetup(model);
        if (liveSessionUp && getModel(modelId) === model) {
          unitSetup = read;
          simView?.unitStateChanged();
        }
      })
      .catch((err: unknown) => stopLiveOnError(errorText(err)));
  }, CUE_READ_SETTLE_MS);
}

/** The addresses the unit announces its settings, its operation mode, its scene pointers, its
 *  microSD card and Software Integration at, which a session on an experimental launch registers. */
function unitStateNotifyAddrs(model: DeviceModel): [number, number, number][] {
  const setup = [
    PARAMS.BRIGHTNESS,
    PARAMS.AUTO_POWER_OFF,
    PARAMS.AUTO_POWER_OFF_TIME,
    PARAMS.DEVICE_LANGUAGE,
    PARAMS.USB_SUPPRESSION,
    PARAMS.UDK_BANK,
    ...(model.hasHDMI ? [PARAMS.HDMI_HDCP, PARAMS.HDMI_INPUT_CHANNELS] : []),
    ...(model.hasSD ? [PARAMS.DATE_FORMAT, PARAMS.TIME_FORMAT, PARAMS.TIME_ZONE] : []),
  ].map((p): [number, number, number] => [p.id, 0, 0]);
  const knobs = [PARAMS.UDK_FUNCTION, PARAMS.UDK_PARAM1, PARAMS.UDK_PARAM2].flatMap((p) =>
    Array.from({ length: UDK_SLOTS }, (_, y): [number, number, number] => [p.id, 0, y]),
  );
  return [
    ...setup,
    ...knobs,
    [PARAMS.OPERATION_MODE.id, 0, 0],
    [PARAMS.CURRENT_SCENE.id, 0, 0],
    [PARAMS.SCENE_CURSOR.id, 0, 0],
    ...(model.hasSD ? [[PARAMS.SD_CARD.id, 0, 0] as [number, number, number]] : []),
    ...dawIntegrationNotifyAddrs(),
  ];
}

/** The addresses the unit announces each MIC/LINE jack's engaged Clip Safe at, which every session
 *  registers: the CONSOLE and the Inspector show it. */
function clipSafeNotifyAddrs(model: DeviceModel): [number, number, number][] {
  return micLineJacks(model).map((j): [number, number, number] => [PARAMS.CLIP_SAFE_ENGAGED.id, 0, j]);
}

/** Take an engaged-Clip-Safe notify. False for any other address. */
function takeClipSafeNotify(paramId: number, y: number, value: number): boolean {
  if (paramId !== PARAMS.CLIP_SAFE_ENGAGED.id) return false;
  if (value !== 0) clipSafeEngaged.add(y);
  else clipSafeEngaged.delete(y);
  paintClipSafe();
  return true;
}

/** Show each jack's engaged Clip Safe on the surfaces that carry it. */
function paintClipSafe(): void {
  consoleView.clipSafeChanged();
  markClipSafe(inspectorHost, (jack) => liveSessionUp && clipSafeEngaged.has(jack));
}

/** The MIC/LINE jacks `model` carries, 0 = the first. */
function micLineJacks(model: DeviceModel): number[] {
  return model.nodes.flatMap((n) => {
    const base = MIC_LINE_JACK_BASE[n.id];
    return base === undefined ? [] : [base, base + 1];
  });
}

/** Take a notify at one of `unitStateNotifyAddrs`. False for any other address. */
function takeUnitStateNotify(paramId: number, y: number, value: number): boolean {
  if (paramId === PARAMS.OPERATION_MODE.id) {
    unitOperationMode = value;
    simView?.unitStateChanged();
    return true;
  }
  if (paramId === PARAMS.CURRENT_SCENE.id) {
    sceneVersions.current++;
    unitScene = { ...unitScene, current: value };
    simView?.unitStateChanged();
    return true;
  }
  if (paramId === PARAMS.SCENE_CURSOR.id) {
    sceneVersions.cursor++;
    takeUnitCursor(value);
    return true;
  }
  if (paramId === PARAMS.SD_CARD.id) {
    cardVersion++;
    unitCardIn = value !== 0;
    simView?.unitStateChanged();
    return true;
  }
  const field = integrationFieldOf(paramId);
  if (field !== null) {
    if (unitIntegration) unitIntegration = withIntegrationNotify(unitIntegration, paramId, value) ?? unitIntegration;
    integrationVersions[field]++;
    simView?.unitStateChanged();
    return true;
  }
  if (!unitStateNotifyAddrs(getModel(modelId)).some(([id, , at]) => id === paramId && at === y)) return false;
  scheduleUnitSetupRead();
  return true;
}

/** Take the unit's SCENE LIST cursor at `raw`: the glass's own row gives way to it, and so does the
 *  glass's own tab where the row is on one tab alone. */
function takeUnitCursor(raw: number): void {
  const tab = sceneTab(raw);
  unitScene = { ...unitScene, cursor: raw, tab: tab ?? unitScene.tab };
  simView?.unitTookOwn(tab === null ? [SCENE_CURSOR_PATH] : [SCENE_CURSOR_PATH, SCENE_TAB_PATH]);
}

/** Read the scene pointers again behind every unit write queued so far, and take each the unit
 *  holds unless the unit announced it while the read ran or the session that asked has ended. A
 *  cursor read at the row it already stands on leaves the glass's own row. */
function rereadScenePointers(): void {
  if (!liveSessionUp || !experimentalOn) return;
  const epoch = liveEpoch;
  const ours = (): boolean => liveSessionUp && liveEpoch === epoch;
  cueWrites = cueWrites
    .then(async () => {
      await live?.idle();
      if (!ours()) return;
      const since = { ...sceneVersions };
      const current = await vdGet(PARAMS.CURRENT_SCENE.id, 0, 0);
      const cursor = await vdGet(PARAMS.SCENE_CURSOR.id, 0, 0);
      if (!ours()) return;
      if (sceneVersions.current === since.current) unitScene = { ...unitScene, current };
      if (sceneVersions.cursor === since.cursor && cursor !== unitScene.cursor) takeUnitCursor(cursor);
      simView?.unitStateChanged();
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

/** Read the unit's microSD card again behind every unit write queued so far, and take it unless the
 *  unit announced a change while the read ran. */
function rereadCardIn(): void {
  if (!liveSessionUp || !experimentalOn || !getModel(modelId).hasSD) return;
  const epoch = liveEpoch;
  const ours = (): boolean => liveSessionUp && liveEpoch === epoch;
  cueWrites = cueWrites
    .then(async () => {
      await live?.idle();
      if (!ours()) return;
      const since = cardVersion;
      const card = (await vdGet(PARAMS.SD_CARD.id, 0, 0)) !== 0;
      if (!ours()) return;
      if (cardVersion === since) unitCardIn = card;
      simView?.unitStateChanged();
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

/** Read SETUP > Software Integration again behind every unit write queued so far, and take each
 *  value the unit holds unless that value changed while the read ran. */
function rereadDawIntegration(): void {
  if (!liveSessionUp || !experimentalOn) return;
  cueWrites = cueWrites
    .then(async () => {
      await live?.idle();
      if (!liveSessionUp) return;
      const since = { ...integrationVersions };
      const read = await readDawIntegration();
      if (!liveSessionUp) return;
      const moved = new Set(INTEGRATION_FIELDS.filter((f) => integrationVersions[f] !== since[f]));
      unitIntegration = mergeIntegrationRead(unitIntegration, read, moved);
      simView?.unitStateChanged();
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

/** Forget what a session read of the unit's states: it ended. The settings stay, as Follow USB
 *  does. */
function resetUnitSessionState(): void {
  if (unitSetupReadTimer !== null) clearTimeout(unitSetupReadTimer);
  unitSetupReadTimer = null;
  unitOperationMode = null;
  unitScene = { current: null, cursor: null, tab: null };
  unitIntegration = null;
  unitCardIn = null;
  clipSafeEngaged.clear();
  paintClipSafe();
  simView?.unitStateChanged();
}

const cueBar = $<HTMLElement>("cue-bar");
const cueBarNames = $<HTMLElement>("cue-bar-names");
const cueBarClear = $<HTMLButtonElement>("cue-bar-clear");
cueBarClear.addEventListener("click", () => clearCues());

function renderCueBar(): void {
  const names = cues.nodes();
  cueBar.hidden = names.length === 0;
  setSpokenText(cueBarNames, t().cueBar.label(names.map((n) => graph.labelOf(n)).join(", ")));
  cueBarClear.textContent = t().cueBar.clear;
}

/** Repaint everything that shows the cues: both views, the status bar and the MIDI feedback. */
function renderCues(): void {
  graph.setCues(cues.nodes());
  consoleView.syncCues();
  refreshInspectorCue();
  renderCueBar();
  midi?.scheduleFeedback();
  simView?.unitStateChanged();
}

/** Repaint the panel's [CUE] row in place: what is cued, or whether a press on the row switches
 *  anything, changed. Nothing else in the panel is rebuilt, so a field being typed in or a
 *  picker held open keeps what it holds. */
function refreshInspectorCue(): void {
  syncInspectorCue(inspectorHost, inspectorActions.cueOf);
}

const graph = new Graph(graphHost, getModel(modelId), plan, {
  onSelect: (sel) => {
    selection = sel;
    // Ungated, and it clears the gate first. A selection change is the one refresh the
    // panel's in-flight input cannot outrank: the picker or composition it would be held
    // for belongs to the node being left. Held, the panel goes on describing that node —
    // which is what a press on another node did while a `<select>` in the panel still had
    // focus, since choosing in one leaves focus there.
    rebuildInspectorNow();
  },
  onStatus: (msg) => setStatus(msg),
  onChange: () => {
    markChanged();
    refreshInspector();
  },
  onHiddenChange: (hidden) => rememberHidden(modelId, hidden),
  mayEdit: () => planTakesEdits(),
});

// External MIDI control (desktop only, assigned in the !DEMO block below).
// Declared before the console so its learn hooks can close over the variable.
let midi: MidiControl | null = null;

// MIDI-learn hooks, shared by every surface a control can be armed from: the
// CONSOLE strips and the channel tuning screens. One object, so the two cannot
// disagree about what is armed or what an assignment reads as. No-ops while midi
// is absent (browser / demo), which is what keeps those builds untouched.
const midiLearnHooks: MidiLearnHooks = {
  learnActive: () => midi?.learnActive() ?? false,
  armedId: () => midi?.armedId() ?? null,
  isMapped: (id) => midi?.isMapped(id) ?? false,
  addrOf: (id) => midi?.addrOf(id) ?? null,
  arm: (id) => midi?.arm(id),
};

// Device setup modal (the unit's SETUP > GENERAL). Desktop only, assigned in the
// !DEMO block below; the language listener re-renders it through this handle.
let deviceSetup: DeviceSetupPanel | null = null;

// Mixer-style CONSOLE view: an alternate view of the same plan. Its edits go
// through the same change funnel (markChanged) so Live sync mirrors them. The
// live signal meters stream only while Live sync is on (consoleView.setLive).
const consoleView = new Console(consoleHost, {
  getModel: () => getModel(modelId),
  getPlan: () => plan,
  clipSafeEngaged: (jack) => liveSessionUp && clipSafeEngaged.has(jack),
  // A console edit changed the plan: flag dirty + schedule live sync. The console
  // re-renders the edited strip itself, so don't rebuild it here (that would
  // disrupt an in-progress fader drag).
  onChange: (written, defaults) => {
    noteSeededDefaults(plan, seededDefaults, defaults ?? []);
    markChanged("ui", written);
    // The panel this view hides was drawn before the edit, and its locks and ranges are
    // taken at draw time: it is rebuilt on the way back to the graph.
    inspectorDeferred = true;
  },
  // The meter stream failed to register. Floor-stuck bars read as "no signal",
  // so end the session rather than let the operator trust a dead display.
  onMeterError: (message) => stopLiveOnError(errorText(message)),
  onOpenDynScreen: (kind, id) => dynScreen.open(DYN_PROCESSORS[kind], id),
  onSetFxEffectType: (id, type) => inspectorActions.onSetFxEffectType(id, type),
  onSwitchSide: (channelId, side) => switchSide(channelId, side, "app"),
  // A strip's [CUE] chip: switchable while a session is up.
  cue: {
    has: (node) => cues.has(node),
    any: () => cues.size > 0,
    set: (node, on) => setCue(node, on, "app"),
    enabled: () => liveSessionUp,
  },
  // MIDI learn: while learn mode is on, console controls arm for binding instead
  // of editing.
  midi: midiLearnHooks,
});

// The SIM view: the unit's LCD as a third surface over the same plan (experimental launch
// only — the gate below builds it). Its edits go to the funnels the Inspector uses; every
// other change to the plan reaches it through simView?.planChanged() beside the MIDI feedback.
const buildSimView = (): SimView =>
  new SimView(
    $<HTMLElement>("sim-host"),
    {
      getModel: () => getModel(modelId),
      getPlan: () => plan,
      apply: (edit) => planTakesEdits() && applySimEdit(edit),
      onRefusedAction: () => setStatus(t().status.simUnitAction),
      isLive: () => liveSessionUp,
      locks: (paths) =>
        simLocks(
          paths,
          setupGlassPaths(getModel(modelId)),
          { ...rateHoldState(), followUsbOn: followUsbState === true },
          {
            unitOnly: t().sim.lockUnitOnly,
            notAccepted: t().inspector.prePostLcdOnly,
            unitOp: t().sim.lockUnitOp,
            ...rateHoldReasons(),
            trackCountLive: t().inspector.sdRecTrackCountLive,
            integrationOff: t().sim.lockIntegrationOff,
            setupOffline: t().sim.lockSetupOffline,
          },
        ),
      releaseMeters: () => consoleView.releaseMeters(),
      regainMeters: () => consoleView.regainMeters(),
      onMeterError: (message) => stopLiveOnError(errorText(message)),
      unitSendsTarget: () => (liveSessionUp ? sendsTarget : null),
      setUnitSendsTarget: (target) => planTakesEdits() && chooseSendsTarget(target),
      unitCues: () =>
        liveSessionUp ? new Map(cueAddrs(getModel(modelId)).map((a) => [a.node, cues.has(a.node)])) : null,
      setUnitCue: (node, on) => {
        if (!liveSessionUp) return false;
        setCue(node, on, "app");
        return true;
      },
      clearUnitCues: () => {
        if (!liveSessionUp) return false;
        clearCues();
        return true;
      },
      unitCueMemory: () => (liveSessionUp ? cues.restorable() : null),
      unitValues: unitGlassValues,
      isUnitPath: (path) =>
        path === "setup.followUsb" || integrationSlotAt(path) !== null || isSetupGlassPath(getModel(modelId), path),
      writeUnitValue: writeUnitGlassValue,
      clipSafeEngaged: (jack) => liveSessionUp && clipSafeEngaged.has(jack),
    },
    detectSimScale(),
  );

// Device follow: the reverse of live sync. While live, parameter
// changes made on the device itself (LCD / physical controls) are pulled back
// into the plan via a debounced readback that reuses applyDeviceState, then
// re-rendered. Echoes of our own writes are filtered by the live snapshot. Null
// in the demo / when live is absent, so it tree-shakes out with the rest of the
// control layer.
// Reflect a device-follow change back onto the plan's two views. A direct notify
// (fader / pan / on) touches one node's scalar and already patched its snapshot
// entry (noteDirect), so it only needs the touched nodes / strips repainted. A
// scoped / full read-back can change anything, so it re-derives both views and
// re-bases the whole snapshot. Selection/viewport are untouched (refresh, not
// rebuild). graphDirty defers the graph's work while it is the hidden view (console
// active); setView does it once on the way back, and inspectorDeferred is the same
// shape for the inspector, which the console view hides for as long as it is up.
let graphDirty = false;
let inspectorDeferred = false;
const followDirtyNodes = new Set<string>();
/**
 * The one ritual for "the device authored this into the plan", shared by the two
 * direct-follow hooks (a numeric scalar and a rename) and by the write-adoption, which is
 * not a follow at all — no notify arrives, the write path is writing back what a converge
 * confirmed — and passes its own `kind` for that reason. `place` performs the mutation and
 * answers whether it landed.
 *
 * The device authored these keys, so the history takes exactly them into its
 * baseline — the per-key rule the refetch path runs on, which is what the other sites that
 * write device values into the plan outside a readback run on too. A whole-plan `rebase()`
 * here dropped the entry an app gesture had open, which made an edit taken while the
 * unit was being touched silently un-undoable AND spent the entry beneath it on the
 * Ctrl+Z that should have taken it back.
 *
 * Diffed from a clone taken here because `place` reports only whether it placed the
 * value: where it lands is several places (a fader / pan / assign ON is a connection
 * param, a name is `nodeNames`, everything else a node param), and a scoped differ
 * that missed one would silently stop absorbing. A whole-plan clone + diff measures
 * 0.12 ms for the URX44V default plan against a stream of ~10 notifies/s.
 *
 * One definition rather than two: this rule is load-bearing and the comment above is
 * the record of what re-opening it costs, so a second copy kept in step by hand is
 * the shape that goes wrong quietly.
 */
function authorFromDevice(node: string, place: () => boolean, kind: WriteSource = "follow-direct"): boolean {
  const before = clonePlanState(plan);
  if (!place()) return false;
  const moved = diffPlans(before, plan);
  // The unit is where these values came from, so they are not something to warn the operator
  // about before a write: the write is what agrees with them.
  markSource(plan, patchContestNames(moved), "device");
  // The trace kind is the caller's, because the callers are not one writer: a follow is a
  // notify landing, while the write-adoption is the device-action funnel writing back what a
  // write confirmed. Both are device-side writers, so no invariant's verdict turns on the
  // split — what it buys is a ledger a person can read without guessing which one wrote.
  traceProbe?.sample(kind);
  followDirtyNodes.add(node);
  planHistory?.absorb(moved);
  return true;
}
/** Snap a STEREO-linked pair a device read brought in, and settle what the snap wrote.
 *
 *  The one seat for every device-side caller, because two things have to happen around the
 *  write and neither is the caller's subject. It runs BEFORE the read's own trace sample, so
 *  the position it writes is attributed to that read rather than to whichever writer samples
 *  next — the reflect that repaints it is coalesced across producers and cannot name one. And
 *  the keys go into the history's baseline rather than into an undo entry: the snap is a
 *  layout consequence of a device change, so a later gesture must not carry it, and the
 *  reconcile's own reset does not cover a read that authored nothing itself.
 *
 *  The board owns the geometry, so the graph is asked rather than the plan: which positions
 *  count as the pair's slot is a question about what is drawn, notes included.
 *
 *  Returns whether anything moved, because a caller whose reflect repaints only the nodes its
 *  read named has to widen it: the partner this moves is not one of them, and the heart tie is
 *  rebuilt by a full render alone. */
function snapLinkedPairs(): boolean {
  const before = clonePlanState(plan);
  if (!graph.alignLinkedPairs()) return false;
  planHistory?.absorb(diffPlans(before, plan));
  return true;
}

/** Record that a device read supplied these keys.
 *
 *  The patch is what the merge ACTUALLY adopted — a key the read left alone because the plan
 *  had moved under it is not in there, and neither is one a failed or cancelled read never
 *  reached. Without this a value the unit replaced keeps reading as the document's, so a later
 *  divergence in it is never mentioned; `authorFromDevice` covers only the follow-side applies.
 */
function notePatchFromDevice(patch: PlanPatch): void {
  markSource(plan, patchContestNames(patch), "device");
}

/** After a write the device confirmed, the plan takes the values that were actually sent.
 *
 *  Which values those are is `confirmedAdoptions` (`app/adopt-writes.ts`), which needs the plan
 *  the converge RAN AGAINST as well as the live one — the live flush clones before its await,
 *  and an address is a different key under a different effect type. Keyed on the ADDRESSES the
 *  device confirmed rather than on the write having succeeded: the two come apart exactly where
 *  this runs, since a session whose snapshot already agrees sends nothing, so a flush can
 *  succeed without the address in question being in it at all.
 *
 *  Authored FROM the device rather than pushed as an edit, through the seat a device-side
 *  recompute already takes: the value is the write path's rather than the operator's, and an
 *  undo that put the unwritable raw back would only have it normalised again on the next write.
 *  Returns how many the plan took, so a caller can say so beside its own outcome. */
function adoptConfirmedWrites(confirmed: ReadonlySet<number>, sent: Plan = plan): number {
  const mine = confirmedAdoptions(getModel(modelId), sent, plan, confirmed);
  if (!mine.length) return 0;
  let taken = 0;
  for (const node of new Set(mine.map((b) => b.node))) {
    const forNode = mine.filter((b) => b.node === node);
    if (authorFromDevice(node, () => (applyParamRange(plan, forNode), true), "device-action")) taken += forNode.length;
  }
  if (taken) requestReflect();
  return taken;
}
// What the live flush's converge took back, waiting for the line `onSent` writes.
let liveAdopted = 0;
// What a follow read took back — a value the plan held outside what this app can write, read
// from the unit as the nearest value it can send — waiting for the next line that reports a
// read or a flush.
let followBounded = 0;
/** The bounded values waiting to be reported, as the tail of a status line, and spent. */
function takeBoundedNote(extra = 0): string {
  const n = followBounded + extra;
  followBounded = 0;
  return n ? ` — ${t().status.paramsBounded(n)}` : "";
}
let followFull = false;
// How many keys the follow reads behind the pending reflect actually authored. A COUNT
// rather than a flag because the reflect is coalesced at ~20 Hz, so several reads can
// land before one runs and a flag ASSIGNED per call would let the last one speak for
// all of them; it accumulates instead. Only its emptiness is read — the keys
// themselves are the reads' own business, and each already absorbed or re-based with
// them at its own site. Both reconcile hooks add through followAuthor; the reflect drains it.
let followAuthored = 0;
/** Settle the history for what a reconcile authored. A patch that moved the sample rate and
 *  nothing else is taken into the baseline here, and takes the rate out of the undo entry whose
 *  move the unit took back, or the redo entry whose move the unit made; every entry stands
 *  otherwise. Any other patch is counted for the reflect, which drops both stacks. */
function followAuthor(patch: PlanPatch): void {
  if (patch.length > 0 && patch.every((e) => e.field === "sampleRate")) planHistory?.takeBackRate(patch);
  else followAuthored += patch.length;
}
function reflectFollow(): void {
  const ids = [...followDirtyNodes];
  followDirtyNodes.clear();
  // Not planReadFromDevice: this funnel is coalesced across producers, so it cannot know
  // what the device authored and a whole-plan re-base here takes the operator's open
  // gesture with it. Each producer settles the history at its own site instead.
  planValuesChanged();
  if (followFull) {
    followFull = false;
    if (graphHost.hidden) graphDirty = true;
    else graph.refresh();
    syncRateUi(); // also refreshes the console (applyRateConstraints)
    dynScreen.refresh();
    // A readback of any breadth re-authored the plan's values from the device, so
    // no earlier entry describes a state it can return to. The snapshot is NOT
    // re-based here: only the private copy the read ran against says what the device
    // holds, and the reconcile hooks re-base from it the moment their read resolves.
    //
    // Conditional on the read having authored anything, because that premise is what
    // the reset rests on: a reconcile that agreed with the plan at every key
    // invalidates no earlier entry, and wiping up to 100 undo entries for it is loss
    // with nothing bought. Both producers of a no-op reconcile reach here — a late
    // announcement mistaken for a device edit, and a write the unit never announced
    // (follow.ts's idle net) — and neither is rare enough to pay for. Nothing is
    // absorbed in the other arm: PlanHistoryStack.absorb returns immediately on an
    // empty patch, so the fallback would be a no-op spelled as code. A read that moved
    // the sample rate alone is not counted here: followAuthor absorbed it at its hook.
    const authored = followAuthored;
    followAuthored = 0;
    if (authored) planHistory?.reset();
  } else {
    // Direct-only: repaint just the changed nodes / strips. The snapshot is already
    // current from noteDirect, so no full re-translate. Only one view is visible.
    if (graphHost.hidden) graphDirty = true;
    else graph.repaintDirtyNodes(ids);
    for (const id of ids) consoleView.refreshStrip(id);
    // The inspector renders ONE selection, but what it draws is read off more nodes than
    // that selection names: a device-side Pan Link ON removes the send PAN
    // (mixSendLocks), and an unrefreshed panel leaves the removed control on screen and
    // live, writing the very address the MIDI catalog is refusing at the same instant.
    // Scoped to the nodes the panel reads, so a device sweep of an unselected node does
    // not rebuild the panel at this branch's ~20 Hz.
    const shown = inspectorNodes(getModel(modelId), plan, selection);
    if (ids.some((id) => shown.includes(id))) refreshInspector();
    // The dynamics screen shows a snapshot of the same node params, so a
    // device-side edit under it would otherwise leave stale sliders on screen.
    dynScreen.refresh(ids);
  }
  // A follow that leaves a channel with +48V and HI-Z both on says so once per change of
  // the set; the plan keeps the unit's state and the app writes nothing for it.
  const both = bothOnLabels();
  if (both && both !== bothOnNoted) setStatus(t().status.phantomHiZBothOn(both));
  bothOnNoted = both;
}

// The jacks holding +48V and HI-Z both on, as the status line last named them.
let bothOnNoted = "";
function bothOnLabels(): string {
  const model = getModel(modelId);
  return phantomHiZBothOn(model, plan)
    .map((j) => jackName(model, plan, j, (id) => graph.labelOf(id)))
    .join(", ");
}
/** A device read's status line, led by what it did about +48V / HI-Z: the ONs it refused
 *  (`merged`, when the read is one that can refuse), then the channels it left with both on.
 *  The plan keeps what the unit holds; the app writes nothing for either.
 *
 *  A line that replaces one still leading with a read's refusals goes on leading with them:
 *  the full read device follow runs once a burst goes quiet lands on that line, and a line
 *  anything else has replaced since carries nothing forward. */
function withSwitchNotes(msg: string, merged?: MergedRead): string {
  bothOnNoted = bothOnLabels();
  const both = bothOnNoted ? [t().status.phantomHiZBothOn(bothOnNoted)] : [];
  let notes = refusalNotes(merged);
  if (!notes.length && refusalLine && statusbar.textContent === refusalLine.line) notes = refusalLine.notes;
  const line = [...notes, ...both, msg].join(" — ");
  refusalLine = notes.length ? { line, notes } : null;
  return line;
}
/** The last status line that led with a read's refusals, and those refusals. */
let refusalLine: { line: string; notes: string[] } | null = null;
/** One sentence per switch a read refused (`MergedRead.refusedOn`), channels in model order. */
function refusalNotes(merged: MergedRead | undefined): string[] {
  if (!merged?.refusedOn.length) return [];
  const model = getModel(modelId);
  const named = (key: InputSwitch): string =>
    merged.refusedOn
      .map((r) => ({ node: r.nodeId, sw: switchOfKey(r.key) }))
      .filter((r) => r.sw?.on === key)
      .map((r) => jackName(model, plan, { node: r.node, side: r.sw!.side }, (id) => graph.labelOf(id)))
      .join(", ");
  const phantom = named("phantom");
  const hiZ = named("hiZ");
  return [
    ...(phantom ? [t().status.phantomRefusedByRead(phantom)] : []),
    ...(hiZ ? [t().status.hiZRefusedByRead(hiZ)] : []),
  ];
}
/** A read refused ONs the operator made while it was in flight and took their keys back to the
 *  unit's values (`MergedRead.refused`): the edit leaves the undo history, so no entry puts it
 *  back, and the keys are recorded as the unit's. */
function takeRefusedEdits(merged: MergedRead): void {
  if (!merged.refused.length) return;
  planHistory?.retract(merged.refused);
  notePatchFromDevice(merged.refused);
}
// What the refetch inside a flush refused, for the line the flush writes once it has sent — the
// refetch writes none of its own, and that line would replace it.
let flushReadNotes: string[] = [];
// A reconcile read that fails loses the device-side change it was called for —
// the notify already fired and nothing re-triggers the read — and the next
// converge would then write the plan's stale value back over the operator's own
// edit on the hardware. Throwing takes DeviceFollow's stop-following path, which
// its hook contract already declares. Shared by both reconcile hooks so the rule
// has one spelling.
function assertReadComplete(merged: MergedRead, label: string): void {
  // What the read HELD is reported here rather than at each call site, so no caller can
  // leave the plan keeping values the unit does not have with nothing saying so. A read
  // with errors merged nothing (followRead's accept), so it held nothing either.
  if (merged.held.length) console.warn("device read: the plan keeps its own value", merged.held);
  if (!merged.errors.length) return;
  console.warn(label, merged.errors);
  throw new Error(linkFailureIn(merged.errors) ?? t().error.followReadIncomplete(merged.errors.length));
}
// A merged device read could not place part of its result: a wire the operator removed
// while it was in flight, or an edit a device-side routing change left nowhere to land.
// What could be applied was; the rest is reported rather than dropped in silence — the
// same rule the history's own apply follows. Not a link failure, so it does not abort.
function noteMergeConflicts(merged: MergedRead): void {
  if (merged.unplaced.length) console.warn("device read: merge targets no longer in the plan", merged.unplaced);
}
/** A Fetch or a Live-sync start found a receiver the unit never leaves without a source on
 *  NONE (`MergedRead.unsourced`): the plan takes the source a new plan carries, recorded as the
 *  fill's, so the write confirm names that receiver when a write moves the unit onto it. A plan
 *  that already holds a wire there — one drawn while the read ran — keeps it. The read's own
 *  view keeps the unit's state, so the snapshot a Live session starts from holds nothing there
 *  and its next flush sends the source. Returns the note that leads the status line, or null
 *  when nothing was supplied. */
function supplyUnsourced(merged: MergedRead): string | null {
  const inputs = (merged.unsourced ?? []).map((id) => ref(id, "in"));
  const wires = supplyRequiredSources(getModel(modelId), plan, inputs);
  if (!wires.length) return null;
  markSource(
    plan,
    wires.map((w) => connectionContestKey(w.from, w.to)),
    "default",
  );
  return t().status.streamingSourceUnlisted;
}
// The send-back, which only a complete read may ask for: a partial read is about to
// drop the link, and a flush issued into that writes nothing and reports nothing. The
// snapshot has just re-based onto the unit's values, so plan and device disagree exactly
// where the read held, and the ordinary outgoing diff is what puts them back — for insert
// FX, the selector, then the stored engine values, then the bypass intent.
//
// The insert-FX half schedules a flush nothing else would: no plan key moved, so no edit
// funnel ran. The source half (sourceChoiceHoldKeys) held against an edit that did run,
// so its own flush is already scheduled and this one is a second ask for the same values.
//
// A read holds insert FX only where it has rate evidence (insertFxHoldKeys) — its own, or
// one the unit announced while it ran. Both reconcile paths hand over the same hold for
// that reason: a scoped read establishes no rate of its own and is still the first thing
// to see a selector the unit has just cleared, so the pairing cannot be skipped at one of
// them.
function reapplyHeld(merged: MergedRead): void {
  if (!merged.held.length) {
    setStatus(withSwitchNotes(t().status.liveFollowed(merged.applied) + takeBoundedNote(), merged));
    return;
  }
  // A follow read outlives a session that merely ended (`abandonFollowWork` is not called
  // from `deactivateLive`), and `schedule()` is a no-op once it has — worth telling apart
  // in the log from a flush that went out.
  if (live?.isActive()) {
    live.schedule();
    const held = heldByHold(merged.held);
    setStatus(
      withSwitchNotes(t().status.liveHeld(merged.applied, held.unrunnable, held.source) + takeBoundedNote(), merged),
    );
    return;
  }
  // Nothing left to send them through: the values stay in the plan, and the status line
  // belongs to whatever ended the session. Writing "re-sent" over it would name an action
  // that did not happen and cannot.
  console.warn("device read: no session left to send the held values back through", merged.held);
}
// Single funnel for every device-follow reflect (direct notifies and the scoped /
// full read-backs alike): a knob sweep delivers notifies in ~30/s IPC batches, any
// of which would otherwise drive a full graph rebuild + snapshot re-base. Coalesce
// them onto one timer capped at ~20 Hz (the device streams at ~10 Hz). applyDirect
// and the read-backs have already written the latest values into the plan, so a
// deferred reflect still renders current truth — no trailing state is lost.
const REFLECT_MIN_MS = 50;
let reflectTimer = 0;
let lastReflect = 0;
function requestReflect(): void {
  if (reflectTimer) return;
  const wait = Math.max(0, REFLECT_MIN_MS - (performance.now() - lastReflect));
  reflectTimer = window.setTimeout(() => {
    reflectTimer = 0;
    lastReflect = performance.now();
    reflectFollow();
  }, wait);
}
// A follow-side device read (the two reconciles, the 1-knob refetch) hands the module
// `plan` to the readback by reference and comes back hundreds of round trips later.
// Nothing raises deviceReadInFlight for these — the Fetch button raises it for its own
// read — so File > New / Open / a drop / a model switch lands mid-read and the read goes
// on filling a document that is no longer open.
//
// Both halves below are needed. The abort stops round trips that are now for nothing;
// but the readback checks the signal at group boundaries, so a read can still resolve
// after it, and readIntoPlan's own identity guard is what keeps the merge — and the
// epilogue behind it — off the plan that replaced the one it read. The set doubles as
// the "a follow read is in flight" predicate the history refuses under: an undo taken
// inside one patches values the read is about to merge over, and unlike a file flow it
// is refused rather than lost. Membership rather than a separate count, so that
// abandonFollowWork's clear also ends the refusal — a read bound to a discarded plan
// provably cannot touch the open one, so it must not keep undo shut.
type FollowRead = { abort: () => void; done: Promise<void> };
const followReads = new Set<FollowRead>();
// Routes the UNIT announced an insert-FX change on, appended in notify order. A read is
// not a snapshot: its addresses are answered hundreds of milliseconds apart, so a change
// landing inside one is caught on the addresses read after it and missed on those read
// before, and comparing two of its values says nothing about what happened between them.
// The notify stream is what carries the order — and it tells the two clearings apart,
// where the read's values cannot: a Signal Type transition announces the insert-FX
// addresses it clears, the sample-rate excursion announces only the rate. Appended
// rather than cleared per read, because two follow reads can be in flight at once and a
// reset would take the evidence of one out from under the other; each takes the slice
// that arrived inside it, and the list is emptied when the last read ends.
const announcedInsertFx: string[] = [];
// Every sample rate the unit announced, from the same stream. Taken WHOLE rather than
// sliced to a read's own window, unlike the list above: the rate notify that escalates to
// the read arrives before it starts, and it is the one carrying the rate that cleared the
// effect. An excursion can also be over before the read asks for the rate at all — 48 →
// 96 → 48 leaves the read holding 48, where the effect runs and the clearing then reads
// as the operator's own.
//
// Each entry carries the sequence number it arrived at, and a read consumes the ones that
// were already there when it started — what arrived while it ran belongs to the reconcile
// it scheduled. By SEQUENCE rather than by array position, and independently of whatever
// else is in flight: a side-effect refetch overlapping the read establishes no rate of its
// own, so tying the consumption to an empty in-flight set left the announcement standing
// after the read it was for had finished with it, and the next clearing the operator made
// by hand on the unit was held against a rate the unit had left long before.
const announcedRates: Array<{ seq: number; hz: number }> = [];
let rateSeq = 0;

// Everything device-follow has in flight for a plan that is being replaced. Called at
// each wholesale reassignment of `plan`, and deliberately not from deactivateLive: with
// the document still on screen, a read stopped half way leaves it a mix of device and
// plan values with nothing marking which is which — the hazard a Fetch avoids by reading
// into a private copy, so cancelling one leaves the document on screen untouched — so a
// session that merely ends lets its read finish. A
// queued reflect goes too — it names nodes in the outgoing plan and its full path resets
// the history of whatever replaced it; loadPlan re-renders the new plan whole anyway.
function abandonFollowWork(): void {
  for (const r of followReads) r.abort();
  followReads.clear();
  // The reads that would have consumed these are gone, so nothing is left to own them.
  announcedInsertFx.length = 0;
  announcedRates.length = 0;
  if (reflectTimer) {
    clearTimeout(reflectTimer);
    reflectTimer = 0;
  }
  followDirtyNodes.clear();
  followFull = false;
  followAuthored = 0;
}

/** What the live session knows about the unit's +48V / HI-Z beyond what a follow read finds
 *  (input-lock.ts `SwitchSession`): what it last sent and captured, and what the unit has
 *  announced since. A session that has ended still holds the first two for the read it lets
 *  finish. */
const switchSession: SwitchSession = {
  holdsOn: (nodeId, key) => {
    const a = live ? switchAddr(getModel(modelId), nodeId, key) : null;
    return a !== null && Boolean(live?.unitHoldsOn(a.paramId, a.x, a.y));
  },
  sentOn: (nodeId, key) => {
    const a = live ? switchAddr(getModel(modelId), nodeId, key) : null;
    return a !== null && Boolean(live?.holdsSent(a.paramId, a.x, a.y, 1));
  },
};

/** Run a follow-side device read as a merged read (readback.readIntoPlan), carrying the
 *  abort handle that makes it abandonable. Null means the plan it was issued for is no
 *  longer the open document — every caller returns without its epilogue, which is what
 *  keeps a status line, a provenance stamp and a history reset off a document the read
 *  never touched. The drop reaches the console only: the replacement that discarded the
 *  plan ended the session and printed its own line. `edits`, when given, is a watch the
 *  caller opened before the read, and the merge skips what was authored since then. */
async function followRead(
  label: string,
  read: (into: Plan, signal: AbortSignal) => Promise<ReadbackResult>,
  edits?: PlanWriteWatch,
): Promise<MergedRead | null> {
  const controller = new AbortController();
  // The `done` half is what the session's own release waits on (releaseLive).
  let finished = (): void => {};
  const entry: FollowRead = {
    abort: () => controller.abort(),
    done: new Promise<void>((resolve) => (finished = resolve)),
  };
  followReads.add(entry);
  const mark = announcedInsertFx.length;
  // Where this read's own share of the rate history ends. Everything after it arrived
  // WHILE the read ran, which means it belongs to the reconcile it scheduled rather than
  // to this one.
  const rateMark = rateSeq;
  let establishedRate = false;
  // What the plan holds outside what this app can write, which the read may answer with the
  // nearest value it can send.
  const unwritable = paramRangeProblems(plan);
  try {
    const merged = await readIntoPlan(
      () => plan,
      (into) => read(into, controller.signal),
      edits ? { watch: () => edits } : planWrites,
      (ctx) => {
        establishedRate = ctx.deviceSampleRate !== undefined;
        const model = getModel(modelId);
        return new Set([
          ...insertFxHoldKeys(model, {
            ...ctx,
            announced: new Set(announcedInsertFx.slice(mark)),
            ratesSeen: announcedRates.map((r) => r.hz),
          }),
          ...sourceChoiceHoldKeys(model, ctx),
        ]);
      },
      // All or nothing, as the session's starting read is: a read that comes back with
      // errors merges none of what it got, so the reflect, the history and the snapshot
      // behind it are not built on a plan that is part device state and part never read.
      (result) => !result.errors.length,
      switchSession,
    );
    if (!merged) console.warn(`${label}: the plan was replaced during the read; its values are discarded with it`);
    else {
      notePatchFromDevice(merged.devicePatch);
      takeRefusedEdits(merged);
      followBounded += boundByRead(unwritable, plan);
    }
    return merged;
  } finally {
    followReads.delete(entry);
    finished();
    if (!followReads.size) announcedInsertFx.length = 0;
    // The rate history outlives a read that established no rate of its own, and a read
    // that did takes only what was already there when it started. Both halves are about
    // the same thing — the read that CONSUMES an announcement is not always the one that
    // was running when it arrived. A scoped read holds no rate at all, so emptying on
    // whichever read finished first took the evidence out from under the full read the
    // notify was for; and a full read already in flight has read the selector before the
    // unit cleared it, so it holds nothing itself and the announcement belongs to the
    // replay it scheduled. Measured: clearing the whole list there left that replay
    // adopting the clearing.
    if (establishedRate) {
      const keep = announcedRates.filter((r) => r.seq > rateMark);
      announcedRates.length = 0;
      announcedRates.push(...keep);
    }
  }
}

/**
 * Whether the unit is still holding what this session last sent to an address.
 *
 * Both parks read through it (`readback.sentOverlay`), and for the same reason: a park
 * answers the app's own write rather than a device-side event, so it arrives while an edit
 * the operator has already made is sitting in the plan waiting for the next flush. Where the
 * unit agrees with what was last sent it has nothing to say, and the read answers with the
 * plan's own value — so that edit survives instead of being replaced by the value it was
 * made against. The merge cannot cover it: what the merge protects is an edit made DURING a
 * read, and this one was made before it.
 */
function holdsSent(paramId: number, x: number, y: number, raw: number): boolean {
  return live ? live.holdsSent(paramId, x, y, raw) : false;
}

const follow =
  DEMO || !live
    ? null
    : new DeviceFollow({
        // The plan's follow set plus Follow USB and the LCD selection, which the plan
        // deliberately does not carry (params.ts): the badge and the side a stereo channel
        // shows are kept in step with the device.
        addrs: () => [
          ...(live?.followAddrs() ?? []),
          FOLLOW_USB_ADDR,
          LCD_SELECTION_ADDR,
          ...(experimentalOn ? [SENDS_TARGET_ADDR] : []),
          ...cueNotifyAddrs(getModel(modelId)),
          ...(experimentalOn ? unitStateNotifyAddrs(getModel(modelId)) : []),
          ...clipSafeNotifyAddrs(getModel(modelId)),
        ],
        // Held off while a flush is armed, running or queued: a read taken then answers an
        // edit the flush has not sent, or a write the unit has not announced, with the value
        // it replaces, and the merge takes that value over the operator's edit.
        deferReconcile: () => live?.isWriting() ?? false,
        intercept: (p) => {
          const at = (a: [number, number, number]): boolean => p.paramId === a[0] && p.x === a[1] && p.y === a[2];
          // The unit's own answer to a side this app wrote is consumed; any other selection
          // of a stereo channel shows that channel's side. Neither opens a reconcile.
          if (at(LCD_SELECTION_ADDR)) {
            if (lcdEchoes.take(p.value)) return true;
            // The unit moved its selection itself, so what this app last wrote is no longer what
            // the unit holds, and a switch still waiting to be written is older than this one.
            lcdLast = null;
            lcdNext = null;
            const sel = decodeLcdSelection(getModel(modelId), p.value);
            if (sel) switchSide(sel.channel, sel.side, "device");
            return true;
          }
          // HOME's [Sends] destination: the unit answering a write of this app's, or a choice on
          // its panel. Neither opens a reconcile.
          if (at(SENDS_TARGET_ADDR)) {
            if (!sendsEchoes.take(p.value)) takeSendsTarget(p.value);
            return true;
          }
          // The unit's settings, its operation mode and an engaged Clip Safe, which the SIM view
          // shows. None opens a reconcile.
          if (p.x === 0 && takeClipSafeNotify(p.paramId, p.y, p.value)) return true;
          if (experimentalOn && p.x === 0 && takeUnitStateNotify(p.paramId, p.y, p.value)) return true;
          // A [CUE] flag: the unit answering a write of this app's, a mirror it sets beside a
          // flag, or a press on its panel. Only the last changes anything.
          const cue = cueAt(getModel(modelId), p.paramId, p.x, p.y);
          if (cue) {
            if (!cue.mirror && !cueEchoes.take(cue.addr.param, cue.addr.y, p.value))
              setCue(cue.addr.node, p.value !== 0, "device");
            return true;
          }
          if (!at(FOLLOW_USB_ADDR)) return false;
          setFollowUsbBadge(p.value !== 0);
          return true;
        },
        // Dispatched on the value's type, because the two snapshots are separate maps:
        // the numeric one holds no entry for a name, so asking it would call the app's
        // own rename a device-side change and bounce it back round.
        isEcho: (p) =>
          (p.valueStr !== undefined
            ? live?.isEchoName(p.paramId, p.y, p.valueStr)
            : live?.isEcho(p.paramId, p.x, p.y, p.value)) ?? false,
        // Dispatched on the value's type for the reason `isEcho` is: names are queued apart
        // from the numeric writes.
        isSuperseded: (p) =>
          (p.valueStr !== undefined
            ? live?.hasUnannouncedName(p.paramId, p.y)
            : live?.hasUnannouncedWrite(p.paramId, p.x, p.y)) ?? false,
        nameOwner: (paramId, x, y) => live?.lookupName(paramId, x, y),
        lookup: (paramId, x, y) => live?.lookup(paramId, x, y),
        // Read for one thing only: which routes the unit announced an insert-FX change
        // on while a read was running (see `announcedInsertFx`). A Signal Type notify
        // names the pair's primary, and the transition it reports clears BOTH members,
        // so the partner is recorded with it.
        onDeviceParam: (p) => {
          if (p.paramId === PARAMS.SAMPLE_RATE.id) {
            announcedRates.push({ seq: ++rateSeq, hz: p.value });
            onAnnouncedRate(p.value);
          }
          const addr = live?.lookup(p.paramId, p.x, p.y);
          if (!addr?.node || !INSERT_FX_ANNOUNCED.has(addr.name)) return;
          announcedInsertFx.push(addr.node);
          const partner = partnerChannel(getModel(modelId), addr.node);
          if (partner) announcedInsertFx.push(partner);
        },
        // A direct (node-local scalar) change: decode the notify value straight into
        // the plan, no read-back, and record the node so the coalesced reflect
        // repaints just it. The reflect is scheduled by flushDirect.
        applyDirect: (node, name, value, y) => authorFromDevice(node, () => applyDirect(plan, node, name, value, y)),
        // A rename made on the unit's own LCD. It arrives as a string notify on a
        // name address, which the numeric follow path cannot carry — the value is
        // text, the address has no catalog entry, and the live snapshot holds no
        // entry to compare against. Placed straight into the plan like any other
        // direct follow: one node repainted, no readback.
        applyName: (paramId, x, y, value) => {
          const node = live?.lookupName(paramId, x, y);
          if (node === undefined) return undefined;
          authorFromDevice(node, () => {
            // Normalized like every other way a name enters the plan. The unit's own
            // screen cannot produce one longer than the bound, so an arriving name is
            // normally within it — but the wire accepts and stores longer, and an
            // unbounded name in the plan draws a label across its neighbours.
            const trimmed = normalizeNodeName(value);
            if (trimmed) plan.nodeNames[node] = trimmed;
            else delete plan.nodeNames[node];
            // The snapshot takes the device's OWN value, not the normalized one: the two
            // agreeing is what stops a flush from writing the operator's board edit
            // back off the board, and where they disagree the next flush is what
            // settles the device on the bounded name.
            live?.noteDirectName(paramId, y, value);
            return true;
          });
          return node;
        },
        noteDirect: (paramId, x, y, value) => live?.noteDirect(paramId, x, y, value),
        flushDirect: () => requestReflect(),
        // A settled scoped change: re-read just the touched owner nodes and reflect.
        // A read-back can change more than the direct fast path handles, so mark the
        // reflect full (re-derive both views + re-base the snapshot).
        // A reconcile read that fails loses the device-side change it was called
        // for — the notify already fired and nothing re-triggers the read — and
        // the next converge would then write the plan's stale value back over the
        // operator's own edit on the hardware. Reject so DeviceFollow takes its
        // stop-following path, the same one a rejected reconcile already took.
        reconcileNodes: async (nodeIds) => {
          // Taken before the read is issued: a direct notify landing while it is in
          // flight is device truth the read's private copy predates, and the re-base
          // below rebuilds the whole snapshot from that copy.
          const since = live?.directMark();
          // The session's own recent writes travel with the read. This node's addresses
          // can include ones the flush wrote tens of ms ago, and a GET inside that
          // window answers the PRE-write value — which the merge then takes as device
          // truth while the unit's own notify for our write is consumed as an echo. The
          // operator's toggle flips back on screen, and plan and snapshot agree on a
          // value the device does not hold until the idle sweep re-reads past it.
          const pending = live?.recentPending(nodeIds);
          const merged = await followRead("device-follow scoped readback", (into, signal) =>
            applyNodeStateScoped(into, nodeIds, signal, pending),
          );
          if (!merged) return;
          // A Signal Type moved on the unit's own panel reaches the plan here, with no edit
          // funnel to snap the pair's partner beside its primary.
          snapLinkedPairs();
          traceProbe?.sample("follow-scoped");
          noteMergeConflicts(merged);
          // Re-based here rather than in the coalesced reflect: only the copy this read
          // ran against says what the device holds, it is not reachable from there, and
          // the reflect's delay is a window in which an undo would diff against a
          // snapshot that still describes the pre-read plan.
          // Ahead of the re-base and the reflect: an incomplete read merged nothing, and
          // the session it belongs to ends at this throw.
          assertReadComplete(merged, "device-follow scoped readback issues:");
          live?.resync(merged.deviceView, since, nodeIds);
          followAuthor(merged.devicePatch);
          followFull = true;
          requestReflect();
          reapplyHeld(merged);
        },
        // Escalation / idle safety net: pull the whole device into the plan.
        reconcileAll: async () => {
          const since = live?.directMark();
          // Unscoped: a whole-device read covers every address, so every recent write is
          // one it must settle before reading (the scoped case above says why).
          const pending = live?.recentPending();
          // The [CUE] flags are outside the plan, so the plan read does not carry them; they are
          // read inside the same follow read, which a session's release waits for. A
          // whole-device read follows a scene recall and stands in for any notify missed.
          const cueSet = cues;
          const cueSince = cueSet.version;
          let cueGot: string[] | null = null;
          const merged = await followRead("device-follow readback", async (into, signal) => {
            const result = await applyDeviceStateScoped(into, signal, pending);
            if (!result.errors.length) cueGot = await readCues(getModel(modelId));
            return result;
          });
          if (!merged) return;
          // Same as the scoped reconcile above: a link the whole-device read brought in gets
          // its partner snapped. This is the hook a scene or preset recall on the unit takes,
          // where several Signal Types move at once.
          snapLinkedPairs();
          traceProbe?.sample("follow-full");
          noteMergeConflicts(merged);
          // Ahead of the re-base and the reflect, as in the scoped reconcile above.
          assertReadComplete(merged, "device-follow readback issues:");
          plan.unreadNodes = merged.unreadNodes;
          live?.resync(merged.deviceView, since);
          followAuthor(merged.devicePatch);
          followFull = true;
          requestReflect();
          reapplyHeld(merged);
          if (cueGot) adoptCueRead(cueGot, cueSet, cueSince);
        },
        onFollow: () => setStatus(t().status.liveFollowing),
        onError: (message) => stopLiveOnError(errorText(message)),
      });

// `persist` false applies a view without remembering it: the startup's stand-in for a
// remembered SIM view, which only an experimental launch shows.
/** The View menu's SIM Scale choices. */
function simScaleItems(): HTMLButtonElement[] {
  return [...document.querySelectorAll<HTMLButtonElement>(".sim-scale")];
}

function syncSimScaleItems(scale: SimScale): void {
  for (const item of simScaleItems())
    item.setAttribute("aria-checked", String(Number(item.dataset.simScale) === scale));
}

function setView(next: ViewName, persist = true): void {
  const isConsole = next === "console";
  const isSim = next === "sim";
  if (persist) {
    rememberView(next);
    simViewPending = false;
  }
  // The view on screen, as one attribute a rule or a query can switch on.
  document.documentElement.dataset.view = next;
  graphHost.hidden = next !== "graph";
  inspectorHost.hidden = next !== "graph";
  $("btn-view-graph").setAttribute("aria-pressed", String(next === "graph"));
  $("btn-view-console").setAttribute("aria-pressed", String(isConsole));
  $("btn-view-sim").setAttribute("aria-pressed", String(isSim));
  if (isConsole) {
    simView?.hide();
    consoleView.show();
  } else if (isSim) {
    consoleView.hide();
    simView?.show();
  } else {
    simView?.hide();
    consoleView.hide();
    // Reflect any console edits back onto the graph. If a device-follow reflect or an
    // undo landed while the graph was hidden, do the deferred full refresh instead.
    if (graphDirty) {
      graphDirty = false;
      graph.refresh();
    } else {
      graph.repaintNodes();
      graph.repaintWires();
    }
    // After the graph, not before: refresh() drops a selection whose node is now
    // shelved, and that drop rebuilds the panel itself — which clears the flag, so this
    // is a no-op there and the gesture costs one rebuild rather than two.
    if (inspectorDeferred) rebuildInspector();
    graph.shown();
  }
}

// ---- the device link is held by exactly one thing at a time ----
//
// The shell has ONE connection slot: `VdState::install` stops the worker already
// installed before putting the new one in, and commands are addressed to whatever
// is installed rather than to the connection that opened them (`sender()` takes no
// epoch). So a second `vdConnect` does not add a link beside the first — it takes
// the first one's. The original owner's next command silently rides the new
// connection, and the moment that owner disconnects, the first one's next command
// fails as not-connected. `core/control/connection-race.test.ts` models exactly
// that contract.
//
// For a destructive round-trip run (the self-test, --prepare-modified) the cost is
// not a failed action: the run perturbs the unit and then verifies it address by
// address, so it is cut off mid-perturbation with the captured original living only
// in its own dead call stack.
//
// Every frontend path that opens a connection therefore takes this latch and
// refuses while it is held — the UI lock below is the affordance, this is what makes
// it true. A holder name rather than a flag, because the entry that holds it is also
// its own way out (its Cancel; for a session, its stop) and must stay usable.
type LinkHolder = "fetch" | "write" | "compare" | "device-setup" | "follow-usb" | "live" | "run" | "update";
let deviceLinkHolder: LinkHolder | null = null;
// Ends a session and starts it again for the device scope now chosen (desktop only).
let restartLiveForScope: (() => void) | null = null;
// Settles once the last session to end has given the device link back.
let liveReleased: Promise<void> = Promise.resolve();

/** Whether the device scope holds still: while a session holds the link and is not up — starting,
 *  or ending, the one a scope change ends and starts again included — since a starting session's
 *  read and its snapshot and registration take the scope each finds. */
const deviceScopeHeld = (): boolean => deviceLinkHolder === "live" && !liveSessionUp;

// Each device entry and the holder it belongs to. While the link is held, every
// entry greys except its holder's own. The `.urxf` import opens no connection: it
// greys for every holder but a session, since it replaces the plan wholesale, which is
// what a read in flight cannot survive, while a session takes its values as an edit.
const DEVICE_ENTRIES: ReadonlyArray<[string, LinkHolder | null]> = [
  ["btn-fetch", "fetch"],
  ["btn-write", "write"],
  ["btn-compare", "compare"],
  ["btn-device-setup", "device-setup"],
  ["btn-live", "live"],
  ["btn-selftest", "run"],
  ["btn-open-settings", "live"],
];

/** Take the device link, or report that something else has it and refuse. */
function holdDeviceLink(holder: LinkHolder): boolean {
  if (deviceLinkHolder !== null) {
    // Reachable only past a disabled entry (a drop target, a keyboard path, a race
    // between the click and the lock), so it is the belt rather than the affordance —
    // but it says so, because an action that silently does nothing reads as a broken
    // one.
    setStatus(t().status.deviceLinkBusy);
    return false;
  }
  deviceLinkHolder = holder;
  syncDeviceActionUi();
  return true;
}

/** Give it back. A release that does not name the current holder is a no-op — the
 *  same rule the Rust side's epoch enforces, so a late teardown of one action cannot
 *  free the link out from under the next. */
function releaseDeviceLink(holder: LinkHolder): void {
  if (deviceLinkHolder !== holder) return;
  deviceLinkHolder = null;
  syncDeviceActionUi();
}

// Lock every action that would take the link away from whoever holds it. Driven by
// the holder rather than by whichever state last moved, so the lock cannot disagree
// with the latch that decides the refusal.
function syncDeviceActionUi(): void {
  for (const [id, owner] of DEVICE_ENTRIES) {
    const el = document.getElementById(id) as HTMLButtonElement | null;
    if (el) el.disabled = deviceLinkHolder !== null && deviceLinkHolder !== owner;
  }
  // Follow USB is the one entry a live SESSION lends its connection to — its handler
  // writes over the session's link instead of opening a second one, and measured on a
  // URX44V the session survives the re-clock. During a live START it must not: the
  // session is not up yet, so the same handler would take the connecting branch.
  followUsbBadge.disabled = deviceLinkHolder !== null && !liveSessionUp;
  // The Device setup screen is the other: while a session is up it reads and writes over the
  // session's connection, behind every unit write queued so far.
  $<HTMLButtonElement>("btn-device-setup").disabled = deviceLinkHolder !== null && !liveSessionUp;
  // A write settles the rate at its boundary (settleSampleRate), so while one holds the link the
  // picker only reports the rate. A live session takes a rate chosen here once the operator has
  // been asked what it costs (confirmLiveRate), and only while Follow USB is off: with it on the
  // unit takes the computer's rate back. Locked here, beside the other device actions, so no
  // early return can leave it stuck disabled.
  syncRateHold();
  // The model picker locks for the live SESSION only, not for every holder: a switch
  // replaces the plan wholesale, which a session cannot survive (loadPlan ends it),
  // and while live the picker is the only surface naming the unit on the wire — the
  // tally prints the tag alone. A start is not covered because the model may still
  // legitimately change there (offerModelSwitch), and the switch is already refused
  // for the read's duration by the deviceReadInFlight latch in fileFlow.
  picker.disabled = liveSessionUp;
  // The SIM view holds the Sampling Frequency screen shut while the link is held.
  simView?.relock();
}

// The rate picker holds still for the reason the LCD's Sampling Frequency gives, and the field it
// sits in shows that reason as its tooltip.
function syncRateHold(): void {
  const held = rateHoldReason(rateHoldState(), rateHoldReasons());
  const field = ratePicker.parentElement!;
  ratePicker.disabled = held !== null;
  if (held === null) field.removeAttribute("title");
  else field.title = held;
}

// Reflect the live-sync state across the toggle, the on-air tally, and the other
// device actions (which conflict with the held connection while sync is on).
// Only ever called in the desktop live-sync path, so re-enabling on `off` is safe.
function setLiveUi(on: boolean): void {
  const liveBtn = document.getElementById("btn-live");
  if (liveBtn) liveBtn.setAttribute("aria-checked", String(on));
  const tally = document.getElementById("live-tally");
  if (tally) {
    tally.hidden = !on;
    if (on) tally.textContent = t().toolbar.liveTag;
  }
  // The lock itself is the link holder's (see syncDeviceActionUi); a session is one
  // holder among several, and this call is what repaints the group when it changes.
  syncDeviceActionUi();
  // Re-render the Preferences modal if it is open (a link loss can end the session
  // behind the scrim).
  prefs.refresh();
  // The surfaces that stream meters. Ordering is load-bearing and lives here for
  // that reason: the console subscribes, then the gate screen (if open) takes the
  // one broker slot back off it. Every way in and out of a session already passes
  // through this function, so a new path cannot desync them by forgetting a call.
  consoleView.setLive(on);
  dynScreen.setLive(on);
  // A selected node's CUE row switches only while a session is up.
  refreshInspectorCue();
  simView?.setLive();
  // The sleep hold lives and dies with the session, so every way in and out of one
  // — the toggle, a write failure, a link loss — passes through here.
  void syncSleepHold(on && getSettings().preventSleep).then((failed) => {
    if (failed) showError(failed);
  });
}

// Turn live sync off and release the connection. Used by the toggle, by a write
// failure, and whenever the plan is replaced wholesale (loadPlan).
function deactivateLive(status?: string, end: LinkSessionEnd = "off"): void {
  if (!liveSessionUp) return;
  liveSessionUp = false;
  fxReseeds.clear();
  midi?.probeMark("live:off");
  // Nothing keeps the plan and the unit together from here, so the output side closes
  // until the next session's readback settles.
  midi?.liveEnded();
  follow?.end();
  live?.end();
  // The sides the session left stay; what it wrote has no session left to answer it, and a
  // switch still waiting to be written has no session to go to.
  lcdEchoes.clear();
  lcdNext = null;
  lcdLast = null;
  // A cue is the unit's state, which nothing reads once the session is gone, and so is the
  // [Sends] destination.
  resetCues();
  resetSendsTarget();
  resetUnitSessionState();
  heldDeviceWrites.clear();
  // The flush that would have reported them has ended with the session.
  flushReadNotes = [];
  liveAdopted = 0;
  liveReleased = releaseLive(liveEpoch, end);
  setLiveUi(false);
  // A CH → FX tap shown read-only while live becomes editable again off-line.
  refreshInspector();
  if (status) setStatus(status);
}

// A Live-sync start from live.begin() until the session counts as up, and the first
// failure reported inside that window. The session is not up there, so nothing is torn
// down for it yet; the start throws the recorded cause instead of declaring the session up.
let liveStarting = false;
let liveStartFailure: string | null = null;

// A live/follow runtime error: stop sync, drop the connection, and surface the
// cause as a dialog (a mirror that did not complete). Several errors can arrive in
// one teardown (live + follow); deactivateLive clears liveSessionUp synchronously,
// so the second and later calls return here before re-showing the dialog.
function stopLiveOnError(message: string): void {
  if (!liveSessionUp) {
    // Inside a start's window: recorded for the start to report, and the live half ended
    // now, so a flush waiting behind a failed read stops at its generation check rather
    // than sending the write the read was in front of. Outside one (a release still
    // running after its session) a late failure has nobody to report to.
    if (liveStarting && liveStartFailure === null) {
      liveStartFailure = message;
      live?.end();
    }
    return;
  }
  deactivateLive(undefined, "error");
  // The badge asserts something about the device on the other end of a link that
  // just went away, so it goes back to unknown rather than keeping a claim about
  // hardware that may no longer be attached.
  setFollowUsbBadge(null);
  showError(t().status.liveError(message));
}

// Whether the computer's idle sleep is currently held off, and the queue that
// keeps the OS calls in order — an off/on across two session transitions must not
// land reversed. Only the difference is sent, so a repeated target costs nothing.
let sleepHeld = false;
let sleepHoldChain: Promise<string | null> = Promise.resolve(null);

/** Drive the idle-sleep hold to `want`. Resolves with null once the OS agreed, or
 *  the refusal text — the caller decides where that lands (a status dialog for a
 *  session transition, the Preferences note for a toggle). */
function syncSleepHold(want: boolean): Promise<string | null> {
  sleepHoldChain = sleepHoldChain.then(async () => {
    if (want === sleepHeld) return null;
    const failed = await applyPreventSleep(want);
    // A refusal leaves sleepHeld alone: the OS did not move, so neither does the
    // app's idea of what it is holding.
    if (!failed) sleepHeld = want;
    return failed;
  });
  return sleepHoldChain;
}

// The one call that takes or releases the hold. Resolves with null once the OS
// took it, or the refusal text.
async function applyPreventSleep(on: boolean): Promise<string | null> {
  try {
    await setKeepAwake(on);
    return null;
  } catch (err) {
    return t().prefs.sleepFailed(errorText(err));
  }
}

// Undo / redo over the plan. Assigned below (after the views it re-renders exist),
// so every funnel reaches it optionally — the same shape as live / midi.
let planHistory: PlanHistory | null = null;
// The engine slots an effect selection seeded with its type's defaults, held until the history
// entry carrying them closes, when they are recorded as the fill's rather than the operator's.
const seededDefaults = new Map<string, SeededDefault>();

// A device read that carries a model switch (Fetch / Live-sync start) runs against a plan of
// the unit's model, and that plan replaces the one on screen once the read lands — so while
// the read runs, the plan on screen is the one the switch discards. Set for exactly that
// window, holding the plan on screen and its state when the read began. An edit from any
// surface reaches markChanged and is refused there, an undo / redo is refused by the history
// and an incoming MIDI change by the MIDI gate, each with `busySwitchRead` on the status line.
// The board asks first (planTakesEdits), since it writes a node's place as the pointer moves
// and reports the move only once the drag ends.
let switchRead: { plan: Plan; state: Plan } | null = null;

// Start refusing edits to the plan on screen for a read that carries a switch. A board
// gesture still in progress is ended first, so what it moved is reported as the edit it is
// rather than kept against the read.
function beginSwitchRead(): void {
  graph.endAllPointers();
  switchRead = { plan, state: clonePlanState(plan) };
}

// Whether the plan on screen takes an edit now: not while a switching read runs, and the
// status line then says so. Asked by a surface before it writes, where markChanged can only
// put back what an edit has already written.
function planTakesEdits(): boolean {
  if (!switchRead) return true;
  setStatus(t().status.busySwitchRead);
  return false;
}

// Refuse an edit made while a switching read runs: put the plan on screen back to the state
// the read began from and repaint it. Queued behind the funnel that called, which goes on
// writing the plan and the status line after its markChanged call. A restore that runs once
// the switch has replaced that plan does nothing: the plan on screen and the status line are
// then the switch's.
function refuseSwitchReadEdit(held: { plan: Plan; state: Plan }): void {
  queueMicrotask(() => {
    if (plan !== held.plan) return;
    const patch = diffPlans(plan, held.state);
    if (patch.length) {
      applyPatch(plan, patch);
      repaintPatched(patchTouch(patch));
    }
    setStatus(t().status.busySwitchRead);
  });
}

// An edit changed the plan: flag it unsaved and (when live) mirror it to the
// device. Every edit funnel routes through here so neither concern is forgotten.
// MIDI feedback also hangs off this funnel, so a mapped controller (motor fader /
// LED) follows edits made anywhere in the UI. The undo history opens its entry
// here too, and closes it at the next gesture boundary — which is why the diff is
// taken then and not now: several funnels mutate the plan further after calling.
function markChanged(source: WriteSource = "ui", written?: Iterable<string>): void {
  if (switchRead) {
    refuseSwitchReadEdit(switchRead);
    return;
  }
  dirty = true;
  // Attribute the write before the funnel's own side effects run: note() may commit an
  // entry, and the ledger has to say who authored the keys that entry carries.
  traceProbe?.sample(source);
  // Name the keys for any device read in flight, so a value the app moved and moved back
  // inside the read's window is not overwritten by what the device held in between —
  // and, for a funnel that carries a patch, the keys it asserted without moving them.
  planWrites.note(written);
  live?.schedule();
  midi?.scheduleFeedback();
  simView?.planChanged();
  planHistory?.note();
}

// Values entered the plan from somewhere other than an edit funnel (a device read, a
// follow notify, an external controller): no dirty flag and no live mirroring, but a
// mapped MIDI controller must still follow what the plan now holds, and the ledger must
// attribute the keys. Every such path funnels through here so the next one cannot
// forget it.
function planValuesChanged(): void {
  traceProbe?.sample("device-action");
  midi?.scheduleFeedback();
  simView?.planChanged();
}

/** Whether the model has the microSD recorder at all. URX22 does not, so nothing about
 *  Track Count — a menu entry, a ceiling, a warning — belongs on it. Spelled once so the
 *  three sites that ask cannot drift apart.
 *
 *  Takes what the UNIT reported, which is a string and may name a model this build does
 *  not know. An unknown one is answered from the plan's model rather than from a guess:
 *  reading it as "no recorder" would drop a warning about a loss that cannot be undone. */
function hasRecorder(reported: string): boolean {
  const known = MODEL_IDS.includes(reported as ModelId);
  return getModel(known ? (reported as ModelId) : modelId).nodes.some((n) => n.id === SDREC_NODE_ID);
}

// A device readback of any breadth settled the plan (fetch, Live-sync start, a .urxf
// import with no session up). The history re-takes its whole baseline: a device-authored value must not
// ride along in the next entry, or undoing an app edit would push it back over the
// operator's own move on the hardware. The follow-side writers do NOT come through here
// — each settles the history at its own site, where what the device authored is known:
// a notify's own keys (applyDirect → absorb), a refetch's patch (refetchNodes →
// absorb), a reconcile's reset (reflectFollow's full branch), the write-adoption's
// confirmed keys (adoptConfirmedWrites → absorb), and the position a STEREO pair's snap
// writes (snapLinkedPairs → absorb), which is the one of the five the device did not
// author and the only one that needs no reset behind it.
function planReadFromDevice(): void {
  planValuesChanged();
  // No full re-send here. This funnel is reached by every read that landed in the plan on
  // screen — a fetch's partial one included, and one whose values changed nothing — and
  // the history re-baselines on each; a read that did not land (cancelled, failed, or
  // incomplete where only a complete one is taken) leaves the history as it was. What none
  // of them establishes is that the plan IS the unit's state, and only that licenses
  // putting every mapped value on the wire (MidiControl.liveReadSettled, called where the
  // session is known to be up).
  planHistory?.rebase();
}

// SSMCS and COMP->EQ are exclusive on a MONO IN channel and share the DSP: on the
// device, switching COMP_EQ_TYPE loads the destination chain's factory values (the
// previous chain's edits are not carried across, so re-entering a chain always
// starts from factory). Mirror that offline so the plan never holds stale bank
// values the device would have reset. Only the destination bank is reset; the
// source bank's now-dormant values are harmless (re-entering it resets them too).
// GATE is type-independent and untouched.
//
// Under Live sync the same reset converges on the WRITE side: COMP_EQ_TYPE is
// sideEffect: "converge", so the flush runs a converge round that re-reads the write
// scope and pushes the plan back. Not follow's scoped read-back — this runs inside the
// app's own edit funnel, so the unit's answer is an echo and follow returns on it before
// arming anything. That is why this mirror is not optional: without it the plan keeps the
// outgoing bank's values and the converge pushes them onto the unit the device just
// reset, which is the reverse of what the first paragraph describes.
function resetCompEqBank(id: string, newType: number): void {
  const np = plan.nodeParams[id];
  if (!np) return;
  const factory = defaultPlan(modelId).nodeParams[id] ?? {};
  if (newType === COMP_EQ_SSMCS) {
    // Entering SSMCS: load the factory morphing strip ("01 Basic", fully engaged).
    np.ssmcs = structuredClone(factory.ssmcs ?? SSMCS_INITIAL);
    np.compOn = true;
    np.eqOn = true;
    // SSMCS has no discrete EQ stage, so the device drops PRE EQ from the Rec
    // Point list and moves a selected PRE EQ tap to PRE COMP; mirror that.
    if (np.recPoint === REC_POINT_PRE_EQ) np.recPoint = REC_POINT_PRE_COMP;
  } else {
    // Entering COMP->EQ: restore the factory comp / 4-band EQ / EQ 1-knob bank and
    // its section ONs. A field absent at the factory is cleared, not left stale.
    assignOrDelete(np, "comp", factory.comp);
    assignOrDelete(np, "eqBands", factory.eqBands);
    assignOrDelete(np, "eqOneKnob", factory.eqOneKnob);
    np.compOn = processorOn(factory, "compOn");
    np.eqOn = processorOn(factory, "eqOn");
  }
}

// Set a node param from the factory value, deleting it when the factory has none,
// so a reset never leaves a stale field behind (clone to keep the factory pristine).
function assignOrDelete<K extends keyof NodeParams>(np: NodeParams, key: K, value: NodeParams[K]): void {
  if (value === undefined) delete np[key];
  else np[key] = structuredClone(value);
}

const inspectorActions = {
  onDeleteConnection: (from: string, to: string) => graph.deleteConnection(from, to),
  // Mutate params in place without re-rendering, so the slider keeps focus while dragging.
  onUpdateParams: (from: string, to: string, patch: ConnParams) => {
    const conn = plan.connections.find((c) => c.from === from && c.to === to);
    if (!conn) return;
    // The destination bus's locks are what decide these controls exist at all
    // (mixSendLocks: the inspector drops the gated one, the console renders it
    // read-only, core/midi/controls.ts swallows the message). A device-side flip lands
    // in the plan a reflect before the panel is rebuilt, so a control the lock already
    // removed can still be on screen and live. Refuse its write rather than let a
    // phantom control author a value the next flush puts on the wire — name the lock
    // that refused it, and rebuild the panel so the control goes away now.
    const { busFixed, panLinked } = mixSendLocks(plan, parseRef(to).nodeId);
    if (busFixed && (patch.level !== undefined || patch.tap !== undefined || patch.pan !== undefined)) {
      setStatus(t().inspector.busFixedSend);
      refreshInspector();
      return;
    }
    const wasOff = (conn.params?.level ?? 0) <= LEVEL_MIN_DB;
    conn.params = { ...conn.params, ...patch };
    // Under Pan Link a send pan is its source's own PAN / BAL, and turning it moves the source,
    // as the unit's SEND TO screen does.
    const sourceMoved =
      panLinked && patch.pan !== undefined ? panLinkedSendPan(plan, parseRef(from).nodeId, patch.pan) : [];
    // A STEREO-linked pair moves as one: copy the same send change to the partner
    // channel. The pan goes with it in BAL, where it is the pair's one shared balance,
    // and stays the member's own in PAN (see mirrorLinkedPair).
    const source = parseRef(from).nodeId;
    const mirrored = mirrorLinkedPair(getModel(modelId), plan, source);
    // A linked MIX's send pans from this source (and a mirrored partner) follow its position.
    markChanged("ui", [
      ...sourceMoved,
      ...alignLinkedSendPans(plan, withLinkedPartner(getModel(modelId), plan, source)),
    ]);
    // A PRE/POST change flips the wire's pre-fader marker; a send ON/OFF, an OSC L/R
    // assign change, or a level that crosses -∞ flips the wire's (and its jacks') off-state
    // dimming. Those repaint the wire layer, which leaves the panel and the slider holding
    // the pointer alone; a level move that stays on one side of -∞ and a pan move change
    // nothing drawn there.
    const crossedOff = patch.level !== undefined && wasOff !== patch.level <= LEVEL_MIN_DB;
    if (
      patch.tap !== undefined ||
      patch.on !== undefined ||
      patch.oscL !== undefined ||
      patch.oscR !== undefined ||
      crossedOff
    )
      graph.repaintWires();
    // Refresh the console so a mirrored partner keeps up (a no-op while hidden).
    if (mirrored) consoleView.refresh();
    // OSC assign L/R are toggle buttons (not focus-holding sliders); re-render so
    // the pressed state updates at once. A PRE/POST change likewise re-renders so the
    // ducked-channel PRE-send note appears/clears with the tap.
    if (patch.oscL !== undefined || patch.oscR !== undefined || patch.tap !== undefined) refreshInspector();
  },
  // `alsoWritten` is the contest keys a caller wrote outside this node's params before
  // handing the patch over, so this edit names them with its own.
  onUpdateNodeParams: (
    id: string,
    patch: NodeParams,
    written?: readonly string[],
    defaults?: readonly string[],
    alsoWritten: readonly string[] = [],
  ) => {
    const prev = plan.nodeParams[id];
    const partner = partnerChannel(getModel(modelId), id);
    plan.nodeParams[id] = { ...prev, ...patch };
    // Signal Type / PAN-BAL move the pair's pans — and PAN-BAL itself on a link —
    // the way the unit does. Applied before the pair mirror below, so the mirror
    // copies the settled values onto the partner. It names its own writes: every one
    // of them can land on the value already there, so nothing downstream can recover
    // them from the plan's diff.
    // Pan Link turned on sets each send pan into the MIX to its source's pan, and turned off
    // leaves it there, as the unit does (sendPansToSources) — the next write then puts back
    // what the unit already holds.
    const transitionKeys =
      patch.stereoLink !== undefined || patch.panBal !== undefined
        ? applyPairTransition(getModel(modelId), plan, id, patch)
        : patch.panLink !== undefined && patch.panLink !== (prev?.panLink === true)
          ? sendPansToSources(plan, id)
          : [];
    const fx = nodeParamEffects(patch, prev);
    // A COMP/EQ Type change loads the destination bank, ahead of the pair mirror for the
    // reason the transition above is: the partner takes the settled bank.
    if (fx.resetCompEqBank) resetCompEqBank(id, patch.compEqType as number);
    // A STEREO-linked pair moves as one: copy this channel's params to the partner
    // (the pair-level Signal Type / PAN-BAL fields stay on the primary).
    const mirrored = mirrorLinkedPair(getModel(modelId), plan, id);
    // The insert FX takes a pass of its own beside it, which is what names the pair's
    // three insert-FX keys whatever this edit was. Both write the same values.
    const insFxMirrored = mirrorLinkedInsertFx(getModel(modelId), plan, id);
    // The insert-FX mirror copies the engine values, defaults included.
    const seeded = (defaults ?? []).map((path) => [id, path] as const);
    if (partner && insFxMirrored) seeded.push(...(defaults ?? []).map((path) => [partner, path] as const));
    noteSeededDefaults(plan, seededDefaults, seeded);
    // The patch's own keys, not only the ones whose value moved: this funnel asserts
    // every member it carries, and a device read in flight must not take back one that
    // happened to already hold the asserted value.
    //
    // A NESTED GROUP is the exception, and it is the default rather than a caller's
    // responsibility: every funnel edits one by REBUILDING it — one field set, the rest
    // copied — and the merge drops a named group WHOLE, so naming it hands the device's
    // answer for every untouched sibling away with it (measured: an OSC frequency the
    // unit moved during an OSC ON toggle was thrown out with the toggle's own key). A
    // group therefore falls through to the plan's own diff, which names the fields that
    // moved. A caller with a field that can be written WITHOUT moving names it itself, as
    // a dotted path — that is what `written` carries, and it replaces this derivation.
    const names = written ?? Object.keys(patch).filter((key) => typeof patch[key as keyof NodeParams] !== "object");
    const keys = [...names.map((name) => nodeParamContestPath(id, name)), ...transitionKeys, ...alsoWritten];
    // A mirror asserts the PARTNER's keys the same way, and it can assert one that already
    // holds the value it writes — the insert-FX mirror re-writes a bypass that was already
    // on. Each names only what IT wrote, and for the pair mirror that is THIS EDIT's keys
    // MINUS the ones the pair does not share, not the whole record it copies: the other keys
    // it carries over were already equal on both sides, so copying them writes nothing, while
    // claiming them takes the device's answer away from the partner alone. Measured before
    // the narrowing: a read that moved both members' HPF, with an unrelated Phase edit inside
    // it, left CH 1 on the device's ON and CH 2 on the plan's OFF — one gesture splitting a
    // pair that moves as one. A key the mirror LEAVES with the partner is the same defect
    // pointed the other way: the head amp is the member's own, so claiming it takes the
    // device's answer for a value this edit never sent.
    const mirroredKeys = new Set<string>();
    if (mirrored) for (const name of names) if (pairSharesNodeKey(name)) mirroredKeys.add(name);
    if (insFxMirrored) for (const key of INSERT_FX_PAIR_KEYS) mirroredKeys.add(key);
    if (partner) for (const name of mirroredKeys) keys.push(nodeParamContestPath(partner, name));
    markChanged("ui", keys);
    // The pair snap below writes the plan AFTER markChanged took the ledger sample, so its
    // keys would land in whatever samples next — under live follow a device notify, which
    // invariant 13 then reads as the device authoring a key the operator moved. Re-sampled
    // as this edit once it has run.
    let lateWrite = false;
    // Linking a pair snaps its partner next to the kept node so the tie isn't drawn
    // across a gap an earlier manual move may have opened.
    if (fx.alignStereoPair) {
      graph.alignStereoPair(id);
      lateWrite = true;
    }
    if (fx.repaintNodes) graph.repaintNodes();
    if (fx.repaintWires) graph.repaintWires();
    if (fx.rerender) graph.render();
    if (mirrored || insFxMirrored) consoleView.refresh();
    if (lateWrite) traceProbe?.sample("ui");
    if (fx.refreshInspector) refreshInspector();
  },
  // The unit refills the slots the incoming type uses with that type's factory values; the plan
  // takes them, and the park in front of the type write leaves them standing (fxReseeds).
  onSetFxEffectType: (id: string, type: number) => {
    const fx = plan.nodeParams[id]?.fxEffect ?? {};
    // The type the channel already holds is not a change: the unit refills nothing for it, so the
    // plan keeps the values it was tuned to.
    if (type === resolveFxEffectType(FX_CHANNEL_NODE_INDEX[id], fx.type)) return;
    const { params, keys } = fxTypeDefaults(fx.params, type);
    const seeded = keys.map((key) => `fxEffect.params.${key}`);
    fxReseeds.set(id, type);
    inspectorActions.onUpdateNodeParams(
      id,
      { fxEffect: { ...fx, type, params } },
      ["fxEffect.type", ...seeded],
      seeded,
    );
  },
  // The unit resets the bus's whole send bank on a BUS Type write, and FIXED turns its Pan
  // Link off (VARI leaves it off); the plan takes both in the same edit.
  onSetBusType: (id: string, busType: number) => {
    const prev = plan.nodeParams[id]?.busType ?? BUS_TYPE_VARI;
    const reset = busType !== prev ? resetMixSendBank(plan, id, busType) : [];
    const patch: NodeParams = busType === BUS_TYPE_FIXED ? { busType, panLink: false } : { busType };
    inspectorActions.onUpdateNodeParams(id, patch, undefined, undefined, reset);
  },
  // Rename mutates in place and repaints the node label without re-rendering the
  // inspector, so the text input keeps focus while typing. Empty clears the override.
  onRenameNode: (id: string, name: string) => {
    // Normalized here as well as in the field itself: this is reachable from callers
    // other than that box, and a name goes to the unit. The field's own clip is length
    // only — it runs per keystroke, so it cannot drop trailing padding without eating
    // the space in `A B` as it is typed. This is where a committed one goes.
    const stored = normalizeNodeName(name);
    if (stored) plan.nodeNames[id] = stored;
    else delete plan.nodeNames[id];
    markChanged();
    graph.repaintNodes();
  },
  // Recolor repaints the node cap and re-renders the inspector so the active
  // swatch ring updates. null clears the override.
  onRecolorNode: (id: string, color: string | null) => {
    if (color) plan.nodeColors[id] = color;
    else delete plan.nodeColors[id];
    markChanged();
    graph.repaintNodes();
    refreshInspector();
  },
  onOpenRecent: (path: string) => void openRecent(path),
  onHideNode: (id: string) => graph.hideNode(id),
  // Declared here but bound below: the dynamics screen's hooks reach back into
  // these actions, so it cannot be constructed until they exist.
  onOpenDynScreen: (kind: DynKind, id: string) => dynScreen.open(DYN_PROCESSORS[kind], id),
  onSwitchSide: (channelId: string, side: JackSide) => switchSide(channelId, side, "app"),
  onClose: () => graph.clearSelection(),
  connectOrigins: (id: string) => graph.connectOrigins(id),
  onConnect: (origin: ConnectOrigin, other: string) => graph.connectTo(origin, other),
  wireDrawn: (from: string, to: string) => graph.wireDrawn(from, to),
  // A node's [CUE] row: switchable while a session is up, as the CONSOLE chip is.
  cueOf: (id: string) => (cueAddrOf(getModel(modelId), id) ? { on: cues.has(id), enabled: liveSessionUp } : null),
  clipSafeEngaged: (jack: number) => liveSessionUp && clipSafeEngaged.has(jack),
  onSetCue: (id: string, on: boolean) => setCue(id, on, "app"),
  // The routing row that was pressed goes with the node's panel, so the wire's panel takes
  // the focus: its first control, the mobile close button aside.
  onSelectConnection: (from: string, to: string) => {
    graph.selectConnection(from, to);
    if (selection?.type !== "conn") return;
    focusables(inspectorHost)
      .find((el) => !el.classList.contains("inspector-close"))
      ?.focus({ preventScroll: true });
  },
};
graph.setTheme(theme);
graph.setLabelSource(labelSource);
if (detectHideOffSends()) graph.setHideOffSends(true);

const labelsBtn = $("btn-labels");
const hideOffBtn = $("btn-hide-off");

function applyStaticI18n(): void {
  const m = t();
  $("lbl-model").textContent = m.toolbar.model;
  $("lbl-rate").textContent = m.toolbar.rate;
  syncRateHold();
  // The badge's hint differs by state, so it cannot be relabelled from the
  // language alone — re-paint it from the state the session already holds.
  renderFollowUsbBadge();
  renderCueBar();
  $("btn-new").textContent = m.toolbar.new;
  $("lbl-file").textContent = m.toolbar.file;
  $("btn-open").textContent = m.toolbar.open;
  $("btn-open-settings").textContent = m.toolbar.openSettings;
  $("btn-save").textContent = m.toolbar.save;
  $("btn-export").textContent = m.toolbar.exportPng;
  $("btn-export-pdf").textContent = m.toolbar.exportPdf;
  $("btn-licenses").textContent = m.licenses.title;
  const viewGraphBtn = $("btn-view-graph");
  viewGraphBtn.textContent = m.toolbar.viewGraph;
  viewGraphBtn.title = m.toolbar.viewGraphHint;
  const viewConsoleBtn = $("btn-view-console");
  viewConsoleBtn.textContent = m.toolbar.viewConsole;
  viewConsoleBtn.title = m.toolbar.viewConsoleHint;
  const viewSimBtn = $("btn-view-sim");
  viewSimBtn.textContent = m.toolbar.viewSim;
  viewSimBtn.title = m.toolbar.viewSimHint;
  for (const item of simScaleItems()) item.textContent = m.toolbar.simScale(Number(item.dataset.simScale));
  $("btn-auto").textContent = m.toolbar.arrange;
  $("btn-hide-unused").textContent = m.toolbar.hideUnused;
  $("lbl-device").textContent = m.toolbar.device;
  $("btn-fetch").textContent = fetchAbort ? m.toolbar.fetchCancel : m.toolbar.fetchDevice;
  $("btn-write").textContent = writeAbort ? m.toolbar.writeCancel : m.toolbar.writeDevice;
  $("btn-midi").textContent = m.midi.menuItem;
  $("btn-device-setup").textContent = m.deviceSetup.menuItem;
  $("btn-compare").textContent = compareAbort ? m.toolbar.compareCancel : m.toolbar.compare;
  $("btn-selftest").textContent = selfTestAbort ? m.toolbar.selfTestCancel : m.toolbar.selfTest;
  // Live-sync toggle keeps a static label; aria-checked and the on-air tally
  // carry the on/off state. The tally is relabelled too — the tag is its whole
  // text, and a language may translate it.
  const liveBtn = document.getElementById("btn-live");
  if (liveBtn) {
    liveBtn.textContent = m.toolbar.liveSync;
    liveBtn.title = m.toolbar.liveSyncHint;
  }
  const liveTally = document.getElementById("live-tally");
  if (liveTally && live?.isActive()) liveTally.textContent = m.toolbar.liveTag;
  // View menu trigger.
  $("lbl-view").textContent = m.toolbar.view;
  $("btn-view").title = m.toolbar.viewHint;
  // Preferences gear (icon button; the glyph is an inline SVG).
  const prefsBtn = $("btn-prefs");
  prefsBtn.title = m.prefs.title;
  prefsBtn.setAttribute("aria-label", m.prefs.title);
  // The two View toggles keep one label each, as Live sync does: checked is the state
  // the label names, carried by aria-checked and the pressed look.
  labelsBtn.textContent = m.toolbar.labelsDevice;
  setTip(labelsBtn, m.toolbar.labelsHint);
  labelsBtn.setAttribute("aria-checked", String(labelSource === "device"));
  hideOffBtn.textContent = m.toolbar.hideOffSends;
  hideOffBtn.title = m.toolbar.hideOffSendsHint;
  hideOffBtn.setAttribute("aria-checked", String(graph.isHideOffSends()));
  // Demo-only desktop-app link (present in the DOM, shown only in the demo build).
  const desktopLbl = document.getElementById("lbl-desktop");
  const desktopLink = document.getElementById("btn-desktop");
  if (desktopLbl) desktopLbl.textContent = m.toolbar.desktopApp;
  if (desktopLink) setTip(desktopLink, m.toolbar.desktopAppHint);
  // Demo-only share / download buttons (same reveal mechanism as the link above).
  const shareBtn = $("btn-share");
  shareBtn.textContent = m.toolbar.shareUrl;
  shareBtn.title = m.toolbar.shareUrlHint;
  const downloadBtn = $("btn-download");
  downloadBtn.textContent = m.toolbar.downloadJson;
  downloadBtn.title = m.toolbar.downloadJsonHint;
}
applyStaticI18n();

// Both build-time hides below set the `hidden` attribute rather than an inline
// display, because that attribute is what the rest of the app reads to mean
// "not here": the menus' roving focus list filters on `[hidden]`, so an entry
// hidden any other way stays in the list, and End — or either arrow's wrap —
// hands focus to a control that is not on screen, which a real engine refuses
// to take: focus stays where it was. The browser build's File menu ends in the
// desktop-only Licenses entry, so that was all three of its gestures.
// `[hidden] { display: none !important }` in style.css is what makes the
// attribute win over an author `display`.

// The GitHub Pages demo is a viewer only: hide file persistence and image export.
if (DEMO) {
  for (const el of document.querySelectorAll<HTMLElement>("[data-demo-hide]")) {
    el.hidden = true;
  }
  // The demo is a viewer; surface a link to the desktop app (full file IO,
  // image export, and live device control) so visitors can find it.
  for (const el of document.querySelectorAll<HTMLElement>("[data-demo-only]")) {
    el.hidden = false;
  }
}

// Live hardware control and the bundled license notice need the Tauri shell
// (Rust commands / resources); hide their controls in a plain browser and the
// demo, where they could only fail.
if (!isTauri()) {
  for (const el of document.querySelectorAll<HTMLElement>("[data-control-hide]")) {
    el.hidden = true;
  }
}

// The line prints the message; the live region beside it announces the message's spoken form
// (ui/glyph.ts, spoken). `captioned`: the message names controls by their captions, whose
// symbols are read as words.
function setStatus(msg: string, captioned = false): void {
  statusbar.textContent = msg;
  setSaid(statusbarSaid, spoken(msg, captioned));
}

// Surface an operation that did not complete as a modal, so it is not missed the
// way a transient status line can be. Clears the status line first so a stale
// progress message (e.g. "Connecting…") does not linger behind the dialog.
function showError(message: string, keep = ""): void {
  // Cleared by default so a stale "Connecting…" does not sit behind the dialog. `keep` is for
  // a caller raising this AFTER its action wrote an outcome: that line is not stale, it says
  // what the dialog does not, and the two are about different things.
  setStatus(keep);
  // Not `void`: a dialog that cannot be raised would otherwise leave the failure it carries on
  // no surface at all. It goes to a box of its own rather than to the status line, which is
  // one transient slot — the line is holding what the run achieved, and whatever the app
  // reports next would take the failure with it.
  errorDialog(message).catch((err: unknown) => {
    console.error("error dialog failed:", message, err);
    showErrorBox(message);
  });
}

// A one-shot click handler, with the failure surface every one of them reports
// through. singleFlight fires its promise without awaiting it, so a rejection that
// names no sink reaches nothing — the handlers that have something specific to say
// still catch inside their own action, and this is the backstop under them.
const guarded = (run: () => Promise<void>): (() => void) =>
  singleFlight(run, (err: unknown) => showError(errorText(err)));

/** A focused inspector control, keyed by what NAMES it on screen rather than by
 *  position. The panel's sliders are bare `input[type=range]` with no class, so an
 *  index key would hand focus to whatever control moved into the slot when a lock
 *  removed the focused one — the case this restore exists for. The parameter row's
 *  label is the discriminator (Pan vs Level), taken from the `data-param-label`
 *  paramBlock stamps while building the row rather than searched for again here: this
 *  runs once per candidate control on a path that repeats at ~20 Hz during device
 *  follow. A control whose text changes with the plan — a connect picker's options —
 *  stamps a `data-focus-key` that stands in for that text. No match on the rebuilt panel =
 *  focus is dropped, which is the wanted outcome. */
function inspectorFocusKey(el: HTMLElement): string {
  const label = el.closest<HTMLElement>(".param")?.dataset.paramLabel ?? "";
  const type = el instanceof HTMLInputElement ? el.type : "";
  return [label, el.tagName, type, el.className, el.dataset.focusKey ?? el.textContent?.slice(0, 24) ?? ""].join("|");
}

/**
 * Those keys made unique, by appending each control's ordinal among the ones sharing
 * its key.
 *
 * The label is not a discriminator on its own, because rows legitimately repeat one:
 * every section's ON/OFF row carries the EMPTY label, since its name is in the section
 * heading above it, so a channel's GATE / COMP / EQ toggles are one key between them.
 * `find` returns the FIRST match, so the restore did not drop focus — which is the
 * documented fallback and would have been fine — it handed focus to a different
 * section's control, and the operator's next keypress operated that one and wrote it to
 * the device.
 *
 * Built for the whole host in ONE pass, and used from both ends of the rebuild: keying
 * each element by scanning its siblings would be quadratic on a path that repeats at
 * ~20 Hz during device follow, which is what the key's own note is about.
 */
function inspectorFocusKeys(): Map<HTMLElement, string> {
  const els = focusables(inspectorHost);
  const seen = new Map<string, number>();
  const counts = new Map<string, number>();
  for (const el of els) {
    const k = inspectorFocusKey(el);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const out = new Map<HTMLElement, string>();
  for (const el of els) {
    const k = inspectorFocusKey(el);
    if ((counts.get(k) ?? 0) < 2) {
      out.set(el, k);
      continue;
    }
    const n = seen.get(k) ?? 0;
    seen.set(k, n + 1);
    out.set(el, `${k}|#${n}`);
  }
  return out;
}

// The scroll offset a rebuild has to carry over. Tracked from the element's own scroll
// event instead of read back at rebuild time: the reflect that rebuilds this panel has
// just dirtied layout (repaintDirtyNodes / refreshStrip), so a read there flushes it
// synchronously, and that path repeats at ~20 Hz while device follow is running — a
// forced layout costs several times more in WebKit than in Chromium. Passive, so it
// never holds up the scroll it is observing.
let inspectorScrollTop = 0;
inspectorHost.addEventListener(
  "scroll",
  () => {
    inspectorScrollTop = inspectorHost.scrollTop;
  },
  { passive: true },
);

// A rebuild made while an IME composition is in flight (a node name being typed in
// kana) would commit its interim characters as literal text and restart it — once per
// device-driven reflect, ~20 Hz through a knob sweep. Held until the composition ends,
// then run once.
// The deferred run takes the UNGATED rebuild: the gate has already cleared `composing` by
// the time it fires, so routing it back through refreshInspector would only re-ask a
// question it just answered.
const inspectorComposition = compositionGate(inspectorHost, () => rebuildInspector());

function refreshInspector(): void {
  if (inspectorComposition.held()) return;
  rebuildInspector();
}

// The LCD selections this app has written that the unit has not announced yet.
const lcdEchoes = new LcdEchoes();
// One LCD selection write at a time: a switch made while one is on the wire waits, and only the
// latest of those goes out once it returns — and not at all when it is the value just written.
// The write on the wire is what a session's release waits for before it disconnects, so its
// answer lands before another session can start.
let lcdWriting = false;
let lcdNext: number | null = null;
let lcdLast: number | null = null;
let lcdInFlight: Promise<void> | null = null;

function writeLcd(raw: number, guess: string | undefined): void {
  if (lcdWriting) {
    lcdNext = raw;
    return;
  }
  lcdWriting = true;
  lcdLast = raw;
  lcdEchoes.note(raw);
  const write = writeLcdSelection(raw);
  lcdInFlight = write;
  write
    .catch((err: unknown) => stopLiveOnError(errorText(err) + (guess ? ` — ${t().status.unverifiedGuess(guess)}` : "")))
    .finally(() => {
      if (lcdInFlight === write) lcdInFlight = null;
      lcdWriting = false;
      const next = lcdNext;
      lcdNext = null;
      if (next !== null && next !== lcdLast && liveSessionUp) writeLcd(next, guess);
    });
}

/** Show `side` of a stereo channel: the one funnel both views' switches and the unit's own
 *  selection reach. UI state rather than a plan edit — no dirty flag, no undo entry — so each
 *  surface that shows it is told here: both views, and the MIDI feedback, since a bound
 *  control on that channel now reads the other side. A switch made in the app during a live
 *  session also moves the unit's LCD selection to that channel and side; a write the unit
 *  refuses ends the session and keeps the side. */
function switchSide(channelId: string, side: JackSide, origin: "app" | "device"): void {
  if (!setDisplayedSide(channelId, side)) return;
  consoleView.refresh();
  if (inspectorNodes(getModel(modelId), plan, selection).includes(channelId)) rebuildInspectorNow();
  midi?.scheduleFeedback();
  if (origin !== "app" || !liveSessionUp) return;
  const model = getModel(modelId);
  const raw = encodeStereoSide(model, channelId, side);
  if (raw === null) return;
  const [id, , y] = LCD_SELECTION_ADDR;
  writeLcd(raw, unverifiedGuessAt(model, id, y));
}

/**
 * The same rebuild, past whatever the gate was holding, for a change the OPERATOR made.
 *
 * The gate exists for the refresh nobody asked for: a device follow or a MIDI sweep
 * arriving at up to 20 Hz while a name is being typed or a dropdown is open. A selection
 * change and an undo are the other kind. Held, the panel goes on showing the node the
 * operator has left, or the value the undo has just replaced — and the control it is held
 * FOR is the one displaying that stale value, because choosing in a select leaves focus
 * on it. The gate is reset rather than merely bypassed: the rebuild removes the composing
 * field, an end event for a field that is gone may never arrive, and a latched flag stops
 * the panel updating for the rest of the session.
 */
function rebuildInspectorNow(): void {
  inspectorComposition.reset();
  rebuildInspector();
}

// The rebuild itself, with no gate in front of it. Split out for the gate's own deferred
// run above, and for the dev keyboard harness, which drives the gated and ungated arms of
// the IME measurement side by side (ui/keyprobe.ts).
function rebuildInspector(): void {
  // The console view hides this panel for as long as it is up, and a device follow
  // rebuilds it at up to 20 Hz regardless. Deferred rather than dropped: setView does
  // the one rebuild on the way back, the same shape graphDirty gives the graph.
  if (inspectorHost.hidden) {
    inspectorDeferred = true;
    return;
  }
  // Cleared here rather than only at the drain: the panel becomes visible before the
  // drain runs, so anything that rebuilds it in between — a selection dropped by the
  // graph's own refresh — has already paid what the flag was owed.
  inspectorDeferred = false;
  // On mobile the inspector is a bottom sheet that slides up only while something
  // is selected; this flag drives that state (no effect on the desktop panel).
  document.body.classList.toggle("has-selection", selection !== null);
  // A device-follow reflect rebuilds this panel while the operator may be inside it,
  // and replaceChildren drops both the scroll offset and keyboard focus. Carry them
  // over (preserveFocus, the shared capture/restore the console rebuild uses too); a
  // text surface also carries its caret, which the rebuilt field would otherwise move to
  // the end, and its text: the plan holds the name normalized, without the trailing space
  // the operator may be in the middle of typing. The text is carried only while the plan
  // still holds what it normalizes to, so a name that changed underneath is shown as it
  // now is. Held on an object rather than in a plain local so the values the capture
  // writes survive to the restore.
  const carried: { caret: readonly [number | null, number | null] | null; text: string | null } = {
    caret: null,
    text: null,
  };
  const restoreFocus = preserveFocus(
    inspectorHost,
    (active) => {
      if (ownsNativeUndo(active) && active instanceof HTMLInputElement) {
        carried.caret = [active.selectionStart, active.selectionEnd];
        carried.text = active.value;
      }
      return inspectorFocusKeys().get(active);
    },
    (key) => {
      const keys = inspectorFocusKeys();
      return focusables(inspectorHost).find((el) => keys.get(el) === key);
    },
    () => inspectorScrollTop,
  );
  renderInspector(
    inspectorHost,
    getModel(modelId),
    plan,
    selection,
    actionsFor(plan),
    recent,
    live?.isActive() ?? false,
  );
  const focused = restoreFocus();
  if (focused instanceof HTMLInputElement) {
    if (carried.text !== null && normalizeNodeName(carried.text) === normalizeNodeName(focused.value))
      focused.value = carried.text;
    if (carried.caret) focused.setSelectionRange(carried.caret[0], carried.caret[1]);
  }
}

// The panel's actions, answering only while the plan the panel was built for is the one on
// screen: what a control of a panel built for a replaced plan still delivers — a choice from a
// picker left open, the commit of a composition — reaches nothing.
function actionsFor(built: Plan): typeof inspectorActions {
  const guarded = { ...inspectorActions };
  for (const key of Object.keys(guarded) as (keyof typeof inspectorActions)[]) {
    const act = inspectorActions[key] as (...args: unknown[]) => unknown;
    (guarded as Record<string, unknown>)[key] = (...args: unknown[]) => (plan === built ? act(...args) : undefined);
  }
  return guarded;
}

// Recompute the sample-rate constraints and apply them to the graph badges, the
// inspector warnings and the console (the stereo EQ chip locks at 176.4 / 192 kHz).
// Each of the three decides for itself whether the new state needs drawing now: the
// board is deferred while the console view is up, the panel while the graph view is.
function applyRateConstraints(): void {
  const c = rateConstraints(getModel(modelId), plan.sampleRate);
  // Asked before the store, so the answer is about the board on screen. A set — or a set
  // of gated record slots — that moved while the console view is up owes the graph a
  // repaint it must not do here: graphDirty is the same deferral the follow reflect uses
  // two lines from its own.
  if (graphHost.hidden && !graph.hasDisabledNodes(c.disabledNodes)) graphDirty = true;
  graph.setDisabledNodes(c.disabledNodes);
  refreshInspector();
  consoleView.refresh();
}

// After a device readback, mirror the device's sample rate into the picker and
// re-apply the rate-dependent constraints (which also refreshes the inspector).
function syncRateUi(): void {
  ratePicker.value = String(plan.sampleRate);
  // A rate that arrived from the device counts as the last known rate, exactly like
  // one picked by hand. The same person operates both the app and the hardware, so
  // a rate set on the device's own front panel is no less their choice — and a new
  // plan started afterwards should open at the rate their rig is actually running.
  rememberRate(plan.sampleRate);
  applyRateConstraints();
}

// Re-render the plan in place, without loadPlan's ownership side effects (it
// clears dirty, leaves live sync, and re-seeds the persisted model/rate/hidden).
// Used wherever the plan's contents changed under the same document: a Fetch, a live
// session's readback, and a `.urxf` settings import — the three callers below.
function rerenderPlan(history: "reset" | "keep" = "reset"): void {
  graph.setModel(getModel(modelId), plan);
  selection = null;
  syncRateUi(); // also re-renders the CONSOLE strips (applyRateConstraints)
  // An open tuning screen draws what it was built from, and every value under it was just
  // re-authored. Refresh redraws it, and closes it when the channel no longer carries the
  // processor it edits.
  dynScreen.refresh();
  // Every value here was re-authored — by the device, or by a settings file imported with no
  // session up — so the entries recorded against the old contents describe states this plan
  // cannot return to. A settings file imported during a session is an edit, and keeps them.
  if (history === "reset") planHistory?.reset();
}

// Reflect an undo / redo whose patch is already applied to the plan. Modelled on
// reflectFollow's fine-grained path rather than rerenderPlan, whose setModel refits
// the viewport — an undo must not reframe the canvas. The plan object is never
// replaced, so nothing needs re-pointing; what needs doing is re-deriving the view
// state held outside the plan and repainting.
function reflectHistory(touch: PatchTouch): void {
  // The patch is already applied by the time the reflect runs, so the ledger is taken
  // here — an undo is a writer like any other, and attributing its keys to "ui" would
  // make a restored value indistinguishable from a fresh edit.
  traceProbe?.sample("undo");
  repaintPatched(touch);
  // A value the unit drives that was turned by hand this session goes back to the unit when an
  // undo or a redo moves it: the plan's writer leaves it out. Only one written by hand, and only
  // when it moved, so an entry on another row of the channel does not overwrite what the unit
  // derived.
  if (liveSessionUp)
    for (const node of touch.nodes)
      for (const w of fxHeldWrites(plan, node)) {
        const key = `${w.param}/${w.y}`;
        if (heldDeviceWrites.has(key) && heldDeviceWrites.get(key) !== w.value)
          writeHeldDeviceValue(w.param, w.y, w.value);
      }
  // Last, so the live diff measures the settled plan. This is also what carries the
  // change to the device: live.ts diffs its snapshot, so only the undone keys go
  // out. resync() must NOT be called — it would re-base the snapshot to the plan
  // and suppress the very write the undo needs.
  markChanged();
}

// Repaint the views for a patch already applied to the plan in place: an undo / redo, or
// an edit refused while a switching read runs (refuseSwitchReadEdit).
function repaintPatched(touch: PatchTouch): void {
  // Before the repaints: commitHidden writes the graph's own set back to the plan,
  // so the persisted mirror has to move first or a later commit would undo the undo.
  if (touch.fields.has("hidden")) rememberHidden(modelId, plan.hidden);
  if (graphHost.hidden) graphDirty = true;
  else graph.refresh();
  dynScreen.refresh();
  // syncRateUi already repaints both through applyRateConstraints, so the two are
  // exclusive — stacking them cost a second full strip rebuild (~9 ms on WebKit).
  // Past the gate: an undo re-authors the plan, and what it replaced is exactly what the
  // control holding the gate is displaying — a select keeps focus after a choice is made
  // in it, so the panel would go on showing the value that was just undone.
  inspectorComposition.reset();
  if (touch.fields.has("sampleRate")) {
    syncRateUi();
  } else {
    rebuildInspector();
    consoleView.refresh();
  }
  simView?.planChanged();
}

// Read the whole device into the plan, honoring the Preferences device scope:
// the read itself stays full (reads are side-effect free on the device), but
// under the "scene" scope the plan keeps its scene-external values. Shared by
// fetch, the Live-sync starting read, and device-follow's full reconcile. On a
// throw (abort / link loss) nothing is restored — the callers discard or keep
// the plan wholesale. The plan is a parameter rather than the module one because the
// read spans seconds: re-reading it after the await would apply the outgoing document's
// scene-external values to whatever replaced it. The model is the target's own, since a
// read that carries a model switch runs against a plan of the device's model while the
// one on screen is still the other.
async function applyDeviceStateScoped(
  target: Plan,
  signal?: AbortSignal,
  pending?: PendingWrites,
): Promise<ReadbackResult> {
  // Counted at the operation, not at its callers: the broker only ever sees the ~800
  // commands this decomposes into, so `reads` is the one ledger row nothing downstream
  // can reconstruct — and a count derived at each call site is only right for as long
  // as every future caller remembers a second, unrelated line. A no-op outside a
  // session (the tracker guards it), so the Fetch button costs nothing here.
  linkLedger?.noteFullRead();
  const keep = getSettings().deviceScope === "scene" ? captureSceneExternal(target) : null;
  // The same boundary decides what the read ASKS for: what this scope restores below is
  // what the read may not have a verdict about, or a selector the unit holds in a shape
  // the plan cannot express stops a session that is not syncing it (readback's own
  // parameter comment carries the case).
  const result = await applyDeviceState(
    getModel(target.modelId),
    target,
    signal,
    undefined,
    pending,
    false,
    keep !== null,
  );
  if (keep) applySceneExternal(target, keep);
  return result;
}

/** A read of a few nodes under the device scope, for the two scoped reads a live session
 *  takes (a follow reconcile and a sideEffect refetch). Under "Scene only" the plan's
 *  scene-external values are put back afterwards, as `applyDeviceStateScoped` does for a
 *  whole-device read: a node read here carries some of them (an oscillator assign into
 *  STEREO, a MIX or an FX channel), and the two reads must not disagree about one value
 *  under one setting. */
async function applyNodeStateScoped(
  target: Plan,
  nodeIds: ReadonlySet<string>,
  signal: AbortSignal | undefined,
  pending: PendingWrites | undefined,
  skipNames = false,
): Promise<ReadbackResult> {
  const keep = getSettings().deviceScope === "scene" ? captureSceneExternal(target) : null;
  const result = await applyNodeState(getModel(target.modelId), target, nodeIds, signal, pending, skipNames);
  if (keep) applySceneExternal(target, keep);
  return result;
}

// A fresh plan, opened at the rate this session last worked at. The model picker
// already keeps the last model across New; the rate belongs to the same rig and
// does not change because a new plan was started. newPlan itself stays at the
// module default — it is a pure core function, and the persisted choice is the
// shell's business (the startup plan takes the same detectRate path).
function newPlanAtLastRate(id: ModelId): Plan {
  const next = newPlan(id);
  setPlanSampleRate(next, detectRate(next.sampleRate));
  return next;
}

/** Replace the open document. Returns true when it did; null when it was refused before
 *  anything was attempted (a device read holds the plan); false when the new one could not
 *  be drawn. A caller must not report the load as having happened unless it is true. Both
 *  refusals report themselves, so no caller needs a failure surface of its own.
 *
 *  `readHoldsLatch` is the device read that holds the latch replacing the plan with the
 *  one it read into — the model switch a Fetch or Live-sync start offered, applied once
 *  its read has landed complete — and is the one replacement the latch does not refuse. */
function loadPlan(next: Plan, { readHoldsLatch = false }: { readHoldsLatch?: boolean } = {}): boolean | null {
  // A device read (fetch / Live-sync start) is merging into the module `plan`;
  // replacing it now would strand the merge (see deviceReadInFlight). A file flow that
  // entered before the read raised the latch reaches here with the latch up, so this is
  // where that flow is refused, and it says so.
  if (flow.deviceReadInFlight && !readHoldsLatch) {
    setStatus(t().status.busyDeviceRead);
    return null;
  }
  // A write converges the plan it was confirmed for and re-reads it after every await, so
  // a document replacing it mid-write is the one the write would put on the unit. A file
  // flow that entered before the write took the link reaches here and is refused.
  if (deviceLinkHolder === "write") {
    setStatus(t().status.deviceLinkBusy);
    return null;
  }
  // Replacing the whole plan invalidates the live snapshot; leave sync first.
  // (Live's own enable path calls loadPlan before begin(), so this is a no-op there.)
  deactivateLive();
  // A seeded slot names a node of the plan being replaced.
  seededDefaults.clear();
  // …and a decision report still up was about a document this one replaces.
  closeLoadReport();
  // deactivateLive drops the subscription and the timers, but a reconcile / refetch
  // already awaiting the device is not reachable from there — the read itself is what
  // still points at the plan being replaced.
  abandonFollowWork();
  // Everything that puts the module state on screen. One definition, called twice: the
  // document is committed before anything draws it — every view reads the module `plan`,
  // so a staged apply would mean threading the document through all of them — and a plan
  // that parses and then cannot be rendered used to leave `plan` and `modelId` moved
  // while the history, the MIDI bindings and the rate UI still described the document
  // that is gone (an undo then applied entries diffed against a plan nothing holds). Two
  // hand-kept lists would put the next view added here into only one of them, which is
  // the failure the rollback exists to prevent.
  const draw = (): void => {
    rememberModel(modelId);
    // Keep the persisted hidden layout in step with the plan now on screen, whether
    // it came from a file (its hidden wins) or a fresh new/switch plan (already
    // seeded from the same store, so this is a no-op).
    rememberHidden(modelId, plan.hidden);
    picker.value = modelId;
    graph.setModel(getModel(modelId), plan);
    syncRateUi(); // picker + persisted rate + constraints (also refreshes the console)
    // Past the gate: what it holds — an open picker, a composition — belongs to the plan
    // being replaced, so nothing outranks the panel for the one taking its place.
    rebuildInspectorNow();
    // A channel tuning screen can be open over this: it reads the plan through a closure,
    // so its values are already the new ones — but nothing had told it to redraw, and it
    // sat showing the plan that was just replaced. Refresh re-resolves the binding too, so
    // a screen whose node or processor the new plan does not have closes itself.
    dynScreen.refresh();
    // A new plan object, and on a model switch another LCD.
    if (modelId !== prevModelId) simView?.reload();
    else simView?.planChanged();
  };
  const prevModelId = modelId;
  const prevPlan = plan;
  const prevDirty = dirty;
  modelId = next.modelId;
  plan = next;
  // The side each stereo channel shows belongs to the model's channels.
  if (modelId !== prevModelId) resetDisplayedSides();
  ensureFixedConnections(getModel(modelId), plan);
  selection = null;
  try {
    draw();
    // After the draw, not before the fixed connections: everything up to here is the load's
    // — the document itself, the connections added above, and the STEREO-pair snap the board
    // takes as it adopts it. Sampled ahead of them, the last two landed in whatever the next
    // gesture stamps, which is an app edit.
    traceProbe?.sample("load");
    dirty = false;
    // A different model is plausibly a different unit, so what the badge read from the last
    // one is no longer a claim the app can make, whichever entry switched it.
    if (modelId !== prevModelId) {
      setFollowUsbBadge(null);
      resetCues();
      resetSendsTarget();
      unitSetup = null;
      resetUnitSessionState();
    }
  } catch (err) {
    // Put the previous document back on screen and report, rather than throwing: three
    // of the four callers pass an app-generated plan and do not catch, so a throw would
    // reach an async fileFlow callback as an unhandled rejection — no status line, no
    // dialog, a canvas that stopped. The restore re-renders a plan that has already
    // rendered once, so it cannot fail in turn. Not restored, none of it recoverable
    // here: the live session (its snapshot was captured for a plan this call set out to
    // replace, and leaving sync is the same answer either way), the selection, and the
    // fixed connections `ensureFixedConnections` added to `next`.
    modelId = prevModelId;
    plan = prevPlan;
    dirty = prevDirty;
    draw();
    showLoadError(err);
    return false;
  }
  // A new document: the model can differ (so an entry's node ids may not exist
  // here), `plan` is a different object, and the operator already confirmed the
  // discard. Reset rather than rebase.
  planHistory?.reset();
  // Reload the (per-model) MIDI mappings and resync the controller to the new plan.
  midi?.onModelChanged();
  return true;
}

// Build a copyable, language-stable report of a plan's violations, so it can be
// pasted back to the tool that generated the plan. One line per problem, keyed by
// its code: a wire prints "[reason] from -> to" (the routing ConnectError codes
// and the plan's "nodeId:portId" refs), an insert-FX slot collision the contended
// slot and every node claiming it — it has no endpoints to name.
//
// The first line carries which of the two classes this is, because the report is
// read away from the modal that framed it: pasted into the tool that generated
// the plan, or into a message. A slot collision offers "load anyway" and the plan
// does load, so a report of one that opened with "validation failed" told the
// generating tool to fix a document the app had already accepted.
function buildPlanReport(model: string, problems: LoadProblem[], refused: boolean): string {
  return [
    refused ? "URX Router plan validation failed" : "URX Router plan validation warnings",
    `model: ${model}`,
    `problems: ${problems.length}`,
    "",
    ...problems.map((p) => {
      if (p.reason === "insertFxSlot") return `[${p.reason}] ${p.slot}: ${p.nodes.join(", ")}`;
      if (p.reason === "insertFxPair") return `[${p.reason}] ${p.nodes.join(" / ")}: ${p.keys.join(", ")}`;
      if (p.reason === "linkedPair")
        return `[${p.reason}] ${p.nodes.join(" / ")}: ${[...p.keys, ...p.sends.map((to) => `send -> ${to}`)].join(", ")}`;
      if (p.reason === "paramRange") {
        // JSON rather than String(): a stored value is a number in the ordinary case but can be
        // a boolean or an object, and `[object Object]` names neither what was there nor why.
        const was = JSON.stringify(p.stored);
        return `[${p.reason}] ${p.node}.${p.key}: ${was} -> ${p.action === "drop" ? "(dropped)" : p.bound}`;
      }
      if (p.reason === "linkedSendPan")
        return `[${p.reason}] ${p.from} -> ${p.to}: ${p.stored ?? "(none)"} -> ${p.pan}`;
      if (p.reason === "compOneKnob")
        return `[${p.reason}] ${p.node}.comp: ${p.keys.map((k) => `${k.key} ${k.stored === undefined ? "(none)" : JSON.stringify(k.stored)} -> ${k.value}`).join(", ")}`;
      if (p.reason === "mbcOneKnob")
        return `[${p.reason}] ${p.node}.insertFxParams: ${p.slots.map((s) => `mbc:${s.slot} ${s.stored === undefined ? "(none)" : JSON.stringify(s.stored)} -> ${s.value}`).join(", ")}`;
      if (p.reason === "booleanParam") return `[${p.reason}] ${p.node}.${p.path}: ${p.stored} -> ${p.value}`;
      if (p.reason === "nodeColor") return `[${p.reason}] ${p.node}: ${JSON.stringify(p.stored)} -> (dropped)`;
      if (p.reason === "documentText")
        return `[${p.reason}] ${p.field}.${p.node}: ${JSON.stringify(p.stored)} -> ${JSON.stringify(p.value)}`;
      return `[${p.reason}] ${p.from} -> ${p.to}`;
    }),
  ].join("\n");
}

// Parse text into a plan, load it, and (when it came from a real path) record it
// as a recent plan. Returns true on success and false on failure (which sets the
// error status); null when the plan carries a problem the operator has been asked
// about — nothing has loaded and nothing has failed, and the load runs from the
// report modal if they proceed — and null when the replacement was refused.
function loadFromText(text: string, path?: string): boolean | null {
  try {
    const doc = deserializeDocument(text);
    const next = doc.plan;
    if (!MODEL_IDS.includes(next.modelId)) {
      showError(t().status.loadError(t().error.unknownModel(next.modelId)));
      return false;
    }
    // A scene-scoped file (Preferences > Plan files) omits the device-wide
    // settings; keep the current plan's values for them — the same semantic as a
    // scene recall on the unit. Only within the same model: another model's
    // monitor / patch wiring would not validate on this one.
    const carriedScene = doc.sceneScoped && next.modelId === plan.modelId;
    if (carriedScene) applySceneExternal(next, captureSceneExternal(plan));
    // Surface every violation as a copyable report. A device readback runs neither
    // check — the unit is the authority for what it is actually running — which is
    // what splits the two classes: illegal routing is a plan this app cannot
    // represent and stays a refusal, while two nodes on one device-wide insert-FX
    // slot is a plan the app itself writes after a readback, so refusing it made
    // Fetch → Save → reopen impossible for its own document. That one warns and
    // offers to open anyway.
    const problems = planProblems(getModel(next.modelId), next);
    const refused = problems.filter(isRefusal);
    if (refused.length > 0) {
      showLoadReport(buildPlanReport(next.modelId, refused, true));
      return false;
    }
    // Repaired before the document opens, so what the panel shows and what the write sends
    // are the same number from here on. The count goes on the status line rather than into
    // a modal: nothing failed and nothing is being asked, which is where architecture.md
    // puts a partial success. It is applied even when a decision below holds the load,
    // because the plan the operator is deciding about is this one. Then completed from the
    // model's factory values: a document carries only what someone wrote in it, and what it
    // omits is a key the panel draws a default for and the write does not send. The DEVICE
    // paths do not come through here: a fetch fills from the unit, and a node it could not
    // read stays absent on purpose.
    const { booleans, ranged, supplied, sendLevels, linkedPairs, linkedPans, oneKnobs, mbcKnobs, colors, texts } =
      prepareLoadedPlan(getModel(next.modelId), next, problems);
    // A STREAMING source the load supplied is recorded as the fill's, so the write confirm
    // names that receiver when the write moves it.
    markSource(
      next,
      supplied.map((p) => connectionContestKey(p.from, p.to)),
      "default",
    );
    // …and the values a scene-scoped document did not carry are not the document's either:
    // they were copied off the plan on screen a few lines above, so their provenance is that
    // plan's. Left as the fill leaves them they would read as "the document wrote this",
    // which exempts the monitor, phones and oscillator from the write confirm's warning.
    if (carriedScene) {
      for (const name of sceneExternalParamNames(next)) {
        markSource(next, [name], plan.paramSource?.get(name) ?? "default");
      }
      // The same for the device-wide wires, whose record is the one a load completion leaves,
      // and for the params they carry, which answer to the wire's record where they have none
      // of their own — so one with no record here has none there either.
      for (const c of next.connections.filter(isSceneExternalConnection)) {
        const name = connectionContestKey(c.from, c.to);
        const from = plan.paramSource?.get(name);
        if (from !== undefined) markSource(next, [name], from);
        for (const key of Object.keys(c.params ?? {})) {
          const leaf = connParamContestKey(c.from, c.to, key);
          const leafFrom = plan.paramSource?.get(leaf);
          if (leafFrom !== undefined) markSource(next, [leaf], leafFrom);
          else next.paramSource?.delete(leaf);
        }
      }
    }
    const finishLoad = (): boolean | null => {
      // Refused (null: nothing was attempted) or failed to draw (false): loadPlan said so,
      // and the caller must not go on to remember a recent path and announce a document
      // that never opened. A refusal is not a file that fails to load.
      const loaded = loadPlan(next);
      if (loaded !== true) return loaded;
      // LEADS the line rather than trailing it. The status bar is one ellipsized line and a
      // file name has no length limit, so a notice placed after the name is off screen for a
      // long one. This is the only thing said about a document the loader changed, and a
      // repair that reports nothing is the defect the report exists to prevent.
      // Two counts, not one: a bound moves a value to the nearest one this app can send, while
      // a drop removes what it could not read at all and leaves the effect on its own default.
      // Said with the bounded sentence alone, a document whose values were discarded is told
      // they were adjusted.
      const boundCount = ranged.filter((p) => p.action === "bound").length;
      const dropCount = ranged.length - boundCount;
      const notes = [
        ...(booleans.length > 0 ? [t().status.booleanParamsConverted(booleans.length)] : []),
        ...(boundCount > 0 ? [t().status.paramsBounded(boundCount)] : []),
        ...(dropCount > 0 ? [t().status.paramsDropped(dropCount)] : []),
        ...(supplied.length > 0 ? [t().status.streamingSourceSupplied] : []),
        ...(sendLevels.length > 0 ? [t().status.sendLevelsSupplied(sendLevels.length)] : []),
        ...(linkedPairs.length > 0 ? [t().status.linkedPairsAligned(linkedPairs.length)] : []),
        ...(linkedPans.length > 0 ? [t().status.linkedSendPansAligned(linkedPans.length)] : []),
        ...(oneKnobs.length > 0 ? [t().status.compOneKnobsAligned(oneKnobs.length)] : []),
        ...(mbcKnobs.length > 0 ? [t().status.mbcOneKnobAligned] : []),
        ...(colors.length > 0 ? [t().status.colorsDropped(colors.length)] : []),
        ...(texts.length > 0 ? [t().status.textsRewritten(texts.length)] : []),
      ];
      const line = (what: string): string => [...notes, what].join(" — ");
      if (path) {
        recent = rememberRecent({ path, name: baseName(path), modelId }, getSettings().recentMax);
        refreshInspector();
        setStatus(line(t().status.openedFrom(baseName(path))));
      } else {
        setStatus(line(t().status.planLoaded));
      }
      return true;
    };
    const decisions = problems.filter(needsDecision);
    if (decisions.length > 0) {
      const m = t().loadReport;
      // What the decision is about: the plan on screen as it stood when the report opened,
      // whose discard has already been confirmed.
      const onScreen = plan;
      const stateAtOpen = clonePlanState(plan);
      showLoadReport(buildPlanReport(next.modelId, problems, false), {
        title: m.slotTitle,
        intro: m.slotIntro,
        // This one runs from the modal's click handler, after the flow that opened the
        // report has let the latch go, so it takes the latch again — and it asks again what
        // the opening flow asked: a plan replaced since is refused (loadPlan closes the
        // report when one is), and one edited since asks the discard confirm once more. The
        // only step that can fail is `loadPlan`, which reports and returns rather than
        // throwing.
        proceed: {
          label: m.loadAnyway,
          run: () =>
            void fileFlow(
              async () => {
                if (plan !== onScreen) {
                  setStatus(t().status.canceled);
                  return;
                }
                if (diffPlans(stateAtOpen, plan).length && !(await confirmDiscard())) return;
                finishLoad();
              },
              { replaces: true },
            ),
        },
      });
      // Neither loaded nor failed: the decision is on screen. Null rather than false,
      // so a recent entry pointing at a file that opens perfectly well is not dropped
      // as unloadable while its report is still up.
      return null;
    }
    return finishLoad();
  } catch (err) {
    showLoadError(err);
    return false;
  }
}

/** The one load-failure surface: every path that fails to open a document reports here,
 *  including `loadPlan`'s own render failure, which is reached from callers that have no
 *  try of their own. */
function showLoadError(err: unknown): void {
  const message = err instanceof PlanError ? t().error[err.code] : errorText(err);
  showError(t().status.loadError(message));
}

// Open a plan from wherever its document comes from — the Open dialog, a recent
// path, a dropped file — with the one discard prompt and the one failure surface
// all three share. `read` resolves null when its dialog was canceled; `path` is
// what the plan is remembered by, so it is absent for a browser pick or drop.
// Resolves true on success, false when the load was attempted and failed, and
// null when nothing was attempted (canceled, another file flow in flight, the
// replacement refused, or the plan's problem is on screen for the operator to decide on).
async function openPlanFrom(read: () => Promise<{ text: string; path?: string } | null>): Promise<boolean | null> {
  return fileFlow(
    async () => {
      if (!(await confirmDiscard())) return null;
      try {
        const doc = await read();
        if (!doc) return null;
        return loadFromText(doc.text, doc.path);
      } catch (err) {
        showLoadError(err);
        return false;
      }
    },
    { replaces: true },
  );
}

async function openRecent(path: string): Promise<void> {
  const outcome = await openPlanFrom(async () => ({ text: await readTextByPath(path), path }));
  // An entry whose file no longer loads (moved / deleted / corrupted) is
  // dropped automatically — keeping it would only reproduce the same error —
  // and the status line says so, since the mutation happened without a prompt.
  if (outcome === false) {
    recent = removeRecent(path);
    refreshInspector();
    setStatus(t().status.recentRemoved(baseName(path)));
  }
}

async function confirmDiscard(): Promise<boolean> {
  if (!dirty) return true;
  return confirmDialog(t().confirm.discard);
}

// One plan/settings file flow at a time across every entry point (File > New /
// Open / Save, a recent row, a window drop, the .urxf import): each latch above
// is private to its handler, so this shared one is what keeps rapid repeat
// across any two of them from stacking discard confirms and file dialogs.
//
// Its `deviceReadInFlight` half covers a different hazard: a device read (fetch /
// Live-sync start) passes the module `plan` by reference into applyDeviceState,
// which mutates it across many awaited round-trips, and its epilogue re-reads the
// module `plan`. Nothing disables the file / model entry points during that
// seconds-long window, so a New / Open / drop / recent / .urxf or a model switch
// mid-read would swap `plan` out from under the read — corrupting it, or (on Live
// start) snapshotting the swapped-in plan as device truth. The two reads raise it
// and clear it in their finally; every wholesale plan replacement checks it, except
// the model switch the read itself offered, which it applies under the latch once the
// read into the new plan has landed complete (loadPlan's `readHoldsLatch`).
//
// The third latch is `deviceLinkHolder` (declared with the lock it drives, above):
// whoever holds the device link, including the destructive runs, which are the reason
// the MIDI gate reads it. Minutes long, unlike the two here.
const flow = new FileFlowLatch({
  // Refused, and said so: the flow the operator picked simply does not happen, and the
  // status line is showing the read's own progress rather than an answer to that click.
  onDeviceReadBusy: () => setStatus(t().status.busyDeviceRead),
  // The MIDI gate's reported window ends with the latch (see MidiEngine.gateReleased).
  onReleased: () => midi?.gateReleased(),
});
// `replaces`: the flow replaces the plan wholesale (New, Open, a recent row, a drop, the model
// picker). Refused at its entry while a write holds the link, for the reason loadPlan refuses
// one that entered before the write did.
const fileFlow = <T>(run: () => Promise<T>, { replaces = false }: { replaces?: boolean } = {}): Promise<T | null> => {
  if (replaces && deviceLinkHolder === "write") {
    setStatus(t().status.deviceLinkBusy);
    return Promise.resolve(null);
  }
  return flow.run(run);
};

// Warn before touching a unit whose System firmware differs from the version this
// build was validated against — the parameter mappings may not match. Returns true
// to proceed (matching firmware, or the user chose to continue), false to abort.
async function confirmFirmware(device: DeviceSummary): Promise<boolean> {
  // An unread version is not a missing one: proceeding would silently disable the
  // very check that decides whether this build's parameter mappings apply.
  if (device.firmware === null) {
    showError(t().error.firmwareUnread);
    return false;
  }
  if (!firmwareMismatch(device.firmware)) return true;
  // Preference-suppressible (Preferences > Warnings): the mismatch becomes a
  // silent proceed, but an unreadable version above stays a hard stop.
  if (!getSettings().warnFirmware) return true;
  return confirmDialog(t().confirm.firmwareMismatch(device.firmware, SUPPORTED_SYSTEM_FIRMWARE));
}

// The connected device may be a different model than the one selected. Fetch and
// Live sync offer to switch the UI to a fresh plan of the device's model so the
// device values map onto the right channels; write and compare refuse instead (they
// act on the current plan — see refuseModelMismatch). Resolves to the plan the read
// runs against when it may proceed — null for the plan on screen (same model), or the
// fresh plan when the switch was taken — "unknown" for a model this build does not
// know, and "canceled" when the switch was declined; the caller formats its own
// message for the two stops. Taking the switch replaces nothing yet: the fresh plan
// replaces the one on screen only once a complete read into it has landed
// (applyModelSwitch), so a read that fails, is cancelled or comes back incomplete leaves
// the plan and its model as they were.
async function offerModelSwitch(device: DeviceSummary): Promise<Plan | null | "unknown" | "canceled"> {
  if (device.model === modelId) return null;
  if (!MODEL_IDS.includes(device.model as ModelId)) return "unknown";
  if (!(await confirmDialog(t().confirm.switchModel(device.model, modelId)))) return "canceled";
  const next = emptyPlan(device.model as ModelId);
  // That model's shelf, as every fresh plan takes it: the switch's plan is drawn through
  // loadPlan, which records the plan's hidden set as the model's, so one built empty here
  // would erase the layout the operator shelved on that model.
  next.hidden = loadHidden(next.modelId);
  // The connections the model always has, as loadPlan gives a plan it adopts, so the read
  // measures the unit's values on them against the values this plan holds there.
  ensureFixedConnections(getModel(next.modelId), next);
  return next;
}

// What a device read merges into: the plan on screen, or — for a read that carries a
// model switch — the switch's plan, for as long as the plan on screen is still the one
// the read started from. Replaced under the read (which loadPlan refuses while the read
// latch is up), it answers the replacement, and readIntoPlan then merges nothing.
function readTarget(switchTo: Plan | null): () => Plan {
  if (!switchTo) return () => plan;
  const onScreen = plan;
  return () => (plan === onScreen ? switchTo : plan);
}

// Replace the plan on screen with the switch's plan, which a read has just landed in.
// False when the replacement did not happen (loadPlan reported why). loadPlan puts the
// badge back to unknown for the new model; the read's own Follow USB value is set after
// this.
function applyModelSwitch(next: Plan): boolean {
  return loadPlan(next, { readHoldsLatch: true }) === true;
}

// Refuse to act on a device whose model differs from the plan's — the plan's
// channels would map onto the wrong hardware. Shared by withCheckedDevice's four call
// sites (write, compare, device setup read, device setup apply), which (unlike fetch /
// Live sync) cannot offer to switch: they act on the plan as it is.
// `wrap` builds the action-specific error message from the mismatch text. Returns
// true to proceed, false when it refused (having surfaced the error).
function refuseModelMismatch(device: DeviceSummary, wrap: (message: string) => string): boolean {
  if (device.model === modelId) return true;
  showError(wrap(t().error.modelMismatch(device.model, modelId)));
  return false;
}

picker.addEventListener("change", async () => {
  const next = picker.value as ModelId;
  if (next === modelId) return;
  // The same shared latch File > New / Open / drop / recent use: the switch runs the
  // one discard confirm + a wholesale plan replacement, so it must not stack with
  // another file flow, and it is refused while a device read or a write holds the plan.
  const switched = await fileFlow(
    async () => {
      if (!(await confirmDiscard())) return false;
      // A refusal said why; the picker goes back to the model still on screen.
      if (!loadPlan(newPlanAtLastRate(next))) return false;
      setStatus(t().status.switchedModel(next));
      return true;
    },
    { replaces: true },
  );
  // Declined discard, another file flow held the latch (null), or a device read or a
  // write refused it: restore the picker to the model still on screen.
  if (!switched) picker.value = modelId;
});

/** Take a sampling frequency chosen on the toolbar or on the SIM into the plan. */
/** Apply one edit the SIM view made, through the funnels the board and the Inspector use.
 *  False where the plan does not take it. */
function applySimEdit(edit: SimEdit): boolean {
  switch (edit.kind) {
    case "node":
      // The Inspector holds the recorder's Track Count while live (the link cannot carry it),
      // and so does this.
      if (liveSessionUp && edit.patch.sdRecTrackCount !== undefined) return false;
      inspectorActions.onUpdateNodeParams(edit.id, edit.patch, edit.written, edit.defaults);
      break;
    case "send": {
      // The funnel addresses a send by its ports, and takes no edit of one the plan does not wire.
      const from = ref(edit.from, "out");
      const to = ref(edit.to, "in");
      if (!plan.connections.some((c) => c.from === from && c.to === to)) return false;
      inspectorActions.onUpdateParams(from, to, edit.patch);
      break;
    }
    case "name":
      inspectorActions.onRenameNode(edit.id, edit.name);
      break;
    case "color":
      inspectorActions.onRecolorNode(edit.id, edit.color);
      break;
    // A wire goes through the board's own connect and delete, which keep their rules
    // (a source replaced rather than doubled, a pair's mirrored source, a fixed wire kept).
    case "connect":
      graph.connectTo({ ref: edit.from, dir: "out", tap: edit.tap ?? false, targets: [] }, edit.to);
      break;
    case "disconnect":
      graph.deleteConnection(edit.from, edit.to);
      break;
    case "fxType":
      inspectorActions.onSetFxEffectType(edit.id, edit.type);
      break;
    // The toolbar's rate picker is locked while the device link is held, and so is this.
    case "rate":
      if (rateHold(rateHoldState()) !== null) return false;
      // During Live sync the LCD's choice waits for the operator's answer, and the glass shows
      // the plan's rate until then.
      if (deviceLinkHolder !== null) {
        pickSampleRate(edit.rate);
        return false;
      }
      chooseSampleRate(edit.rate);
      break;
    case "deviceWrite":
      writeHeldDeviceValue(edit.param, edit.y, edit.value);
      break;
    case "none":
      break;
    // One choice on the LCD that takes more than one gesture on the board.
    case "steps":
      for (const step of edit.steps) if (!applySimEdit(step)) return false;
      break;
  }
  return true;
}

// The values written by hand this session to a row the unit drives, by address (param/y).
const heldDeviceWrites = new Map<string, number>();

/** Write one value the plan's writer leaves out to the unit, behind the plan flush and every unit
 *  write queued so far, while a session is up. With no session there is no unit to write it to. */
function writeHeldDeviceValue(param: number, y: number, value: number): void {
  if (!liveSessionUp) return;
  heldDeviceWrites.set(`${param}/${y}`, value);
  cueWrites = cueWrites
    .then(async () => {
      await live?.idle();
      if (liveSessionUp) await vdSet(param, 0, y, value);
    })
    .catch((err: unknown) => stopLiveOnError(errorText(err)));
}

/** What the microSD recorder refuses a clock change with during a session, by what it holds. */
interface RecorderRefusals {
  recording: string;
  playback: string;
}

/** Whether the microSD recorder lets a session change the unit's clock: refused, saying so, while
 *  it holds a file. A read that fails says so and answers no. */
async function recorderAllowsClockChange(refusals: RecorderRefusals): Promise<boolean> {
  let held: SdRecorderHold;
  try {
    held = hasRecorder(modelId) ? await readSdRecorderHold() : null;
  } catch (err) {
    showError(t().error.playbackUnread(errorText(err)));
    return false;
  }
  if (!held) return true;
  showError(held === "recording" ? refusals.recording : refusals.playback);
  return false;
}

/**
 * Ask the operator about re-clocking the unit to `rate` during a session, the way a write's settle
 * asks: refused with `refusals` while the microSD recorder holds a file, and confirmed with what it
 * costs the recorder's Track Count and the inserts otherwise. A read that fails says so and
 * answers no.
 */
async function askLiveRate(rate: number, refusals: RecorderRefusals): Promise<boolean> {
  if (!(await recorderAllowsClockChange(refusals))) return false;
  let cost: { from: number; to: number } | null;
  try {
    cost = hasRecorder(modelId) ? trackCountDrop(await readTrackCount(), rate) : null;
  } catch (err) {
    showError(t().error.trackCountUnread(errorText(err)));
    return false;
  }
  const drop = cost ? t().confirm.trackCountDrop(cost.from, cost.to) : "";
  const off = insertsAboveRate(plan, rate);
  const offNote = off.length ? t().confirm.insertsOffAtRate(insertNames(off)) : "";
  const ask = [t().confirm.reclock(formatRate(plan.sampleRate), formatRate(rate)), offNote, drop]
    .filter(Boolean)
    .join(" ");
  if (await confirmDialog(ask)) return true;
  setStatus(t().status.canceled);
  return false;
}

/**
 * Ask the operator about a rate a live session would write to the unit (askLiveRate), taken only
 * while the session takes a rate at all. Null when nothing needs asking — no session, or the rate
 * the plan already holds.
 */
function confirmLiveRate(rate: number): Promise<boolean> | null {
  if (!liveSessionUp || rate === plan.sampleRate) return null;
  const deviceRate = formatRate(plan.sampleRate);
  const nextRate = formatRate(rate);
  return (async () =>
    (await askLiveRate(rate, {
      recording: t().error.rateDuringRecordingLive(deviceRate, nextRate),
      playback: t().error.rateDuringPlaybackLive(deviceRate, nextRate),
    })) && liveRateOpen())();
}

/**
 * What a settings file imported during a session does to the unit's clock, once the operator has
 * agreed to it: the Follow USB to write before the import where the file's differs from the
 * unit's, and whether the file's rate is taken. Null where the operator declined or the microSD
 * recorder refused it, which imports nothing. Under the Scene only device scope the rate and
 * Follow USB are outside the session: nothing is asked and the rate is not taken.
 *
 * - The same Follow USB, another rate: asked as a rate chosen in the app is (askLiveRate); with
 *   Follow USB on, the unit takes the computer's rate back after the write.
 * - Follow USB turned on: asked as the toolbar's FOLLOW USB asks, and refused while the recorder
 *   holds a file.
 * - Follow USB turned off: asked about the file's rate where it differs, and otherwise about
 *   turning Follow USB off.
 *
 * A value the file does not carry is one it does not change.
 */
async function confirmImportClock(
  source: ParamSource,
): Promise<{ followUsb: boolean | undefined; takeRate: boolean } | null> {
  if (getSettings().deviceScope !== "all") return { followUsb: undefined, takeRate: false };
  const carried = (paramId: number): Promise<number | null> => source.get(paramId, 0, 0).catch(() => null);
  const fileRate = (await carried(PARAMS.SAMPLE_RATE.id)) ?? plan.sampleRate;
  const fileFollow = await carried(PARAMS.FOLLOW_USB.id);
  const followUsb =
    fileFollow !== null && followUsbState !== null && (fileFollow !== 0) !== followUsbState
      ? fileFollow !== 0
      : undefined;
  const refusals: RecorderRefusals = {
    recording: t().error.settingsClockDuringRecording,
    playback: t().error.settingsClockDuringPlayback,
  };
  const asked = async (question: string): Promise<boolean> => {
    if (await confirmDialog(question)) return true;
    setStatus(t().status.canceled);
    return false;
  };
  if (followUsb === true) {
    const cost = hasRecorder(modelId) ? ` ${t().confirm.trackCountMayDrop}` : "";
    if (!(await recorderAllowsClockChange(refusals))) return null;
    return (await asked(`${t().confirm.followUsbOn}${cost}`)) ? { followUsb, takeRate: true } : null;
  }
  if (fileRate !== plan.sampleRate)
    return (await askLiveRate(fileRate, refusals)) ? { followUsb, takeRate: true } : null;
  if (followUsb === false && !(await asked(t().confirm.followUsbOffImport(formatRate(plan.sampleRate))))) return null;
  return { followUsb, takeRate: true };
}

/** Take a rate chosen in the app: during Live sync once the operator has agreed to what it costs,
 *  and not at all while the link holds the rate. */
function pickSampleRate(rate: number): void {
  if (rateHold(rateHoldState()) !== null) {
    ratePicker.value = String(plan.sampleRate);
    return;
  }
  const asked = confirmLiveRate(rate);
  if (!asked) {
    chooseSampleRate(rate);
    return;
  }
  ratePicker.value = String(plan.sampleRate);
  void asked.then((ok) => {
    if (ok) chooseSampleRate(rate);
  });
}

/** The inserts a rate takes off, each named by its effect and, in parentheses, the channel or bus
 *  that held it — the same in every language, since both are the unit's own names. */
function insertNames(off: InsertOffAtRate[]): string {
  return off.map((o) => `${o.label} (${graph.labelOf(o.node)})`).join(", ");
}

/** Write to the unit each value of `id`'s rows the unit drives that an edit moved — `before` is what
 *  they held ahead of it. A row the unit started driving in the same edit is left to the unit. */
function writeMovedHeld(id: string, before: readonly { param: number; y: number; value: number }[]): void {
  if (!liveSessionUp) return;
  const was = new Map(before.map((w) => [`${w.param}/${w.y}`, w.value]));
  for (const w of fxHeldWrites(plan, id)) {
    const prev = was.get(`${w.param}/${w.y}`);
    if (prev !== undefined && prev !== w.value) writeHeldDeviceValue(w.param, w.y, w.value);
  }
}

function chooseSampleRate(rate: number): void {
  // The recorder's Track Count and the inserts the rate rules out go with the rate, one way:
  // see changePlanSampleRate.
  const off = changePlanSampleRate(plan, rate);
  // Same change funnel as every other edit: dirty + (in Live sync) push the new
  // rate to the device. Re-clocking glitches audio, but that is inherent to a
  // deliberate rate change and keeps Live sync from deferring it onto a later edit.
  markChanged();
  syncRateUi();
  setStatus(
    off.length
      ? t().status.insertsOffAtRate(formatRate(plan.sampleRate), insertNames(off))
      : t().status.sampleRate(formatRate(plan.sampleRate)),
  );
}

ratePicker.addEventListener("change", () => pickSampleRate(Number(ratePicker.value)));

$("btn-new").addEventListener(
  "click",
  () =>
    void fileFlow(
      async () => {
        if (!(await confirmDiscard())) return;
        if (!loadPlan(newPlanAtLastRate(modelId))) return;
        setStatus(t().status.newPlan);
      },
      { replaces: true },
    ),
);

$("btn-open").addEventListener(
  "click",
  () => void openPlanFrom(() => openTextDocument({ ext: "json", label: t().filter.plan })),
);

// Fine-tuning mode: holding Shift tightens the step of the controls whose device
// parameter has a verified fine grid (see ui/fine.ts).
initFineMode();

// Drag & drop, the second way into File > Open. A dropped plan lands exactly as it
// would from the dialog — same validation, same recent-list entry when the drop
// carried a real path. The experimental settings-file import registers "urxf" here
// once its gate resolves (see the !DEMO block below); until then nothing takes it,
// and the caption and the refusal both name whatever is registered at that moment.
const dropzone = initDropzone({
  caption: (accepted) => (accepted.includes("urxf") ? t().dropzone.planOrSettings : t().dropzone.plan),
  // A refused drop is a routine "not that file" — the status line, not a modal.
  onReject: (rejection, name, accepted) => {
    setStatus(
      rejection === "multiple"
        ? t().status.dropMultiple
        : accepted.includes("urxf")
          ? t().status.dropUnsupportedSettings(name)
          : t().status.dropUnsupported(name),
    );
  },
});
dropzone.register("json", (file) => void openPlanFrom(async () => ({ text: await file.text(), path: file.path })));

$("btn-save").addEventListener(
  "click",
  () =>
    void fileFlow(async () => {
      // A failed write must keep the plan dirty and surface as a modal, like the
      // load paths do — a silent rejection would read as a successful save.
      try {
        const res = await saveTextDocument(`${modelId}-plan.json`, serialize(plan, sceneSaveOpts()), {
          ext: "json",
          label: t().filter.plan,
        });
        if (!res.saved) {
          setStatus(t().status.canceled);
          return;
        }
        dirty = false;
        if (res.path) {
          recent = rememberRecent({ path: res.path, name: baseName(res.path), modelId }, getSettings().recentMax);
          refreshInspector();
          setStatus(t().status.savedTo(baseName(res.path)));
        } else {
          setStatus(t().status.planSaved);
        }
      } catch (err) {
        showError(t().status.saveError(errorText(err)));
      }
    }),
);

// Demo-only sharing (the buttons stay hidden outside the demo build, but are
// wired unconditionally so the dev-server E2E can drive them): the demo has no
// file IO, so the plan travels as a ?plan= deep link — the same encoding
// loadPlanFromUrl reads — or as a JSON download the desktop app opens.
$("btn-share").addEventListener(
  "click",
  guarded(async () => {
    const url = new URL(location.href);
    url.search = "";
    url.hash = "";
    // Encoding compresses via the platform CompressionStream; surface a failure
    // as a modal rather than silently copying nothing. A missing deflate-raw
    // codec arrives as the typed browser-floor PlanError, same as the decode path.
    try {
      url.searchParams.set("plan", await encodePlanParam(plan, sceneSaveOpts()));
    } catch (err) {
      showError(err instanceof PlanError ? t().error[err.code] : t().status.shareUrlError(errorText(err)));
      return;
    }
    const link = url.toString();
    // Put the link in the address bar first, so it stays copyable by hand when
    // the clipboard is unavailable (insecure context) or rejects the write.
    try {
      history.replaceState(null, "", link);
    } catch {
      // ignore (history unavailable)
    }
    void copyText(link).then((ok) => setStatus(ok ? t().status.shareUrlCopied : t().status.shareUrlInBar));
  }),
);

$("btn-download").addEventListener("click", () => {
  // The demo's stand-in for Save: the downloaded JSON matches a desktop save,
  // so File → Open on the desktop app loads it as-is.
  downloadText(`${modelId}-plan.json`, serialize(plan, sceneSaveOpts()));
  dirty = false;
  setStatus(t().status.planDownloaded);
});

$("btn-export").addEventListener(
  "click",
  guarded(() =>
    graph
      .exportPng(`${modelId}-routing.png`)
      .catch((err: unknown) => showError(t().status.exportError(errorText(err)))),
  ),
);

$("btn-export-pdf").addEventListener(
  "click",
  guarded(() =>
    graph
      .exportPdf(`${modelId}-routing.pdf`)
      .catch((err: unknown) => showError(t().status.exportError(errorText(err)))),
  ),
);

// Third-party license notice (desktop only): the cargo-about page bundled as a
// Tauri resource, parsed and rendered as app DOM (ui/licenses.ts).
$("btn-licenses").addEventListener(
  "click",
  // .catch (not a rejection handler on .then): a notice that reads fine but
  // fails to parse in showLicenses must land in the same error dialog.
  guarded(() =>
    thirdPartyLicenses()
      .then(showLicenses)
      .catch((e: unknown) => showError(t().licenses.error(errorText(e)))),
  ),
);

// The plan-file save scope (Preferences > Plan files), shared by every
// serialize call site: file save, demo JSON download, and the share URL.
function sceneSaveOpts(): SerializeOptions {
  return { sceneOnly: getSettings().saveScope === "scene" };
}

// Preferences modal (the toolbar gear): available in every build; rows that need
// the desktop shell render locked with a tag instead (see ui/prefs.ts).
const prefs = new PrefsPanel({
  // A session is built for one scope: its snapshot and its notify registration. A change
  // during one ends it and starts it again, which reads the unit for the new scope.
  onDeviceScopeChanged: () => restartLiveForScope?.(),
  isDeviceScopeHeld: deviceScopeHeld,
  onRecentChanged: (list) => {
    recent = list;
    refreshInspector();
  },
  onWarningsChanged: () => refreshInspector(),
  // The FINE tag hint names the entry style at build time; rebuild both views so
  // the hint follows a style change.
  onFineChanged: () => {
    refreshInspector();
    consoleView.refresh();
  },
  checkUpdates: () => {
    // DEMO folds statically, so the demo bundle keeps dropping the updater path
    // (the button is desktop-only anyway; this branch is unreachable).
    if (DEMO) return Promise.resolve<UpdateCheckOutcome>({ kind: "failed" });
    return checkForUpdates();
  },
  // Off-line there is nothing to take: the preference is stored on its own and
  // the hold is taken when a session starts. While live it applies at once, so a
  // refusal reaches the row that asked for it.
  setPreventSleep: (on) => (liveSessionUp ? syncSleepHold(on) : Promise.resolve(null)),
  isExperimental: () => experimentalOn,
  themeMode: () => themeMode,
  onThemeMode: (mode) => setThemeMode(mode),
});
$("btn-prefs").addEventListener("click", () => prefs.open());

// Dynamics tuning screens (GATE / COMP). Opened per MONO IN channel from the
// inspector's matching section and from the CONSOLE strip; one host serves both,
// so opening either replaces whatever was on it. It owns the broker's one meter
// slot while open, which is why the console is told to release and regain it
// rather than discovering the swap from frozen bars.
const dynScreen = new DynScreen({
  getModel: () => getModel(modelId),
  getPlan: () => plan,
  isLive: () => liveSessionUp,
  onUpdateNodeParams: (id, patch, written) => {
    const held = fxHeldWrites(plan, id);
    inspectorActions.onUpdateNodeParams(id, patch, written);
    writeMovedHeld(id, held);
  },
  releaseMeters: () => consoleView.releaseMeters(),
  regainMeters: () => consoleView.regainMeters(),
  onMeterError: (message) => stopLiveOnError(errorText(message)),
  midi: midiLearnHooks,
  // Both surfaces print these values, and the inspector's sliders are built from a
  // snapshot taken at render time, so they would keep writing back stale values
  // after the screen moved them.
  onClosed: () => {
    refreshInspector();
    consoleView.refresh();
  },
  // The launcher as the surface on screen draws it now: the inspector's button for the
  // node it shows, or the CONSOLE's opener on that node's strip.
  focusOpener: (key, nodeId) => {
    if (!(key in DYN_PROCESSORS)) return;
    const kind = key as DynKind;
    if (!inspectorHost.hidden) {
      if (selection?.type === "node" && selection.id === nodeId) document.getElementById(`btn-${kind}-screen`)?.focus();
      return;
    }
    consoleView.focusOpener(kind, nodeId);
  },
});

// The macOS Edit menu, built first so the history's depth hook can push into it. Its
// own hooks read `planHistory` lazily, which is still null at this point.
const editMenu = installEditMenu({
  canUndo: () => planHistory?.canUndo() ?? false,
  canRedo: () => planHistory?.canRedo() ?? false,
  run: (kind) => planHistory?.menu(kind),
});

// Undo / redo. Built after the views it repaints, and after the two busy latches it
// reads, so every hook body closes over something already initialized.
planHistory = new PlanHistory({
  getPlan: () => plan,
  reflect: (touch) => reflectHistory(touch),
  labelOf: (id) => graph.labelOf(id),
  onStatus: (msg) => setStatus(msg),
  onAuthored: (names) => markAuthored(plan, names, seededDefaults),
  // A device read merges into `plan` across many awaits and re-bases the live snapshot
  // from its own copy, and a file flow can replace the plan outright: patching under
  // either acts on a premise that is still moving. Every read that RE-AUTHORS the plan
  // counts — the operator's fetch and Live-sync start, and equally device follow's two
  // reconciles and Live sync's 1-knob refetch, and EVERY silent-address park a flush takes
  // — the one in front of its head writes and the one in front of its converge, each under
  // its own condition, so a given flush has 0, 1 or 2 of them. A converge ROUND is not one:
  // it reads the whole write scope but writes nothing back into the plan, so an undo during
  // one is answerable — which makes the parks the only part of a converging flush that
  // refuses a press. The press itself is never consumed
  // (run() refuses before it commits the open entry, so a retry is exact), but the two
  // reconciles reset the history in their reflect a moment later, so for those a refused
  // press is an entry the operator loses — visibly, rather than an edit that may or may
  // not have reached the unit. A modal is refused because none of them edits the plan —
  // except the channel tuning screen, which is exactly what its sliders do, so an undo
  // taken with it open belongs to the plan behind it. A read that carries a model switch
  // says so instead (see switchRead).
  blocked: () =>
    switchRead
      ? t().status.busySwitchRead
      : flow.busy || followReads.size > 0
        ? t().status.undoDeviceBusy
        : modalOpen() && !dynScreen.isOpen()
          ? t().status.undoModal
          : null,
  // As the rate picker locks, and for the same reason: a write converges the plan it re-reads each
  // round, and a session takes a rate only under a scope that carries it and while Follow USB is
  // off, once the operator has agreed to it.
  rateHeld: () => rateHold(rateHoldState()),
  confirmRate: (rate) => confirmLiveRate(rate),
  // Asked of the state the entry would leave behind rather than of the keys it carries: an
  // undo turns a switch on as much as the gesture it reverses did, and which of the two an
  // entry moved does not decide the answer. The patch is applied to a copy, which is the
  // same application the real one performs a moment later rather than a reading of the
  // patch that would have to agree with it.
  patchBlocked: (patch) => {
    const after = clonePlanState(plan);
    applyPatch(after, patch);
    return undoRefusal(getModel(modelId), plan, after, (id) => graph.labelOf(id));
  },
  // The macOS application menu's Undo / Redo render this state (a no-op elsewhere).
  onDepthChange: () => editMenu.pushState(),
});
planHistory.install();

// Trace build only: the read-only probe over the plan-key write ledger and the live
// snapshot (src/ui/trace-probe.ts). TRACE folds to a constant, so a plain build
// evaluates this to null and the module is dropped entirely — the same way DEMO drops
// the control layer. Declared after planHistory because it reads the settled depth.
const traceProbe = TRACE
  ? installTraceProbe({
      getPlan: () => plan,
      // Gated on the session rather than on `live` existing: outside a DEMO build it is
      // always constructed, and end() leaves the finished session's map in place — a
      // flush still awaiting a vdSet when the session ends writes into it afterwards, so
      // clearing there would not answer null either. isActive() is what "no session" is.
      liveSnapshot: () => (live?.isActive() ? live.snapshotEntries() : null),
      depth: () => planHistory?.depth() ?? { undo: 0, redo: 0 },
      rates: () => announcedRates.map((r) => r.hz),
    })
  : null;

$("btn-auto").addEventListener("click", () => {
  graph.autoLayout();
});

$("btn-hide-unused").addEventListener("click", () => {
  graph.hideUnused();
});

// The codes the shell LATCHES: past one of these every later command fails on the latch
// rather than on its own merits, so a single occurrence names the whole operation's cause.
// `broker-unresponsive` is the one that made this necessary — a broker that stops answering
// turns a whole-device read into hundreds of identical failures, and a count then tells the
// operator how much failed while saying nothing about why or what to do.
const LATCHED_LINK_CODES = ["broker-unresponsive", "device-lost", "broker-closed", "control-worker-gone"];

/** The localized cause when a multi-parameter read failed because the LINK died, or null
 *  when its failures are its own — a param the unit refused, a group that did not decode —
 *  which is what the failure count is the honest answer to. A read reports per group rather
 *  than stopping at the first, so the code sits behind the group's own prefix and is matched
 *  inside the entry rather than at its head.
 *
 *  This re-derives at the top of the stack what the shell knew at the bottom. The deeper
 *  fix is for the readback's per-group catch to RETHROW a latched code instead of
 *  collecting it — every caller already has a rejection path (applyDeviceState rejects on
 *  abort), so all three read consumers would get the cause for free and this helper would
 *  delete. It is left for its own change because it alters what a partially-read plan keeps
 *  on the device link, which is a behaviour question rather than a wording one. */
function linkFailureIn(errors: string[]): string | null {
  for (const e of errors) {
    const code = LATCHED_LINK_CODES.find((c) => e.includes(c));
    if (code) return errorText(code);
  }
  return null;
}

// Turn a connect-time failure into a clear, localized status. Four of the Rust vd
// worker's codes (vd.rs) name a state the user can act on directly — Device Center
// not running, running with no URX attached, more than one URX attached with the
// selected model not singling one out, or the control worker dying / going
// unresponsive — so they replace the "<action> failed: …" frame instead of filling
// it. Anything else (a broker-side action failure, an unexpected error) goes into
// onError, localized by errorText like every other embedded cause.
function connectFailureStatus(err: unknown, onError: (message: string) => string): string {
  const code = errorCode(err);
  const standalone =
    code === "broker-unreachable" ||
    code === "no-device" ||
    code === "multiple-devices" ||
    code === "control-worker-gone";
  return standalone ? errorText(err) : onError(errorText(err));
}

/** A cancel (throwIfAborted) surfaces as an AbortError. Read by NAME rather than by class:
 *  `instanceof DOMException` answers false for an error raised in another realm, and one
 *  reported as a device failure sends the operator after a write that never failed. */
function isAbortError(err: unknown): boolean {
  return (err as { name?: unknown } | null)?.name === "AbortError";
}

// Connect, run an action with the connected device, then always disconnect. The
// connect doubles as a pre-check: callers that would discard work put their
// confirm inside `action`, so a no-device state surfaces a clear message without
// first prompting. Connection and action errors surface through connectFailureStatus
// (clear text for the actionable connect states, else the given formatter).
async function withDevice(
  holder: LinkHolder,
  connecting: string,
  onError: (message: string) => string,
  action: (device: DeviceSummary) => Promise<void>,
): Promise<void> {
  // Ahead of the status line: an action that never starts must not leave its own
  // "connecting…" on screen over the holder's progress.
  if (!holdDeviceLink(holder)) return;
  setStatus(connecting);
  try {
    const device = await vdConnect(modelId);
    try {
      await action(device);
    } finally {
      // Release exactly the connection this action opened, by its epoch.
      await vdDisconnect(device.epoch);
    }
  } catch (err) {
    // A cancel surfaces as an AbortError; show the neutral "canceled" status rather than
    // wrapping it as an action failure.
    if (isAbortError(err)) setStatus(t().status.canceled);
    else showError(connectFailureStatus(err, onError));
  } finally {
    releaseDeviceLink(holder);
  }
}

// The pre-flight every device action that acts on the *current plan* runs: connect,
// confirm an untested firmware, and refuse a device that is not the model on screen.
// Fetch and Live sync deliberately stay out — they offer to switch the model instead
// of refusing. Wraps withDevice so the three cannot drift apart across the four call
// sites (write, compare, device setup read, device setup apply).
async function withCheckedDevice(
  holder: LinkHolder,
  connecting: string,
  onError: (message: string) => string,
  action: (device: DeviceSummary) => Promise<void>,
): Promise<void> {
  await withDevice(holder, connecting, onError, async (device) => {
    if (!(await confirmFirmware(device))) {
      setStatus(t().status.canceled);
      return;
    }
    if (!refuseModelMismatch(device, onError)) return;
    await action(device);
  });
}

// A failure report a device action produced (built while connected) but offers to
// save after the connection is released — so the user's confirm + native save
// dialog do not hold the broker connection open. Null when nothing failed.
type ErrorReport = { filename: string; markdown: string } | null;

// Offer to save a device action's failure report. Called after withDevice has
// disconnected, so the per-command reasons are visible without the dev console
// and the connection is not held across the (indefinite) dialogs. The self-test
// passes its own prompt and file-type label; everything else takes the defaults.
async function offerErrorReport(report: ErrorReport, prompt?: string, label?: string): Promise<void> {
  if (report && (await confirmDialog(prompt ?? t().confirm.deviceErrorExport))) {
    // A failed report write must surface like a failed plan save — a silent
    // rejection would read as a saved report.
    try {
      await saveTextDocument(report.filename, report.markdown, { ext: "md", label: label ?? t().filter.errorReport });
    } catch (err) {
      // Called after withDevice returned, so the line holds that action's OUTCOME rather than
      // a progress message, and a failed save would take it down with the clearing — leaving a
      // dialog about the report where the action's own verdict was. Read HERE and not before
      // the save: `saveTextDocument` opens a native dialog first, so the wait is the
      // operator's own and a line written across it is the newer one.
      showError(t().status.saveError(errorText(err)), statusbar.textContent ?? "");
    }
  }
}

// Pull the connected device's current channel levels/pans into the plan. This
// overwrites the matching plan params, so it confirms before discarding edits —
// but only after connecting, so a no-device state is reported without first
// prompting to discard. Desktop only: DEMO is statically true in the browser
// bundle, so this branch — and the control imports it alone references — drops
// from the demo build.
if (!DEMO) {
  // Toggle the device's clock policy from the badge. Turning it ON hands the clock
  // back to the USB host: if the host is on a different rate the device re-clocks to
  // it there and then, so the confirm names that possibility — but when the rates
  // already agree nothing happens at all, which is why it is a confirm rather than a
  // refusal. Turning it OFF changes nothing immediately; it only makes the rate
  // picker authoritative again.
  // A Follow USB press on the SIM view's glass is a press of this button.
  pressFollowUsb = () => followUsbBadge.click();
  followUsbBadge.addEventListener(
    "click",
    guarded(async () => {
      // Unknown: the click reads the device rather than toggling it. Toggling from
      // unknown would have to guess which way, and the operator's first question here
      // is "what is it?", not "change it".
      if (followUsbState === null) {
        // A read that fails is reported through withDevice as a failed READ, and the badge
        // stays unknown.
        await withDevice("follow-usb", t().status.fetchConnecting, t().error.followUsbRead, async () => {
          setFollowUsbBadge(await readFollowUsb());
        });
        return;
      }
      const next = !followUsbState;
      // Asked of the model the UNIT reports, not the one the plan names: a plan may be open
      // against a device it was not written for, and a recorder warning is either missing or
      // invented on the model that is actually on the wire. While live the session already
      // verified the two agree, so the plan's model IS the unit's there.
      const ask = async (deviceModel: string): Promise<boolean> => {
        if (!next) return true;
        // It names no numbers: the device will clock from whatever the computer is running,
        // which this app cannot read. So on a model that HAS a recorder the choice is
        // between saying it may cost the Track Count and saying nothing, and the cost is
        // irreversible. URX22 has no recorder and is asked nothing about one.
        const cost = hasRecorder(deviceModel) ? ` ${t().confirm.trackCountMayDrop}` : "";
        return confirmDialog(`${t().confirm.followUsbOn}${cost}`);
      };
      const apply = async (): Promise<void> => {
        await setFollowUsb(next);
        setFollowUsbBadge(next);
        setStatus(next ? t().status.followUsbOn : t().status.followUsbOff);
      };
      // While live the connection is already held for the session; opening a second
      // one would fight it. Otherwise connect just for this write.
      if (liveSessionUp) {
        if (!(await ask(modelId))) return;
        try {
          await apply();
        } catch (err) {
          // The confirm leaves the window running, so the session can have ended behind it,
          // and its teardown reported its own cause and nothing about this write. A write
          // that failed with no session left is reported as this write's failure.
          if (!liveSessionUp) {
            showError(t().status.writeError(t().error.followUsbWrite(errorText(err), next)));
            return;
          }
          stopLiveOnError(errorText(err));
        }
        return;
      }
      // Inside the holder, so the question is asked of a unit that answered: declining
      // leaves the link released by withDevice's own teardown and writes nothing.
      await withDevice("follow-usb", t().status.writeConnecting, t().status.writeError, async (device) => {
        if (!(await ask(device.model))) {
          setStatus(t().status.canceled);
          return;
        }
        await apply();
      });
    }),
  );

  const fetchBtn = $<HTMLButtonElement>("btn-fetch");
  // A click cancels an in-flight fetch; otherwise it starts one. The whole-device
  // read is serial and stalls when the link drops, so it threads the controller's
  // signal into applyDeviceState (which checks throwIfAborted between reads).
  fetchBtn.addEventListener("click", async () => {
    if (fetchAbort) {
      fetchAbort.abort();
      return;
    }
    const controller = new AbortController();
    fetchAbort = controller;
    fetchBtn.textContent = t().toolbar.fetchCancel;
    let report: ErrorReport = null;
    let reportPrompt: string | undefined;
    try {
      await withDevice("fetch", t().status.fetchConnecting, t().status.fetchError, async (device) => {
        if (!(await confirmFirmware(device))) {
          setStatus(t().status.canceled);
          return;
        }
        if (!(await confirmDiscard())) {
          setStatus(t().status.canceled);
          return;
        }
        // The connected device may be a different model than the one selected.
        // Offer to switch the UI to the device's model (a fresh plan) so the
        // fetched values map onto the right channels; otherwise abort.
        const switchTo = await offerModelSwitch(device);
        if (switchTo === "unknown") {
          showError(t().status.fetchError(t().error.unknownModel(device.model)));
          return;
        }
        if (switchTo === "canceled") {
          setStatus(t().status.canceled);
          return;
        }
        // A file flow that is already running (a save or open dialog the operator has up,
        // a document being read) holds the plan as it stood when it began: the read would
        // replace that plan under it, so the read does not start.
        if (flow.busy) {
          setStatus(t().status.busyFileFlow);
          return;
        }
        // Hold off every wholesale plan replacement for the duration of the read and
        // its epilogue (cleared in the finally below); the read mutates `plan` in
        // place, so a New/Open/switch mid-read would corrupt it.
        flow.deviceReadInFlight = true;
        // The plan on screen takes no edit while a read that carries a switch runs (cleared
        // as the read returns or throws, and in the finally below).
        if (switchTo) beginSwitchRead();
        // The read runs against a private copy, so a cancel throws out of it with the
        // plan on screen untouched — "cancel means nothing happened" needs no restore,
        // and the module plan object is never replaced (which is what used to leave
        // every MIDI binding attached to a discarded Plan). A read that carries a model
        // switch runs against a copy of the switch's plan instead, and that plan
        // replaces the one on screen only once the read has landed complete; with the plan
        // on screen taking no edit meanwhile, that read names no key through the write
        // witness.
        //
        // Follow USB is outside the plan (see params.ts), so the readback does not carry
        // it. It is read on the same connection, first inside the read: a failure throws
        // out of it with nothing merged — reported as the cancel when one was asked for,
        // as a plan read's is — and the badge takes the value only once the plan read has
        // landed.
        let followUsb = false;
        let merged: MergedRead | null;
        try {
          merged = await readIntoPlan(
            readTarget(switchTo),
            async (into) => {
              followUsb = await readFollowUsb().catch((err: unknown) => {
                controller.signal.throwIfAborted();
                throw err;
              });
              return applyDeviceStateScoped(into, controller.signal);
            },
            switchTo ? undefined : planWrites,
            // Paired with the witness above: with no witness there is no authorship to
            // arbitrate on, and the model this asks would be the one the switch is leaving.
            switchTo ? undefined : (ctx) => sourceChoiceHoldKeys(getModel(modelId), ctx),
          );
        } finally {
          switchRead = null;
        }
        // Unreachable while this handler holds deviceReadInFlight (loadPlan refuses
        // under it), but the contract is stated rather than assumed.
        if (!merged) {
          setStatus(t().status.canceled);
          return;
        }
        if (merged.errors.length) console.warn("device readback issues:", merged.errors);
        // A partial read into the plan on screen is kept. One into the switch's plan is not:
        // the switch is applied only by a complete read, as a live start's is, so an
        // incomplete one switches nothing, puts nothing it read on screen and leaves the
        // badge as it was — and is still reported below, as any incomplete fetch is.
        let supplied: string | null = null;
        if (!switchTo || !merged.errors.length) {
          if (switchTo && !applyModelSwitch(switchTo)) return;
          // The undo history re-bases as the read lands, whether or not its values changed
          // any; an edit made after this — while the link is still being released — is an
          // entry of its own and stays undoable. A read that did not land (cancelled, failed,
          // stopped before it, or an incomplete one carrying a switch) leaves the history alone.
          planReadFromDevice();
          noteMergeConflicts(merged);
          notePatchFromDevice(merged.devicePatch);
          takeRefusedEdits(merged);
          // STREAMING holds a source whatever the unit answered, so a read that found none
          // supplies one before the board is drawn.
          supplied = supplyUnsourced(merged);
          // A link that dropped part-way leaves the badge unknown, as a session's drop does
          // (stopLiveOnError).
          setFollowUsbBadge(linkFailureIn(merged.errors) ? null : followUsb);
          // Per-node provenance: nodes a read failed on still show their plan
          // value, so the graph/inspector flag them as not read from the device.
          plan.unreadNodes = merged.unreadNodes;
          rerenderPlan();
          dirty = true;
        }
        // Nodes the readback tried but could not confirm (left at their plan default).
        const unread = merged.unreadNodes.size;
        const outcome = merged.errors.length
          ? // A link that died mid-read is named rather than counted, for the reason
            // linkFailureIn gives; the per-group reasons stay in the report below.
            // An incomplete read into the switch's plan applied nothing, and says that instead
            // of a count.
            (linkFailureIn(merged.errors) ??
            (switchTo
              ? t().status.fetchSwitchIncomplete(merged.errors.length, device.model, modelId)
              : t().status.fetchPartial(merged.applied, merged.errors.length, unread)))
          : unread
            ? t().status.fetchedUnread(device.model, merged.applied, unread)
            : t().status.fetchedDevice(device.model, merged.applied);
        setStatus(withSwitchNotes(supplied ? [supplied, outcome].join(" — ") : outcome, merged));
        // Read failures AND values the merge did not apply are otherwise console-only,
        // and a packaged build has no inspector to read a console in: capture a report
        // to offer after disconnect (below). The two travel together because both are
        // "what this fetch did not do", and neither is visible from the status line —
        // which says a plain success when only the second happened.
        if (merged.errors.length || merged.unplaced.length || merged.held.length) {
          report = {
            filename: `${device.model}-fetch-report.md`,
            markdown: formatReadbackReport(device.model, merged),
          };
          // Which of the two it is decides the prompt. With no read failure nothing
          // failed — the read worked and the merge left the operator's own edits
          // standing — so the default wording would report correct behaviour as a
          // fault, and do it right after the status line said the fetch succeeded.
          // Pinned in src/main.device.clock.test.ts by a fetch whose edit lands inside the read on
          // a key the read authored: "keeps an edit made while a fetch reads Follow USB"
          // (the prompt) and "saves the device values a fetch left standing, under the
          // prompt that says so" (the prompt and the report).
          reportPrompt = merged.errors.length ? undefined : t().confirm.deviceUnappliedExport;
        }
      });
    } finally {
      flow.deviceReadInFlight = false;
      switchRead = null;
      // The MIDI gate's reported window ends with the latch (see MidiEngine.gateReleased).
      midi?.gateReleased();
      fetchAbort = null;
      fetchBtn.textContent = t().toolbar.fetchDevice;
    }
    await offerErrorReport(report, reportPrompt);
  });

  // What a move to `nextRate` costs the microSD recorder, as the sentence to put in front
  // of the operator — or "" when it costs nothing.
  //
  // Empty for a model with no recorder (URX22 has none), and for a count that already fits
  // the new rate: an irreversible-loss warning shown to someone losing nothing is how a
  // warning stops being read.
  //
  // The count comes from the DEVICE, not the plan. An offline plan's count is whatever was
  // last authored, which would both miss real drops and invent false ones. A read that
  // fails throws, and the settle that asked aborts the write on it, as it does on a clock
  // read that fails.
  // Set once a rate has actually gone out, and consumed after the write: the unit does
  // the lowering itself, and whether it announces one is not something this app has
  // measured, so the plan is re-read rather than left to a notify that may never come.
  // Set by the send rather than by the settle's confirm: the operator can approve the
  // re-clock and then decline the change count, and the write then sends nothing at all —
  // re-reading there would apply the unit's UNCHANGED count over a plan whose rate had
  // already moved.
  let trackCountMayHaveDropped = false;

  async function trackCountCost(nextRate: number): Promise<{ from: number; to: number } | null> {
    if (!hasRecorder(modelId)) return null;
    return trackCountDrop(await readTrackCount(), nextRate);
  }

  // Settle what sample rate this write is going to happen at, before anything is
  // sent. Returns true to go ahead, false to abort the write.
  //
  // The rate is the one plan value the device can accept and then undo on its own:
  // with SETUP > Follow USB on it slaves its clock to the USB host, so a rate write
  // re-clocks the hardware and is dragged back to the host's rate about a second
  // later. Writing straight through would report success for a change that did not
  // last. The check is here, at the write boundary, rather than wherever the rate
  // was chosen: the picker and plan loading both happen with no device attached, so
  // there is nothing to compare against until now.
  //
  // A read failure aborts. Which rate the device will end up running at decides
  // which parameters the rest of the write may even contain, so proceeding without
  // it would be writing on a premise the link just failed to establish.
  async function settleSampleRate(): Promise<boolean> {
    let clock;
    try {
      clock = await readClockState();
    } catch (err) {
      showError(t().status.writeError(t().error.clockUnread(errorText(err))));
      return false;
    }
    // The read just told us what the badge is for; show it whether or not there is
    // anything to settle.
    setFollowUsbBadge(clock.followUsb);
    const action = rateAction(plan.sampleRate, clock);
    if (action === "proceed") return true;
    const planRate = formatRate(plan.sampleRate);
    const deviceRate = formatRate(clock.sampleRate);
    // What the plan's rate would cost the recorder. The unit lowers its own Track Count to
    // fit a rate it cannot carry and nothing this app can write raises it again, so unlike
    // every other rate constraint this one is destroyed rather than merely left out — it
    // has to be in front of the decision, not in the release notes. Read from the DEVICE
    // rather than taken from the plan: an offline plan's count is whatever was last
    // authored, and warning from that would both miss real drops and invent false ones.
    // A read that fails aborts the write, as the clock read above does; `null` is the model
    // having no recorder at all, or a count the rate already carries.
    let trackCost;
    try {
      trackCost = await trackCountCost(plan.sampleRate);
    } catch (err) {
      showError(t().status.writeError(t().error.trackCountUnread(errorText(err))));
      return false;
    }
    // A playback or a recording held by the microSD recorder is when the unit keeps its own
    // rate control out of reach. Both arms that write the plan's rate stop here; adopting the
    // device's rate writes none and stays open.
    let recorderHold: SdRecorderHold;
    try {
      recorderHold = hasRecorder(modelId) ? await readSdRecorderHold() : null;
    } catch (err) {
      showError(t().status.writeError(t().error.playbackUnread(errorText(err))));
      return false;
    }
    const refuseHeldRate = (): false => {
      const refuse = recorderHold === "recording" ? t().error.rateDuringRecording : t().error.rateDuringPlayback;
      showError(t().status.writeError(refuse(deviceRate, planRate)));
      return false;
    };
    if (action === "confirmReclock") {
      if (recorderHold) return refuseHeldRate();
      // The device will take the rate and hold it. Re-clocking interrupts audio and
      // renegotiates the USB stream, so it is worth stating outright — but it is a
      // plain yes/no: the plan's rate is the one that sticks.
      const cost = trackCost ? t().confirm.trackCountDrop(trackCost.from, trackCost.to) : "";
      const ask = [t().confirm.reclock(deviceRate, planRate), cost].filter(Boolean).join(" ");
      if (await confirmDialog(ask)) return true;
      setStatus(t().status.canceled);
      return false;
    }
    // Above 96 kHz whole features drop out, so adopting a high device rate means
    // part of the plan will not be written. Name it before the choice is made.
    // The inserts adopting the device's rate takes off the plan. While there are any, the shared
    // note leaves the insert line out: it says the plan keeps what the rate leaves out, which is
    // untrue of these on the adopt arm, and the adopt arm's own line names them.
    const adoptOff = insertsAboveRate(plan, clock.sampleRate);
    const limits = rateConstraints(getModel(modelId), clock.sampleRate)
      .warnings.filter((w) => !(w === "insFx" && adoptOff.length))
      .map((w) => t().warning[w])
      .join(" ");
    // The note belongs to the whole dialog, so it may only say what is true of every arm.
    // `limits` is: adopting the device's high rate and releasing to the plan's each leave
    // those features out. The Track Count warning is NOT — it is what RELEASING costs, and
    // adopting costs nothing — so it gets a line of its own rather than going into the note,
    // worded to name the release arm.
    const note = limits ? t().rateChoice.hiRateNote(limits) : null;
    // Its own wording, not the confirm's: this arm is one of three answers and the sentence
    // has to say which one it is about. `trackCost` decides WHETHER, the message decides
    // how it reads here.
    const releaseNote = trackCost ? t().rateChoice.trackCountDrop(trackCost.from, trackCost.to) : "";
    const adoptNote = adoptOff.length ? t().rateChoice.insertsOffAtRate(deviceRate, insertNames(adoptOff)) : "";
    const choice = await askRateChoice(planRate, deviceRate, note, releaseNote, adoptNote);
    if (choice === "cancel") {
      setStatus(t().status.canceled);
      return false;
    }
    if (choice === "adopt") {
      // The device's rate becomes the plan's, so the rate write is a no-op and the
      // gating downstream matches what the hardware can actually hold. This is an
      // edit like any other — the operator chose it — so it goes through the same
      // funnel and is remembered as the last known rate.
      changePlanSampleRate(plan, clock.sampleRate);
      markChanged();
      syncRateUi(); // persists the adopted rate as the last known one
      return true;
    }
    if (recorderHold) return refuseHeldRate();
    try {
      await setFollowUsb(false);
    } catch (err) {
      showError(t().status.writeError(t().error.followUsbWrite(errorText(err))));
      return false;
    }
    setFollowUsbBadge(false);
    return true;
  }

  // Write the plan to the connected device: diff the plan against the device's
  // current values, confirm the change count, then send only what differs.
  // Writing to a device of a different model is refused (the plan's channels
  // would map onto the wrong hardware). Write and live sync are available on any
  // desktop build (the self-test below stays behind --experimental).
  {
    const writeBtn = $<HTMLButtonElement>("btn-write");
    writeBtn.disabled = false;
    // Like fetch: a click cancels an in-flight write, else starts one. The diff +
    // converging send is serial and stalls on a dropped link, so the controller's
    // signal threads into diffPlan and sendConverging (both check throwIfAborted
    // between round-trips); the string name diff/send are bracketed by explicit
    // abort checks since they take no signal.
    writeBtn.addEventListener("click", async () => {
      if (writeAbort) {
        writeAbort.abort();
        return;
      }
      // A plan holding +48V and HI-Z both on for a channel is a plan this app cannot send:
      // whichever of the two goes out second turns one on while the other is on, so the
      // order they are emitted in cannot make the write legal. Decided before the link is
      // opened — nothing is connected and nothing is sent — and the operator turns one of
      // them off, which every surface allows. The plan can only hold it from the unit's own
      // state (a read, or a `.urxf` import), which stays displayed until they do.
      const bothOn = bothOnLabels();
      if (bothOn) {
        setStatus(t().status.writePhantomHiZ(bothOn));
        return;
      }
      const controller = new AbortController();
      const { signal } = controller;
      writeAbort = controller;
      writeBtn.textContent = t().toolbar.writeCancel;
      let report: ErrorReport = null;
      try {
        await withCheckedDevice("write", t().status.writeConnecting, t().status.writeError, async (device) => {
          // Scene scope drops SAMPLE_RATE from the write set, so there is no
          // rate to settle — the device keeps running at its own.
          const scope = getSettings().deviceScope;
          if (scope !== "scene" && !(await settleSampleRate())) return;
          // One attempt of the whole diff → confirm → send sequence. Returns the
          // sent/not-sent split when the send stopped part-way (so the caller can
          // offer a retry), or null when there is nothing left to do.
          const saveReport = (
            failed: Array<{ name: string; error?: string }>,
            residual: CommandDiff[],
            reads: string[],
            after: { wrote?: boolean; unconfirmed?: string[] } = {},
          ): void => {
            report = {
              filename: `${modelId}-write-errors.md`,
              markdown: formatWriteReport(device.model, failed, residual, reads, after),
            };
          };
          // What the whole write operation has taken back, across attempts. Per ATTEMPT it would go
          // missing the moment a retry succeeds: the plan has already taken the key, so the next
          // attempt's diff no longer reports it and its own count is 0 — while the plan is holding
          // the value the count is about.
          let adopted = 0;
          const adoptedNote = (): string => (adopted ? ` — ${t().status.paramsBounded(adopted)}` : "");
          // What the whole operation has sent, numeric values and presets both, and which nodes an
          // accepted preset left unconfirmed — across attempts, for the same reason: a retry that
          // stops before sending anything still stands on what an earlier attempt sent.
          let sentSoFar = 0;
          const unconfirmedSoFar: string[] = [];
          // A read that fails once something has gone out is a read-back the write could not
          // confirm; before anything has, it is the read the write stops on.
          const readFailed = (reads: string[]): void => {
            if (sentSoFar) {
              setStatus(t().status.writeUnconfirmed(sentSoFar, reads.length) + adoptedNote());
              saveReport([], [], reads, { wrote: true, unconfirmed: unconfirmedSoFar });
            } else {
              setStatus(t().status.writeReadFailed(reads.length) + adoptedNote());
              saveReport([], [], reads);
            }
          };
          const attemptWrite = async (
            confirmFirst: boolean,
          ): Promise<{ sent: number; notSent: number; unread?: number } | null> => {
            // A read failure leaves those parameters' device values unknown, so the
            // write stops on the first one — the rest of the sweep would only be
            // establishing values for a write that is already canceled.
            const { diffs, errors } = await diffPlan(getModel(modelId), plan, { signal, stopOnError: true, scope });
            if (errors.length) {
              readFailed(errors);
              return null;
            }
            // CH SETTING names are string params outside the numeric diff; diff them
            // separately so a name-only change still counts and writes.
            signal.throwIfAborted();
            const { writes: nameWrites, errors: nameErrors } = await diffNames(getModel(modelId), plan);
            if (nameErrors.length) {
              readFailed(nameErrors);
              return null;
            }
            const total = diffs.length + nameWrites.length;
            // From the EMITTED list, not from `diffs`: a survivor that already
            // matches the device is not a diff, and the owner it displaced would
            // then go unmentioned — including when the only pending change is that
            // owner's, which reports "no changes to write".
            const owners = collisionOwners(dryRun(getModel(modelId), plan));
            const sharedNote = owners.length ? sharedSettingText(owners) : "";
            // A node whose name is empty has no name to send, and the unit keeps its own: said
            // rather than counted as a match, since nothing here knows whether the two agree.
            const unnamed = unnamedNodes(getModel(modelId), plan);
            const unnamedLabels = unnamed.map((id) => graph.labelOf(id)).join(", ");
            if (total === 0) {
              const nothing = unnamed.length
                ? t().status.writeNamesNotSent(unnamedLabels, unnamed.length)
                : t().status.writeNoChanges;
              setStatus((sharedNote ? `${nothing} ${sharedNote}` : nothing) + adoptedNote());
              return null;
            }
            const renaming = new Set(nameWrites.filter((w) => w.name === undefined).map((w) => w.node));
            // What the operator never chose, from the addresses that will actually move. The
            // plan is dense, so a write carries keys nobody set; naming the strips is what makes
            // that a decision rather than a surprise. Built inside the ask, since a retry is the
            // same decision being carried out rather than taken again.
            const ask = (): string =>
              [
                sharedNote,
                unauthoredNoteFor(new Set(diffs.map((d) => cmdAddr(d.command))), scope, renaming),
                unnamed.length ? t().confirm.namesNotSent(unnamedLabels, unnamed.length) : "",
                t().confirm.write(total),
              ]
                .filter(Boolean)
                .join("\n\n");
            if (confirmFirst && !(await confirmDialog(ask()))) {
              setStatus(t().status.canceled + adoptedNote());
              return null;
            }
            // Held here rather than read off the result, for the same reason `onSent` below
            // exists: a cancel throws out of the loop and there is no result to read. What the
            // unit confirmed before the cancel is in here, and taking it is the only chance —
            // the address stops differing the moment the write lands, so no later write
            // produces a diff that would offer it again.
            const ledger = newConfirmLedger();
            const presetFailures: Array<{ name: string; error?: string }> = [];
            // A refused preset stops the write, as any refused write does. What it leaves is the
            // presets after it unsent and the nodes whose preset was accepted with a rebuilt strip
            // nothing read back.
            let presetStop: { notSent: number; unconfirmed: string[] } | null = null;
            let presetsSent = 0;
            let presetsCanceled = false;
            let convergeResult: ConvergeResult;
            try {
              const converge = {
                signal,
                scope,
                ledger,
                // Armed the moment the rate RESOLVES, not from the returned outcomes: an
                // abort between two sends throws out of the send loop and takes those
                // outcomes with it, so a rate that reached the unit would go unrecorded and
                // the recorder would never be re-read. Everything but an explicit refusal
                // arms it: the shell sends before it waits, so a write whose answer never
                // came may have landed and taken the Track Count down with it, and the
                // recorder would then be left showing a count the unit no longer has. Any
                // rate that goes out arms it, whatever put it in the plan, on a model that
                // has the recorder.
                onSent: (o: SendOutcome) => {
                  if (o.result !== "refused" && o.command.name === "SAMPLE_RATE" && hasRecorder(device.model)) {
                    trackCountMayHaveDropped = true;
                  }
                },
              };
              convergeResult = await sendConverging(getModel(modelId), plan, { ...converge, initialDiffs: diffs });
              // The Sweet Spot presets go out once the numeric phase has landed intact, and the strip
              // they rebuild is converged again behind them (client.ts sendPresetsAndReconverge).
              if (!convergeResult.outcomes.some((o) => !o.ok) && !convergeResult.readErrors.length) {
                const second = await sendPresetsAndReconverge(getModel(modelId), plan, converge);
                presetFailures.push(
                  ...second.presets
                    .filter((o) => !o.ok)
                    .map((o) => ({ name: `name ${o.write.param}:${o.write.y}`, error: o.error })),
                );
                presetsSent = second.presets.filter((o) => o.ok).length;
                presetsCanceled = second.canceled;
                if (second.canceled) unconfirmedSoFar.push(...second.unconfirmed);
                if (presetFailures.length)
                  presetStop = { notSent: second.notSent.length, unconfirmed: second.unconfirmed };
                const first = convergeResult;
                if (second.result)
                  convergeResult = {
                    ...second.result,
                    outcomes: [...first.outcomes, ...second.result.outcomes],
                    readErrors: [...first.readErrors, ...second.result.readErrors],
                  };
                else if (second.readErrors.length)
                  convergeResult = { ...first, readErrors: [...first.readErrors, ...second.readErrors] };
              }
            } catch (err) {
              if (!isAbortError(err)) throw err;
              // Reported here rather than left to withDevice's neutral line, because the plan
              // may have changed on the way out. The same join window as the clean path below:
              // this runs on the rejection's own microtask, so no announcement can land
              // between the loop stopping and the join.
              const cancelTaken = adoptConfirmedWrites(confirmedAddrs(ledger));
              adopted += cancelTaken;
              setStatus(t().status.canceled + adoptedNote());
              return null;
            }
            const { outcomes, residual, readErrors: convergeErrors } = convergeResult;
            // Here, with nothing awaited between the converge and this line: the adoption joins
            // the confirmed ADDRESSES to plan KEYS, and one address is a different key under a
            // different effect type, so a front-panel type change announced during the awaits
            // below would join one moment's addresses to another moment's plan. `sendNames` is
            // one such await, on the clean path.
            const takenBack = adoptConfirmedWrites(confirmedAddrs(ledger));
            adopted += takenBack;
            const skipped = outcomes.filter((o) => o.skipped).length;
            const failed: Array<{ name: string; error?: string }> = outcomes.filter(reachedAndFailed).map((o) => ({
              name: o.command.name + unverifiedGuessNote(getModel(modelId), o.command.paramId, o.command.y),
              error: o.error,
            }));
            failed.push(...presetFailures);
            // On every outcome, not only the clean one: a write that stopped can still have
            // landed and read back the address the plan takes its value from, and a line that
            // reported only the stop would leave a changed plan unmentioned.
            const note = adoptedNote();
            // Names only go out once the numeric phase reached the device intact —
            // a stopped or unreadable numeric phase means the link already failed.
            if (!failed.length && !skipped && !convergeErrors.length && !presetsCanceled) {
              // Reported here rather than thrown, for the reason the converge's own cancel is:
              // the adoption above has already changed the plan, and a bare "Canceled" from
              // withDevice would leave that unsaid.
              if (signal.aborted) {
                setStatus(t().status.canceled + note);
                return null;
              }
              const nameOutcomes = await sendNames(nameWrites.filter((w) => w.name === undefined));
              // Normalize the two outcome shapes (numeric command vs string name write)
              // to {name, error} so the count and the saved report share one list.
              failed.push(
                ...nameOutcomes
                  .filter((o) => !o.ok)
                  .map((o) => ({ name: `name ${o.write.param}:${o.write.y}`, error: o.error })),
              );
            }
            if (failed.length) console.warn("device write failures:", failed);
            if (residual.length) console.warn("device write did not converge:", residual);
            // Failures/non-convergence are otherwise console-only: capture a report to
            // offer after disconnect (below), so the reasons are visible without the console.
            // Every read the converge makes comes after a send (the first round is the confirmed
            // diff), so a read failure here is a read-back of a write, not the read a write stops on.
            const sent = outcomes.filter((o) => o.ok).length + presetsSent;
            sentSoFar += sent;
            if (presetStop) unconfirmedSoFar.push(...presetStop.unconfirmed);
            if (failed.length || residual.length || convergeErrors.length || presetStop || presetsCanceled) {
              saveReport(failed, residual, convergeErrors, { wrote: sentSoFar > 0, unconfirmed: unconfirmedSoFar });
            }
            // A cancel taken once a preset has gone out: what went out is in the report, and nothing
            // more is sent or offered.
            if (presetsCanceled) {
              setStatus(t().status.canceled + note);
              return null;
            }
            if (presetStop) {
              const renames = nameWrites.filter((w) => w.name === undefined).length;
              const notSent = presetStop.notSent + renames;
              setStatus(t().status.writeStopped(sent, notSent) + note);
              return { sent, notSent };
            }
            if (!skipped && !failed.length && convergeErrors.length) {
              setStatus(t().status.writeUnconfirmed(sentSoFar, convergeErrors.length) + note);
              return { sent: sentSoFar, notSent: 0, unread: convergeErrors.length };
            }
            if (!skipped) {
              setStatus(
                (failed.length
                  ? t().status.writePartial(total - failed.length, failed.length)
                  : residual.length
                    ? t().status.writeResidual(residual.length)
                    : t().status.written(total)) + note,
              );
              return null;
            }
            // Counted from the outcomes rather than against `total`: a converge round
            // re-sends what the device reset, so `total` (the round-1 count) is not the
            // denominator. A stopped numeric phase never sent the names, so they are
            // all not-sent too.
            const notSent = skipped + nameWrites.length;
            setStatus(t().status.writeStopped(sent, notSent) + note);
            return { sent, notSent };
          };

          // A stopped write leaves the device holding part of what was confirmed.
          // Offer to run it again rather than reporting a breakdown the user cannot
          // act on: the retry re-diffs, so what already landed drops out by itself.
          // Whether the write ENDED — every path that returns writes an outcome line, and so
          // does the cancel arm below. What is left is a throw this does not answer, which
          // leaves whatever progress message was on screen; the recorder tail restores a line
          // only when there is an outcome to restore.
          let wroteOutcome = false;
          try {
            let stop = await attemptWrite(true);
            wroteOutcome = true;
            while (
              stop &&
              (await confirmDialog(
                stop.unread
                  ? t().confirm.writeRetryUnread(stop.sent, stop.unread)
                  : t().confirm.writeRetry(stop.sent, stop.notSent),
              ))
            ) {
              stop = await attemptWrite(false);
            }
          } catch (err) {
            if (!isAbortError(err)) throw err;
            // A cancel taken in a LATER attempt throws out of it, and the neutral line
            // withDevice would write for that says nothing about what an earlier attempt took
            // back. Counted across attempts because each one adopts different keys: a key the
            // plan has already taken is no longer a problem the next attempt reports.
            setStatus(t().status.canceled + adoptedNote());
            wroteOutcome = true;
          } finally {
            // INSIDE the action, because withDevice awaits its own vdDisconnect on the way
            // out: a read placed after it is issued with no connection installed and the
            // shell answers "not-connected" for every address. It is in a FINALLY because
            // the rate goes out first — a write that failed or was cancelled after it still
            // moved the unit, and the recorder still needs re-reading.
            if (trackCountMayHaveDropped) {
              trackCountMayHaveDropped = false;
              const merged = await followRead("track count after a rate change", (into, signal) =>
                applyNodeState(getModel(modelId), into, new Set([SDREC_NODE_ID]), signal, undefined, true),
              );
              if (merged) {
                noteMergeConflicts(merged);
                // absorb, not rebase: this writer KNOWS which keys the device authored, and
                // rebase drops any open entry — an edit the operator started while the read
                // ran would become un-undoable (ui/history.ts states the split).
                planHistory?.absorb(merged.devicePatch);
                // The same repaint a Track Count EDIT earns, not the follow queue:
                // `requestReflect` drains followDirtyNodes / followFull, which this tail
                // sets neither of, so it repaints nothing at all. The count changes how
                // many recorder slots exist (`app/node-param-effects.ts` classes it as a
                // rerender rather than a local repaint), and the panel has to be rebuilt
                // or it goes on showing the value the plan held before the read.
                graph.render();
                refreshInspector();
                planValuesChanged();
                // And a read that FAILED is not a write that succeeded: without this the
                // whole flow reports success while the recorder's value is whatever the
                // plan happened to hold. Caught rather than thrown on, because the throw
                // would leave withDevice reporting that the WRITE failed — it did not, and
                // the operator would go looking for a write that is already on the unit.
                try {
                  assertReadComplete(merged, "track count read issues:");
                } catch (err) {
                  // `showError` clears the status, which is right for a stale "Connecting…"
                  // sitting behind a dialog and wrong for a line the write ended on: a write
                  // that took values back says so there and nowhere else, while the dialog
                  // names the recorder read. Read HERE rather than before the read above,
                  // which takes seconds: a refusal written while it ran (an undo declined
                  // because a device read holds the history) is the newer line and the one
                  // to keep. And only where the write ENDED — a throw leaves a progress
                  // message, which is exactly what the clearing is for.
                  showError(
                    t().error.trackCountReread(errorText(err)),
                    wroteOutcome ? (statusbar.textContent ?? "") : "",
                  );
                }
              }
            }
          }
        });
      } finally {
        writeAbort = null;
        writeBtn.textContent = t().toolbar.writeDevice;
      }
      await offerErrorReport(report);
    });

    // Live sync: connect, read the whole device once (overwriting
    // edits, hence the discard confirm), then mirror each later edit as it
    // happens. The connection is held open for the session and released when the
    // toggle, a write failure, or a plan replacement turns sync off.
    async function activateLive(): Promise<void> {
      try {
        if (!live) return;
        // The link is taken for the whole activation, not just for the connect: the
        // starting readback runs for seconds with liveSessionUp still false, and it was
        // exactly that window in which another action could take the connection away
        // (or be started under one). A session that comes up keeps the holder until
        // deactivateLive gives it back; every other exit gives it back here.
        if (!holdDeviceLink("live")) return;
        // The device scope holds still from here until the session is up or has failed.
        prefs.refresh();
        try {
          await startLiveSession();
        } finally {
          if (!liveSessionUp) releaseDeviceLink("live");
        }
      } finally {
        prefs.refresh();
      }
    }

    async function startLiveSession(): Promise<void> {
      if (!live) return;
      // Connect first (the pre-check): a no-device state is reported plainly,
      // without discarding the user's edits. Only on a live device do we confirm
      // the discard, since live sync overwrites the plan with the device state.
      setStatus(t().status.liveConnecting);
      let device: Connection;
      try {
        device = await vdConnect(modelId);
      } catch (err) {
        showError(connectFailureStatus(err, t().status.liveError));
        return;
      }
      // Past the connect: any exit must release the held connection first. A
      // user-neutral exit (canceled) goes to the status line; a failure (failLive)
      // surfaces as a dialog — both drop the connection first, by its epoch.
      // Opened on the connect, so the elapsed time and the Rust-side command counts —
      // which start there too — measure the same window. Every exit below closes it.
      beginLinkLedger(device.model);
      const abort = async (status: string): Promise<void> => {
        await releaseLive(device.epoch, "off");
        setStatus(status);
      };
      // The note a supplied STREAMING source leads the status line with, kept there when the
      // start fails after the supply.
      let supplied: string | null = null;
      const failLive = async (message: string): Promise<void> => {
        // live.begin() flips LiveSync.active before the awaited follow.begin() /
        // vdWatchLink; if one of those throws we land here with a half-started
        // session, so tear those down too. Otherwise live.isActive() stays true
        // while liveSessionUp stays false, and every later toggle click routes into
        // deactivateLive's early return — a dead toggle. Safe before begin() too:
        // both end()s no-op when nothing was started.
        follow?.end();
        live?.end();
        // The cues, the [Sends] destination and the unit's states the starting read took belong
        // to the session that did not start.
        resetCues();
        resetSendsTarget();
        resetUnitSessionState();
        await releaseLive(device.epoch, "error");
        showError(message, supplied ?? "");
      };
      try {
        // Inside the try, so a confirm that rejects instead of answering takes the same exit a
        // failure does and the connection and the ledger close behind it.
        if (!(await confirmFirmware(device))) return await abort(t().status.canceled);
        if (!(await confirmDiscard())) return await abort(t().status.canceled);
        // A device of a different model maps onto the wrong channels; offer to
        // switch the UI to a fresh plan of the device's model (mirrors fetch).
        const switchTo = await offerModelSwitch(device);
        if (switchTo === "unknown") {
          return await failLive(t().status.liveError(t().error.unknownModel(device.model)));
        }
        if (switchTo === "canceled") return await abort(t().status.canceled);
        // Not while a file flow runs, as for a fetch.
        if (flow.busy) return await abort(t().status.busyFileFlow);
        // Hold off every wholesale plan replacement until the session is established
        // (cleared in the finally below). The read mutates `plan` in place and
        // live.begin() snapshots it as device truth, so a New/Open/switch landing in
        // between would either corrupt the read or enshrine the swapped-in plan.
        flow.deviceReadInFlight = true;
        // The plan on screen takes no edit while a read that carries a switch runs, as for a
        // fetch (cleared as the read returns or throws, and in the finally below).
        if (switchTo) beginSwitchRead();
        midi?.probeMark("live:read:start");
        // Follow USB, which the readback does not carry, is read on the same connection,
        // first inside the read: a failure throws to failLive with nothing merged, and the
        // badge takes the value only once the read has landed complete.
        //
        // A read that carries a model switch runs against a copy of the switch's plan,
        // as a fetch's does (see there).
        //
        // live.begin() then snapshots through the same scope filter, so a kept
        // scene-external value is neither written back nor tracked. The LCD selection is
        // read beside Follow USB, for the same reason and with the same failure.
        let followUsb = false;
        let lcdSelection = 0;
        let sendsRead: number | null = null;
        let setupRead: DeviceSetup | null = null;
        let modeRead: number | null = null;
        let sceneRead: { current: number | null; cursor: number | null } = { current: null, cursor: null };
        let integrationRead: DawIntegration | null = null;
        let cardRead: boolean | null = null;
        let cueRead: string[] = [];
        let merged: MergedRead | null;
        try {
          merged = await readIntoPlan(
            readTarget(switchTo),
            async (into) => {
              followUsb = await readFollowUsb();
              lcdSelection = await readLcdSelection();
              if (experimentalOn) {
                sendsRead = await readSendsTarget();
                setupRead = await readDeviceSetup(getModel(switchTo ? switchTo.modelId : modelId));
                modeRead = await vdGet(PARAMS.OPERATION_MODE.id, 0, 0);
                sceneRead = {
                  current: await vdGet(PARAMS.CURRENT_SCENE.id, 0, 0),
                  cursor: await vdGet(PARAMS.SCENE_CURSOR.id, 0, 0),
                };
                integrationRead = await readDawIntegration();
                if (getModel(switchTo ? switchTo.modelId : modelId).hasSD)
                  cardRead = (await vdGet(PARAMS.SD_CARD.id, 0, 0)) !== 0;
              }
              // On the device's model: a read that carries a switch reads for the plan it
              // switches to.
              cueRead = await readCues(getModel(switchTo ? switchTo.modelId : modelId));
              return applyDeviceStateScoped(into);
            },
            switchTo ? undefined : planWrites,
            // Paired with the witness above, as a fetch's is.
            switchTo ? undefined : (ctx) => sourceChoiceHoldKeys(getModel(modelId), ctx),
            // Only a complete read merges (below).
            (read) => !read.errors.length,
          );
        } finally {
          switchRead = null;
        }
        midi?.probeMark("live:read:end");
        if (!merged) return await abort(t().status.canceled);
        // A partial read would leave the plan holding defaults where the device was not
        // heard from, and the live snapshot would enshrine those as device truth — the
        // first sideEffect edit then converges the whole plan and writes them over the
        // real values. Live sync needs a complete read to start from, so an incomplete one
        // merges nothing into the plan, applies no switch and leaves the undo history as it
        // was.
        if (merged.errors.length) {
          console.warn("live readback issues:", merged.errors);
          return await failLive(
            t().status.liveError(linkFailureIn(merged.errors) ?? t().error.liveReadIncomplete(merged.errors.length)),
          );
        }
        if (switchTo && !applyModelSwitch(switchTo)) {
          await releaseLive(device.epoch, "error");
          return;
        }
        // The undo history re-bases as the read lands, as a fetch's does: an edit made while
        // the session is still registering stays undoable, and a read that did not land
        // (cancelled, failed, stopped before it, or incomplete) leaves the history alone.
        planReadFromDevice();
        // This read established the unit's rate, so a rate an earlier session was told about
        // decides nothing in this one. No follow read is in flight here and no notify can
        // arrive before follow.begin() below.
        announcedRates.length = 0;
        noteMergeConflicts(merged);
        notePatchFromDevice(merged.devicePatch);
        takeRefusedEdits(merged);
        // STREAMING holds a source whatever the unit answered, so a read that found none
        // supplies one before the board is drawn.
        supplied = supplyUnsourced(merged);
        plan.unreadNodes = merged.unreadNodes;
        rerenderPlan();
        dirty = false;
        // Set before the session is up, so the badge is already right when it becomes
        // the session's control (syncDeviceActionUi) — the only Follow USB control while
        // live.
        setFollowUsbBadge(followUsb);
        // The unit announces the side of the channel it has selected and of no other, so
        // that channel's side is taken from the unit here — over one switched in the app
        // while the read ran — and every other stereo channel keeps the side the app shows.
        const lcd = decodeLcdSelection(getModel(modelId), lcdSelection);
        if (lcd) switchSide(lcd.channel, lcd.side, "device");
        // What the unit has cued replaces whatever a MIDI control switched while no session
        // was up.
        resetCues();
        if (cues.replace(cueRead)) renderCues();
        resetSendsTarget();
        sendsTarget = sendsRead === null ? null : decodeSendsTarget(getModel(modelId), sendsRead);
        resetUnitSessionState();
        if (setupRead) unitSetup = setupRead;
        unitOperationMode = modeRead;
        unitScene = { current: sceneRead.current, cursor: null, tab: null };
        if (sceneRead.cursor !== null) takeUnitCursor(sceneRead.cursor);
        unitIntegration = integrationRead;
        unitCardIn = cardRead;
        // A count left by a session that ended without the line that would have carried it
        // belongs to that session; this one's first flush starts from nothing.
        liveAdopted = 0;
        // From here until the session is declared up, a failure is recorded rather than
        // dropped (stopLiveOnError).
        liveStartFailure = null;
        liveStarting = true;
        // The copy the starting read ran against: without it an edit made during the
        // multi-second read is snapshotted as a value the device was already given.
        live.begin(merged.deviceView);
        // Both registrations are awaited before the session counts as up. Without
        // the notify stream the app is blind to device-side edits and the next
        // converge writes the plan back over them; without the link watch an idle
        // drop freezes the session instead of ending it. Either failure throws to
        // failLive rather than starting a session that cannot do its job.
        await follow?.begin();
        await vdWatchLink(() => stopLiveOnError(t().error.shell.deviceLost));
        // …and follow is still following. A structural edit can flush between `live.begin`
        // above and this line; that flush asks the follow layer to re-register, and a
        // refusal there stops follow and reports through `stopLiveOnError`, which returns
        // without doing anything while `liveSessionUp` is false. The lines below are what
        // would declare the session up, so the check belongs in front of them: otherwise
        // the app says "Live sync on" over a follow that discards every notify, and
        // nothing restarts it.
        if (follow && !follow.isActive()) throw new Error(t().error.liveFollowStopped);
        // …and the live half is still sending: a flush that failed in the same window, or a
        // read in front of a write that failed there, recorded its cause and ended it.
        if (liveStartFailure !== null || !live.isActive())
          throw new Error(liveStartFailure ?? t().error.liveSyncStopped);
        // Remember which generation the session holds, so deactivateLive releases
        // exactly this one even when its disconnect lands after a later connect.
        liveEpoch = device.epoch;
        liveSessionUp = true;
        // A Clip Safe the unit announced while the session was starting shows from here.
        paintClipSafe();
        // A read the watch makes can be the one that finds the link dead (a device-lost push
        // is consumed by whichever command is reading), and the shell latches without a link
        // event then, so a latched cause there ends the session the way any other does.
        paramWatch?.start(device.model, (err) => {
          const cause = linkFailureIn([err instanceof Error ? err.message : String(err)]);
          if (cause) stopLiveOnError(cause);
        });
        fxReseeds.clear();
        setLiveUi(true);
        // The flags were read before the notify stream was registered; a press on the unit in
        // between announced itself to nobody.
        rereadCues();
        // The destination too, and the glass takes it now the session counts as up.
        rereadSendsTarget();
        // …and Software Integration, which a DAW taking or letting go of the unit can have moved.
        rereadDawIntegration();
        // …and the scene pointers, which a recall, a store or the cursor on the unit can have moved.
        rereadScenePointers();
        // …and the microSD card, which can have gone in or come out.
        rereadCardIn();
        simView?.unitStateChanged();
        // An edit made while the starting read ran had no session to flush through, so the
        // plan differs from the snapshot the session just took with nothing else left to
        // schedule the send — whether the read held that key, lost it, or found the unit
        // already on the value. A source the read itself supplied is not an edit and waits
        // for one, as on a fetch. The reconcile paths reach the same send-back through
        // `reapplyHeld`.
        if (merged.authored) live.schedule();
        const on = t().status.liveOn(device.model, merged.applied);
        setStatus(withSwitchNotes(supplied ? [supplied, on].join(" — ") : on, merged));
      } catch (err) {
        await failLive(t().status.liveError(errorText(err)));
      } finally {
        liveStarting = false;
        flow.deviceReadInFlight = false;
        switchRead = null;
        // The MIDI gate's reported window ends with the latch (see MidiEngine.gateReleased).
        midi?.gateReleased();
        // Gated on the session, not on reaching here: this block is also where a read
        // that threw, was cancelled or came back incomplete lands, and so does a start that
        // failed after its read. None of those establishes that the plan is the unit's
        // state, which is what licenses putting its values on the controller and the bus
        // it shares. With the session up, the plan is the unit's own.
        if (liveSessionUp) midi?.liveReadSettled();
      }
    }

    const liveBtn = $<HTMLButtonElement>("btn-live");
    liveBtn.hidden = false;
    // Rapid repeat: an activation is a long async flow (connect / confirms /
    // full read) during which the toggle is neither on nor off — a second click
    // there must not start a second concurrent session. Deactivation is
    // synchronous and stays outside the latch, so an active session always
    // turns off immediately.
    const startLive = guarded(() => activateLive());
    restartLiveForScope = () => {
      if (!live?.isActive()) return;
      deactivateLive(t().status.liveRescoped);
      // The link goes back once the session's last round trip is answered; a start before
      // then is refused as a link another action holds.
      void liveReleased.then(() => startLive());
    };
    liveBtn.addEventListener("click", () => {
      if (live?.isActive()) deactivateLive(t().status.liveOff);
      else startLive();
    });
  }

  // External MIDI control: map controller messages onto console controls (with
  // MIDI learn) and feed edits back to the controller. Incoming edits repaint
  // through the coalesced follow reflect (a controller sweep arrives at wire
  // rate) and run markChanged via onApplied, so Live sync mirrors them too.
  midi = new MidiControl({
    getModel: () => getModel(modelId),
    getPlan: () => plan,
    // A CUE control switches the flag while a session is up, and is refused otherwise.
    // …and a delay time tempo Sync drives is written to the unit while one is up, as on the glass.
    cue: {
      has: (node) => cues.has(node),
      set: (node, on) => setCue(node, on, "app"),
      enabled: () => liveSessionUp,
      writeHeld: (param, y, value) => writeHeldDeviceValue(param, y, value),
    },
    onApplied: (control, mirrored, keys) => {
      markChanged("midi", [
        ...keys,
        ...alignLinkedSendPans(plan, withLinkedPartner(getModel(modelId), plan, control.node)),
      ]);
      followDirtyNodes.add(control.node);
      // A channel's input-stage control writes the jack or source it reads.
      if (control.writes?.kind === "node" && control.writes.node) followDirtyNodes.add(control.writes.node);
      const partner = mirrored ? partnerChannel(getModel(modelId), control.node) : undefined;
      if (partner) followDirtyNodes.add(partner);
      requestReflect();
      // An app edit like any other: the entry markChanged opened commits at its own
      // boundary, which records the keys as the operator's and makes the edit undoable.
      // No wire repaint here: the reflect requested above redraws them. Its direct
      // branch ends in graph.repaintDirtyNodes, whose own tail is redrawWires, and both
      // sites carry the same `!graphHost.hidden` guard — so a toggle's wire dimming
      // lands either way, at most one reflect window (REFLECT_MIN_MS) later.
    },
    // The operator-started latches, a strict subset of what the history refuses under:
    // a Fetch or a Live-sync start holds the plan for seconds and the latter then
    // snapshots it as device truth, a file flow can replace the plan outright, and a
    // destructive run holds the device link while it verifies the unit against what it
    // wrote. That last one is the only latch here whose reason is the DEVICE rather
    // than the plan: with Live sync off a refused message would have edited the plan
    // and gone no further, but a controller is a physical surface an operator keeps
    // moving, and a run is the one window where the app must not let what that moves
    // become a device write. Read off the link holder rather than a flag of its own,
    // so what refuses a message and what refuses a second connection cannot diverge.
    // The follow-side reads (followReads) are deliberately NOT here, unlike in
    // PlanHistory.blocked: they recur once per flush window of a 1-knob drag, so
    // refusing in them would make an external desk stutter continuously — and an edit
    // made inside one now survives it (readback.readIntoPlan merges rather than
    // assigns), which is what makes leaving them open safe. A modal is not here either:
    // a MIDI desk is a second physical surface, and the panel that configures it is
    // itself counted by modalOpen(). A read that carries a model switch says so instead
    // (see switchRead).
    blocked: () =>
      switchRead ? t().status.busySwitchRead : flow.busy || deviceLinkHolder === "run" ? t().status.midiBusy : null,
    // Both arming surfaces repaint: learn mode, the armed control and the mapping
    // set all decide what they draw, and a tuning screen open over the console is
    // the one the operator is looking at.
    onLearnChanged: () => {
      consoleView.refresh();
      dynScreen.refresh();
    },
    onStatus: setStatus,
  });
  $("btn-midi").addEventListener("click", () => midi?.toggleWindow());

  // Device setup: the unit's SETUP > GENERAL settings — brightness, auto power off,
  // the date/time formats and time zone, the HDMI and USB Main pages, the menu
  // language, and the user-defined knob assignments. None of them is represented by
  // a node on the graph or a strip on the console, and none belongs to the plan: a
  // plan travels between units as a file and a link, and writing one absolutely
  // would push this operator's screen brightness and knob assignments onto someone
  // else's hardware.
  //
  // Batch, not live: the screen opens on values just read, edits accumulate in the
  // modal, and Apply sends only the differences. The read happens here rather than
  // inside the panel because a failed read must leave the screen unopened — showing
  // a half-established baseline invites applying a diff against values that were
  // never read, which is what the standing device-link rule forbids.
  //
  // No faceplate readout of the unit's clock: the URX exposes no writable clock and
  // does not take the time from a USB host either, so displaying a clock nobody can
  // correct earns nothing. If a way to set it turns up, that readout is also what
  // would make the Time Zone and Display Format rows tangible.
  deviceSetup = new DeviceSetupPanel({
    model: () => getModel(modelId),
    confirmDiscard: () => confirmDialog(t().confirm.deviceSetupDiscard),
    apply: async (writes, changed) => {
      if (liveSessionUp) return applySetupLive(writes, changed);
      let applied = false;
      await withCheckedDevice(
        "device-setup",
        t().status.deviceSetupApplying,
        (message) => t().error.deviceSetupWrite(message),
        async () => {
          await sendDeviceSetup(writes);
          applied = true;
          if (unitSetup) unitSetup = withSetupWrites(unitSetup, writes);
          setStatus(t().status.deviceSetupApplied(changed));
        },
      );
      simView?.unitStateChanged();
      return applied;
    },
  });

  /** Write the screen's changes over the live session's connection, behind every unit write queued
   *  so far. A write that fails ends the session, as any other session write does. */
  async function applySetupLive(writes: SetupWrite[], changed: number): Promise<boolean> {
    let applied = false;
    const run = cueWrites.then(async () => {
      if (!liveSessionUp) return;
      await live?.idle();
      if (!liveSessionUp) return;
      await sendDeviceSetup(writes);
      applied = true;
      if (unitSetup) unitSetup = withSetupWrites(unitSetup, writes);
      setStatus(t().status.deviceSetupApplied(changed));
    });
    cueWrites = run.catch((err: unknown) => stopLiveOnError(t().error.deviceSetupWrite(errorText(err))));
    await cueWrites;
    simView?.unitStateChanged();
    return applied;
  }

  /** Read the unit's settings over the live session's connection, behind every unit write queued
   *  so far; null when the session ended first or the read failed, which ends it. */
  async function readSetupLive(): Promise<DeviceSetup | null> {
    const model = getModel(modelId);
    let read: DeviceSetup | null = null;
    const run = cueWrites.then(async () => {
      if (!liveSessionUp) return;
      await live?.idle();
      if (!liveSessionUp) return;
      read = await readDeviceSetup(model);
    });
    cueWrites = run.catch((err: unknown) => stopLiveOnError(t().error.deviceSetupRead(errorText(err))));
    await cueWrites;
    return liveSessionUp && getModel(modelId) === model ? read : null;
  }

  // One read-and-open at a time: a second pick while the first read is outstanding would open
  // the screen again on its own read and replace whatever was edited on the first.
  $("btn-device-setup").addEventListener(
    "click",
    guarded(async () => {
      // A live session holds the one connection, so the screen reads over it rather than opening
      // a second one.
      if (liveSessionUp) {
        setStatus(t().status.deviceSetupReading);
        const read = await readSetupLive();
        if (!read) return;
        unitSetup = read;
        simView?.unitStateChanged();
        setStatus(t().status.deviceSetupRead);
        deviceSetup?.open(read);
        return;
      }
      let setup: DeviceSetup | null = null;
      await withCheckedDevice(
        "device-setup",
        t().status.deviceSetupReading,
        (message) => t().error.deviceSetupRead(message),
        async () => {
          setup = await readDeviceSetup(getModel(modelId));
          unitSetup = setup;
        },
      );
      simView?.unitStateChanged();
      if (setup) {
        setStatus(t().status.deviceSetupRead);
        deviceSetup?.open(setup);
      }
    }),
  );

  // Experimental-only menu group (its separator + the self-test): the self-test
  // is a diagnostic that briefly overwrites every parameter, so it stays behind
  // the flag — MIDI control, write, and live sync do not.
  // Import a URX settings file (.urxf) — what the unit itself writes to microSD
  // from SETUP > SAVE — onto the plan already open, through the same device→plan
  // inverse a fetch uses. Nothing is sent to hardware: the file is the source.
  //
  // Two things the file cannot supply, both surfaced in the confirm: it names no
  // model (its header reads "URX" for every variant, so the operator vouches for
  // the selected one), and it holds no editing state, so layout / hidden / notes
  // stay as they are rather than being reset.
  // `load` fetches the bytes when the import is actually going ahead (a dialog
  // that returns null was canceled). Reading and parsing share one failure
  // surface, so neither entry point below carries its own catch.
  async function importSettings(load: () => Promise<{ bytes: Uint8Array; name: string } | null>): Promise<void> {
    // Same latch as the plan-open flows: the import shares their dialog chain
    // (confirms + a file dialog), so rapid repeat across entry points is ignored.
    await fileFlow(() => importSettingsFlow(load));
  }

  // Whether the link is held by anything but a session that is up: an import made during a
  // session is an edit the session writes to the unit, while any other holder — a Live sync
  // still connecting among them — is mid-flow on the plan the import would replace.
  const importBlocked = (): boolean => deviceLinkHolder !== null && !(deviceLinkHolder === "live" && liveSessionUp);

  async function importSettingsFlow(load: () => Promise<{ bytes: Uint8Array; name: string } | null>): Promise<void> {
    if (importBlocked()) {
      setStatus(t().status.deviceLinkBusy);
      return;
    }
    let name = "";
    let current;
    try {
      const doc = await load();
      if (!doc) return;
      name = doc.name;
      // CURRENT is the unit's live settings. Stored scenes are separate chunks with
      // no place in a plan — their names exist only in the file's scaffolding.
      current = parseUrxf(doc.bytes).chunks.find((chunk) => chunk.name === "CURRENT");
      if (!current) throw new UrxfError("noCurrent");
    } catch (err) {
      showError(t().status.settingsError(err instanceof UrxfError ? t().error.urxf[err.code] : errorText(err)));
      return;
    }
    if (!(await confirmDiscard())) return;
    // The confirms ask about a session, or about none, as they find it.
    const live = liveSessionUp;
    if (!(await confirmDialog((live ? t().confirm.importSettingsLive : t().confirm.importSettings)(name, modelId)))) {
      setStatus(t().status.canceled);
      return;
    }
    // During a session the file can change how the unit is clocked, which is asked about
    // before anything is imported.
    const source = paramSourceOf(current);
    const clock = live ? await confirmImportClock(source) : { followUsb: undefined, takeRate: true };
    if (!clock) return;
    // Re-checked HERE, not only at the flow's entry. This is the one wholesale plan
    // replacement that does not go through `loadPlan` — it mutates the module plan in
    // place — so it has no share of that backstop, and the entry check is separated
    // from the mutation by the confirm dialogs. Those leave the window running, so a
    // device action can take the link behind them — a write converging the plan object
    // this is about to overwrite key by key, or a read merging into it. A session that
    // came up behind the confirms would take every imported value with no confirm of its
    // own, so the import goes ahead only in the state the confirms asked about.
    if (flow.deviceReadInFlight) {
      setStatus(t().status.busyDeviceRead);
      return;
    }
    if (importBlocked()) {
      setStatus(t().status.deviceLinkBusy);
      return;
    }
    if (liveSessionUp !== live) {
      setStatus(t().status.canceled);
      return;
    }
    // The file's Follow USB goes to the unit first, so the rate the session then writes lands
    // on a unit clocked the way the file says.
    if (live && clock.followUsb !== undefined) {
      try {
        await setFollowUsb(clock.followUsb);
        setFollowUsbBadge(clock.followUsb);
      } catch (err) {
        stopLiveOnError(errorText(err));
        return;
      }
    }
    let result: ReadbackResult;
    let rateKept = false;
    // During a session the import is one undo step of its own, so the edits before it close here.
    if (live) planHistory?.commit();
    try {
      // A settings FILE, not the connected unit: its values are the same kind of thing a plan
      // document supplies, so they are recorded as such. Calling them the unit's would claim
      // the connected device holds them, which a file written by another unit does not.
      const beforeImport = clonePlanState(plan);
      result = await applySourceState(getModel(modelId), plan, source);
      // A rate outside the session stays the unit's.
      if (live && !clock.takeRate && plan.sampleRate !== beforeImport.sampleRate) {
        setPlanSampleRate(plan, beforeImport.sampleRate);
        rateKept = true;
      }
      markSource(plan, patchContestNames(diffPlans(beforeImport, plan)), "load");
    } catch (err) {
      showError(t().status.settingsError(errorText(err)));
      return;
    }
    if (result.errors.length) console.warn("settings import issues:", result.errors);
    // During a session the import is an edit: the session writes it to the unit, and it is one
    // undo step on top of the history it found. Otherwise it settled the plan the way a read does.
    const edit = live && liveSessionUp;
    // Same provenance as a device fetch: nodes whose values did not come through
    // still show their plan default, and the graph flags them as such. During a session
    // they hold what the session read from the unit, so none is flagged.
    if (!edit) plan.unreadNodes = result.unreadNodes;
    rerenderPlan(edit ? "keep" : "reset");
    dirty = true;
    if (edit) {
      markChanged("ui");
      planHistory?.commit();
    } else planReadFromDevice();
    const unread = result.unreadNodes.size;
    setStatus(
      withSwitchNotes(
        result.errors.length
          ? t().status.settingsPartial(result.applied, result.errors.length, unread)
          : t().status.settingsImported(name, result.applied),
      ) + (rateKept ? t().status.settingsRateKept(formatRate(plan.sampleRate)) : ""),
    );
    await offerErrorReport(
      result.errors.length
        ? { filename: `${modelId}-import-errors.md`, markdown: formatReadbackReport(modelId, result) }
        : null,
    );
  }

  experimentalEnabled().then((enabled) => {
    if (!enabled) return;
    experimentalOn = true;
    prefs.refresh();
    for (const el of document.querySelectorAll<HTMLElement>("[data-experimental-only]")) el.hidden = false;
    simView = buildSimView();
    if (simViewPending) setView("sim");

    // The link ledger's readout. The counters and the log run in every desktop build;
    // this is the only part that is a diagnostic, so it is the only part gated. Built
    // here rather than at startup so a build that never shows it never makes it — and
    // it picks up a session already running, since the gate resolves asynchronously.
    if (linkLedger) {
      linkStatsView = new LinkStatsView($("link-stats"), {
        read: () => linkLedger.read(),
        logPath: () => linkLedger.path,
        onCopied: setStatus,
      });
      linkStatsView.setSession(linkLedger.active);
    }

    // Arm the settings-file import: the File menu entry (revealed just above) and
    // the drop target, which only accepts .urxf from this registration on.
    dropzone.register("urxf", (file) => importSettings(async () => ({ bytes: await file.bytes(), name: file.name })));
    $("btn-open-settings").addEventListener("click", () =>
      importSettings(async () => {
        const doc = await openBinaryDocument({ ext: "urxf", label: t().filter.settings });
        return doc && { bytes: doc.bytes, name: baseName(doc.path) };
      }),
    );

    // Compare with device (experimental): a read-only pass that reads every
    // parameter the plan implies, records the device's value beside the plan's,
    // and writes nothing. It is the automated counterpart to eyeballing an
    // imported settings file — connect the unit the .urxf came from and compare,
    // and a faithful import shows no differences. Same shape as fetch: connect,
    // confirm firmware, require the model to match, then read; a second click
    // cancels the (serial, link-stalling) sweep. Unlike fetch it does not stop on
    // a read failure — the audit wants every parameter it can still read.
    //
    // The result is always shown, even on a full match, as a full per-parameter
    // log with a compared count and the elapsed time: an instant "matches" is
    // otherwise indistinguishable from a comparison that read nothing, so the
    // report has to make the reads visible rather than asking the operator to
    // trust the verdict.
    const compareBtn = $<HTMLButtonElement>("btn-compare");
    compareBtn.addEventListener("click", async () => {
      if (compareAbort) {
        compareAbort.abort();
        return;
      }
      const controller = new AbortController();
      const { signal } = controller;
      compareAbort = controller;
      compareBtn.textContent = t().toolbar.compareCancel;
      // Built while connected, shown after disconnect — like the fetch/write error
      // reports, so an indefinite modal does not hold the broker connection open.
      let report: string | null = null;
      try {
        await withCheckedDevice("compare", t().status.compareConnecting, t().status.compareError, async (device) => {
          const model = getModel(modelId);
          // Both halves are taken from the plan on screen NOW: the numeric sweep spans
          // seconds, and the plan read again after it can be another document. The input
          // stage of every jack and source is compared, read by a channel or not — the unit
          // keeps them all, and a settings file carries them all.
          const commands = planToCommands(model, plan, "all", { everyInputHolder: true });
          const names = planToNameWrites(model, plan);
          const startedAt = performance.now();
          const { entries, errors } = await comparePlan(commands, signal, model);
          signal.throwIfAborted();
          const { entries: nameEntries, errors: nameErrors } = await compareNames(names);
          const elapsedMs = Math.round(performance.now() - startedAt);
          const reads = [...errors, ...nameErrors];
          const { compared, differ } = compareCounts(entries, nameEntries);
          report = formatCompareReport(device.model, entries, nameEntries, reads);
          setStatus(
            reads.length
              ? t().status.comparePartial(differ, compared, reads.length, elapsedMs)
              : differ
                ? t().status.compareDiff(differ, compared, elapsedMs)
                : t().status.compareMatch(compared, elapsedMs),
          );
        });
      } finally {
        compareAbort = null;
        compareBtn.textContent = t().toolbar.compare;
      }
      // Always shown (match or not): the full log is the point — it lets an
      // instant "all match" be verified as N reads that agreed.
      const m = t().compareReport;
      if (report) showLoadReport(report, { title: m.title, intro: m.intro });
    });

    // Device self-test (experimental): read the device, write a perturbed copy,
    // verify it matches, then restore. It owns its own connection, so it does not
    // go through withDevice. Reports are console.warn'd (not log) so they reach
    // the dev-server log for a headless read.
    const selfTestBtn = $<HTMLButtonElement>("btn-selftest");
    // selfTestAbort (module scope) holds the in-flight run's controller, so a second
    // menu click cancels instead of starting another run (the run can take minutes
    // of serial round-trips, and stalls entirely if the device link drops mid-test).

    // The --self-test launch has nobody to answer a save dialog, so its report goes to
    // the log. In chunks: the whole thing in one console line risks the dev server's
    // forwarding limits, and one line per row would be thousands of frames.
    function logReport(markdown: string): void {
      const CHUNK = 50;
      const rows = markdown.split("\n");
      const total = Math.ceil(rows.length / CHUNK);
      for (let n = 0; n < total; n++) {
        console.warn(`[self-test] report ${n + 1}/${total}\n${rows.slice(n * CHUNK, (n + 1) * CHUNK).join("\n")}`);
      }
    }

    // `headless` = the --self-test launch (see logReport).
    async function runDeviceSelfTest(headless = false): Promise<void> {
      // Taken before the first await, so nothing can slip through between the click
      // and the connect — neither a MIDI message nor a second device action.
      if (!holdDeviceLink("run")) return;
      const controller = new AbortController();
      selfTestAbort = controller;
      selfTestBtn.textContent = t().toolbar.selfTestCancel;
      setStatus(t().status.selfTestRunning);
      try {
        // The settle window is settle.ts's one constant; this site takes the default.
        const report = await runSelfTest(getModel(modelId), undefined, controller.signal);
        // Traces go out as a count, not as their contents: one carries every command of
        // a failing pass's first round, which is a terminal-flooding console line. The
        // report below is where they are read.
        console.warn(
          `[self-test] ${report.aborted ? "CANCELLED" : report.ok ? "PASS" : "FAIL"}`,
          JSON.stringify({ ...report, traces: report.traces.length }),
        );
        if (report.errors.length) console.warn("[self-test] issues:", JSON.stringify(report.errors));
        if (report.residual.length) console.warn("[self-test] mismatches:", JSON.stringify(report.residual));
        const verdicts = summarizeVerdicts(report.unverified);
        const divergence = report.residual.filter((r) => !r.stoppedOn).length;
        const neverCompared = report.residual.length - divergence;
        setStatus(
          report.aborted
            ? cancelledBeforeWriting(report)
              ? t().status.selfTestCancelledUntouched
              : t().status.selfTestCancelled
            : // Before the restore verdict: a refusal wrote nothing, so `restored` is
              // false only because there was nothing to restore — reading it as a failed
              // restore tells the operator their unit may be left perturbed when it was
              // never touched.
              report.phase === "refused"
              ? report.refusal === "modelMismatch"
                ? t().status.selfTestModelMismatch(report.device, modelId)
                : report.refusal === "sideEffectUnheld"
                  ? t().status.selfTestRefusedHead
                  : t().status.selfTestRefused
              : !report.restored
                ? t().status.selfTestRestoreFail
                : report.unverified.length
                  ? t().status.selfTestUnverified(
                      verdicts.confirmed,
                      verdicts.roundTripped,
                      verdicts.refuted,
                      verdicts.untestable,
                    )
                  : report.ok
                    ? t().status.selfTestPass(report.written)
                    : // `residual` holds two things once a pass can stop partway: what the
                      // device answered differently, and what the run never got to ask
                      // about. Only the first is a mismatch, and if there is any of the
                      // second the headline is that the run did not finish.
                      neverCompared > 0
                      ? t().status.selfTestIncomplete(neverCompared)
                      : t().status.selfTestFail(divergence),
        );
        // Two reasons to keep the report, kept apart: the model's unconfirmed mappings
        // (a property of the model, so the run always has those verdicts to send back)
        // and anything the run found wrong. The second is the one that used to have no
        // way out of the console — a failure whose per-param detail is unreadable is a
        // diagnostic that diagnoses nothing.
        const guesses = report.unverified.length > 0;
        const problems = report.residual.length > 0 || report.errors.length > 0 || !report.restored;
        if (headless) {
          if (guesses || problems) logReport(formatSelfTestReport(report));
          return;
        }
        if (!report.aborted && (guesses || problems)) {
          await offerErrorReport(
            { filename: `${modelId}-self-test.md`, markdown: formatSelfTestReport(report) },
            guesses ? t().confirm.selfTestExport : t().confirm.selfTestFailExport,
            t().filter.report,
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.warn("[self-test] ERROR", message);
        showError(connectFailureStatus(err, t().status.selfTestError));
      } finally {
        selfTestAbort = null;
        releaseDeviceLink("run");
        // The MIDI gate's reported window ends with the latch (see MidiEngine.gateReleased),
        // so the next run speaks up again instead of refusing silently.
        midi?.gateReleased();
        selfTestBtn.textContent = t().toolbar.selfTest;
      }
    }

    // A click cancels an in-flight run; otherwise it confirms first (the run is
    // destructive-then-restored) and starts one.
    selfTestBtn.addEventListener("click", async () => {
      if (selfTestAbort) {
        selfTestAbort.abort();
        return;
      }
      if (await confirmDialog(t().confirm.selfTest)) await runDeviceSelfTest();
    });
    // Headless trigger: when launched with --self-test, run it once on startup
    // (no dialog), so it can be driven from the command line without the UI. Behind the
    // first-run consent like every device write, and its one-shot flag is read once the
    // reset gate has passed, so a --reset-storage reload cannot take the flag with it.
    void resetGate
      .then(() => selfTestRequested())
      .then(async (auto) => {
        if (!auto) return;
        if (!consentSettled) console.warn("[self-test] waiting for first-run consent");
        if (await consented) void runDeviceSelfTest(true);
      });

    // Headless trigger (audit): --prepare-modified writes a distinctive silent
    // state to the device and leaves it (no restore), so a scene SAVE/RECALL audit
    // can save and diff it. Reports go to the dev-server log like the self-test.
    void resetGate
      .then(() => prepareModifiedRequested())
      .then(async (auto) => {
        if (!auto) return;
        if (!consentSettled) console.warn("[prepare-modified] waiting for first-run consent");
        if (!(await consented)) return;
        if (!holdDeviceLink("run")) return;
        setStatus(t().status.selfTestRunning);
        try {
          const report = await runPrepareModified(getModel(modelId));
          console.warn(`[prepare-modified] ${report.aborted ? "CANCELLED" : "DONE"}`, JSON.stringify(report));
          if (report.errors.length) console.warn("[prepare-modified] issues:", JSON.stringify(report.errors));
          setStatus(`prepare-modified: wrote ${report.written}, residual ${report.residual}`);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.warn("[prepare-modified] ERROR", message);
          showError(connectFailureStatus(err, t().status.selfTestError));
        } finally {
          releaseDeviceLink("run");
          midi?.gateReleased();
        }
      });
  });
}

$("btn-view-graph").addEventListener("click", () => setView("graph"));
$("btn-view-console").addEventListener("click", () => setView("console"));
$("btn-view-sim").addEventListener("click", () => setView("sim"));
// Restore the last selected view now that the console is wired up. A remembered SIM view
// waits for the experimental gate; the graph stands in for it until then.
const restoredView = detectView();
setView(restoredView === "sim" ? "graph" : restoredView, restoredView !== "sim");
simViewPending = restoredView === "sim";

// Re-resolve the active theme from the current mode and repaint what reads it:
// the SVG graph, the console's scribble ink, and an open tuning screen's plot.
function applyResolvedTheme(): void {
  theme = resolveTheme(themeMode);
  document.documentElement.dataset.theme = theme;
  graph.setTheme(theme);
  // The funnel for surfaces CSS variables cannot repaint on their own. The graph is SVG
  // built from a palette; a tuning screen's plot is a canvas whose theme tokens are read
  // once per render — and auto mode can flip underneath an open one with no press at all.
  // The console is deliberately NOT in this set, and that is a live assumption rather
  // than an oversight: its scribble ink stopped being a stylesheet value when inkOn()
  // began picking black or white from the ground, which for a rail-coloured strip is a
  // theme token — so the ink survives a theme switch computed against the ground that
  // just left. Measured 2026-08-07: harmless, because all five rails resolve to white
  // in BOTH themes, so the stale answer is the right one. Move a rail to a ground where
  // the two themes disagree and this becomes a strip inked for the theme that left;
  // adding consoleView.refresh() here is the fix, and it costs one strip rebuild.
  dynScreen.refresh();
}

function setThemeMode(mode: ThemeMode): void {
  themeMode = mode;
  rememberThemeMode(mode);
  applyResolvedTheme();
  const m = t().status;
  setStatus(mode === "auto" ? m.themeAuto : theme === "dark" ? m.themeDark : m.themeLight);
}

// Follow the OS color scheme live while in auto mode.
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
  if (themeMode === "auto") applyResolvedTheme();
});

labelsBtn.addEventListener("click", () => {
  labelSource = labelSource === "device" ? "model" : "device";
  rememberLabelSource(labelSource);
  graph.setLabelSource(labelSource);
  applyStaticI18n();
  setStatus(labelSource === "device" ? t().toolbar.labelsDevice : t().toolbar.labelsModel);
});

hideOffBtn.addEventListener("click", () => {
  const next = !graph.isHideOffSends();
  graph.setHideOffSends(next);
  rememberHideOffSends(next);
  applyStaticI18n();
  setStatus(next ? t().toolbar.hideOffSends : t().toolbar.showOffSends);
});

// The View menu's SIM Scale choices: the scale the SIM view draws the LCD at.
for (const item of simScaleItems()) {
  item.addEventListener("click", () => {
    const scale = Number(item.dataset.simScale) as SimScale;
    if (!(SIM_SCALES as readonly number[]).includes(scale)) return;
    simView?.setScale(scale);
    rememberSimScale(scale);
    syncSimScaleItems(scale);
  });
}
syncSimScaleItems(detectSimScale());

/** An entry of a toolbar menu: an action, a toggle or a choice that carries aria-checked. */
const MENU_ITEM = ':is([role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"])';

// Wire the File dropdown: open/close, click-outside, and roving keyboard focus
// across its menu items. The panel is positioned fixed (toolbar clips overflow),
// so its coordinates are derived from the trigger each time it opens.
setupMenu($<HTMLButtonElement>("btn-file"), $<HTMLElement>("file-menu"));
// Device actions (desktop only; the whole menu is hidden in a plain browser).
setupMenu($<HTMLButtonElement>("btn-device"), $<HTMLElement>("device-menu"));
// View menu: layout (Arrange) and display toggles (Hide unused / off-sends / labels).
setupMenu($<HTMLButtonElement>("btn-view"), $<HTMLElement>("view-menu"));

function setupMenu(trigger: HTMLButtonElement, panel: HTMLElement): void {
  const items = (): HTMLButtonElement[] =>
    Array.from(panel.querySelectorAll<HTMLButtonElement>(`${MENU_ITEM}:not([disabled]):not([hidden])`));
  let open = false;

  function setOpen(next: boolean, focusFirst = false): void {
    if (next === open) {
      if (next && focusFirst) items()[0]?.focus();
      return;
    }
    open = next;
    trigger.setAttribute("aria-expanded", String(next));
    panel.hidden = !next;
    if (next) {
      const r = trigger.getBoundingClientRect();
      panel.style.top = `${Math.round(r.bottom + 8)}px`;
      panel.style.right = `${Math.round(window.innerWidth - r.right)}px`;
      if (focusFirst) items()[0]?.focus();
      document.addEventListener("pointerdown", onOutside, true);
      document.addEventListener("keydown", onKey, true);
    } else {
      document.removeEventListener("pointerdown", onOutside, true);
      document.removeEventListener("keydown", onKey, true);
    }
  }

  function onOutside(e: PointerEvent): void {
    const target = e.target as Node;
    if (!panel.contains(target) && !trigger.contains(target)) setOpen(false);
  }

  function onKey(e: KeyboardEvent): void {
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      setOpen(false);
      trigger.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      list[(i + 1) % list.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      list[(i - 1 + list.length) % list.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      list[list.length - 1]?.focus();
    }
  }

  trigger.addEventListener("click", () => setOpen(!open, true));
  trigger.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true, true);
    }
  });
  // Each item runs its own action listener; close the menu once one is chosen and put
  // focus back on the trigger, as Escape leaves it. Delegated to the panel so items
  // enabled after setup (the experimental device self-test, disabled at this point) is
  // covered too. An item's async action yields at its first await, so this runs, hides
  // the menu and focuses the trigger before any confirm dialog renders — and a modal
  // the action opens afterwards records the trigger, not the item this hides, as the
  // place to return to on close. The item is looked for on the path the click was
  // dispatched along: an item's own listener may rewrite its contents first (a toggle
  // relabels itself), which leaves a target inside it detached by the time this runs.
  panel.addEventListener("click", (e) => {
    if (!e.composedPath().some((n) => n instanceof Element && n.matches(MENU_ITEM))) return;
    setOpen(false);
    trigger.focus();
  });
}

onLangChange(() => {
  applyStaticI18n();
  refreshInspector();
  consoleView.refresh();
  graph.relocalizeChrome();
  midi?.relocalize();
  // The language row lives in the Preferences modal, so the switch happens with
  // the modal open — rebuild it in the new language. Device setup can be open at
  // the same time (its notes are translated even though its labels are not).
  prefs.refresh();
  deviceSetup?.refresh();
  dynScreen.refresh();
  setStatus(t().status.language(LANG_NAMES[getLang()]));
});

// True while any modal overlay is up: the consent-scrim dialogs (consent, load
// report, rate choice, licenses, Preferences, Device setup, and the drag overlay)
// share the class and toggle [hidden]. MIDI control is not among them — it is a
// window of its own, so it covers nothing here and holds nothing back: an undo
// taken with it open belongs to the plan in this window.
function modalOpen(): boolean {
  return !!document.querySelector(".consent-scrim:not([hidden])");
}

/**
 * Whether a modal was open when the Escape now being handled STARTED.
 *
 * Asking `modalOpen()` in the bubble-phase handler answers about the wrong moment: a
 * modal dismisses in the capture phase without stopping propagation, so by the time the
 * window handler runs the modal has already gone and the test says "no modal" — and the
 * graph's selection, and the inspector's contents with it, were cleared for an Escape
 * addressed to Preferences, the licences notice or Device setup. This listener is
 * registered in the capture phase on `window`, which is the first thing the event
 * reaches, so it records the state the operator's key was actually aimed at. The link
 * ledger's panel dismisses on Escape the same way, so an open one consumes the key too.
 */
let escapeConsumed = false;
window.addEventListener(
  "keydown",
  (e) => {
    if (e.key === "Escape") escapeConsumed = modalOpen() || document.querySelector(".linkbar-pop") !== null;
  },
  true,
);

window.addEventListener("keydown", (e) => {
  // Undo / redo first, and with its own target test: `typing` below is too broad
  // for it (a focused range slider or the model picker owns no undo stack of its
  // own, so bailing there would make Ctrl+Z quietly do nothing right after a slider
  // drag). Its modal and busy refusals live in the hooks, not here. Registered in
  // the bubble phase like the rest of this handler, which is what lets the note
  // editor's stopPropagation shield an in-progress note from the shortcut.
  if (planHistory?.handleKey(e)) return;
  const typing = ["INPUT", "TEXTAREA", "SELECT"].includes((e.target as Element)?.tagName);
  if (typing) return;
  if (e.key === "Delete" || e.key === "Backspace") {
    // Deletion acts on the graph's selection, so it must not fire from the CONSOLE
    // view (graph hidden, and the console renders no deletable kinds) or reach past
    // an open modal overlay to the graph behind it.
    if (graphHost.hidden || modalOpen()) return;
    e.preventDefault();
    graph.deleteSelection();
  } else if (e.key === "Escape") {
    // Gated like the Delete branch above, and for the same reason. A modal closes on
    // Escape in the capture phase without stopping propagation, so this bubble-phase
    // handler then ran too and cleared the graph's selection — and the inspector's
    // contents with it — for an Escape addressed to Preferences, the licences notice or
    // Device setup. The same key closing a console popover cleared the hidden graph's
    // selection behind the console view.
    if (graphHost.hidden || modalOpen() || escapeConsumed) return;
    graph.clearSelection();
  }
});

applyRateConstraints();
setStatus(t().status.loaded(modelId));

// Keyboard measurement harness (ui/keyprobe.ts), dev builds only. Installed here,
// after the keydown handler above, so its chord log can report whether the app had
// already claimed the chord. The branch is statically dropped from a production build.
if (import.meta.env.DEV)
  installKeyProbe({
    onReport: setStatus,
    // The two arms of the IME measurement: the panel repaint as the app runs it (the
    // composition gate decides), and the same repaint with no gate in front of it — the
    // behaviour the gate replaced. The host supplies both because neither is reachable
    // from a module the harness can import, and `pulseMs` so the harness fires at the
    // rate a real device sweep produces rather than at a number of its own.
    refreshInspector,
    rebuildInspector,
    inspectorHost,
    pulseMs: REFLECT_MIN_MS,
  });

// Deep-link entry: a `?plan=<base64url-json>` parameter loads a plan straight
// into the viewer (a generator emits a shareable URL). A decode failure or a
// routing violation surfaces the copyable report instead of loading, leaving the
// default plan on screen. Runs after the initial render so a failure is visible.
async function loadPlanFromUrl(): Promise<void> {
  let encoded: string | null;
  try {
    encoded = new URL(location.href).searchParams.get("plan");
  } catch {
    return; // URL / searchParams unavailable
  }
  if (!encoded) return;
  let text: string;
  try {
    text = await decodePlanParam(encoded);
  } catch (err) {
    // A z link failing on a webview without the deflate-raw codec is a browser
    // limitation, not a broken link — the codec reports it as a typed PlanError.
    showLoadReport(err instanceof PlanError ? t().error[err.code] : t().error.badPlanUrl);
    return;
  }
  loadFromText(text);
}
void loadPlanFromUrl();

// Desktop boot: gate on first-run consent, then check for updates. DEMO is
// statically false in the desktop build, so the browser demo bundle drops the
// update branch (and the updater imports) entirely; the consent gate is skipped
// at runtime there since it has no device control. The call is at the end of the
// module so CONSENT_KEY (a const requireConsent reads) is already initialized.
async function boot(): Promise<void> {
  await resetGate;
  await requireConsent();
  if (!DEMO) {
    if (getSettings().updateCheck) await checkForUpdates();
  }
}

// Desktop --reset-storage: the flag arrives async (after the synchronous init has
// already read localStorage), so clear it and reload once to re-init clean. A
// sessionStorage guard stops the still-present flag from looping the reload.
async function resetStorageIfRequested(): Promise<void> {
  let requested = false;
  try {
    requested = await resetStorageRequested();
  } catch {
    return; // flag query failed (e.g. command unavailable) — nothing to reset
  }
  if (!requested) return;
  try {
    if (sessionStorage.getItem(RESET_DONE_KEY) === "1") return; // already cleared this launch
    localStorage.clear();
    sessionStorage.setItem(RESET_DONE_KEY, "1");
  } catch {
    return; // storage unavailable — skip the reload to avoid a loop
  }
  location.reload();
  await new Promise(() => {}); // hold boot() until the reload takes over
}

const CONSENT_KEY = "urx-disclaimer-accepted";

// The first-run consent's outcome: true once it was given (or where no gate applies), false
// when it was declined. The headless launch actions wait on it, since they reach the device
// layer without anything on screen to press.
let consentSettled = false;
let settleConsent: (agreed: boolean) => void = () => {};
const consented = new Promise<boolean>((resolve) => {
  settleConsent = (agreed) => {
    consentSettled = true;
    resolve(agreed);
  };
});

// First-run consent: the Windows installer shows the same notice, but the macOS
// drag-install and auto-updates bypass it, so gate the desktop app once. Stored
// acceptance survives updates, so an updated user is not asked again.
async function requireConsent(): Promise<void> {
  if (!isTauri()) {
    settleConsent(true);
    return;
  }
  let accepted = false;
  try {
    accepted = localStorage.getItem(CONSENT_KEY) === "1";
  } catch {
    // Storage unavailable: treat as not yet accepted and ask again, rather than
    // letting a throw reject boot() and leave the app running un-gated.
  }
  if (accepted) {
    settleConsent(true);
    return;
  }
  if (await showConsent()) {
    try {
      localStorage.setItem(CONSENT_KEY, "1");
    } catch {
      // ignore (storage may be unavailable; consent will be asked again next launch)
    }
    settleConsent(true);
    return;
  }
  // Declined: the app must not start without consent.
  settleConsent(false);
  await exitApp();
}

// The check and its confirm. Answers once the operator has answered, so the Preferences lock
// (which waits on this) covers the check only: an accepted update's download runs after it.
async function checkForUpdates(): Promise<UpdateCheckOutcome> {
  let update: UpdateInfo | null;
  try {
    update = await checkUpdate();
    if (!update) return { kind: "upToDate" };
    if (!(await confirmDialog(t().confirm.update(update.version)))) {
      return { kind: "declined", version: update.version };
    }
    // The install ends the app, so unsaved edits are asked about first, as a New or an
    // Open asks; declining keeps them and installs nothing.
    if (!(await confirmDiscard())) return { kind: "declined", version: update.version };
  } catch {
    // Best-effort — offline, or no release published yet: the launch check stays silent
    // and a manual check reports it through the Preferences inline note.
    return { kind: "failed" };
  }
  // The install also ends whatever device action is running, so it takes the link: one
  // already holding it refuses the install, and none can start while the bundle downloads.
  if (!holdDeviceLink("update")) return { kind: "busy" };
  // An accepted update is the one outcome that leaves the Preferences modal:
  // the scrim would hide the download status. No-op at the launch check.
  prefs.close();
  setStatus(t().status.updateDownloading);
  void installAccepted(update);
  return { kind: "installing" };
}

// Download, install, and relaunch into the new bundle. "Downloading update…" is on screen with
// the modal closed, so a failure has to clear that status and surface — otherwise it reads as a
// download that never ends.
async function installAccepted(update: UpdateInfo): Promise<void> {
  try {
    // What the app's own exit would save and close, first: on Windows the install ends the
    // process from inside itself.
    await prepareForExit();
    await installUpdate(update.rid);
  } catch (err) {
    releaseDeviceLink("update");
    showError(t().status.updateInstallFailed(errorText(err)));
    return;
  }
  // The new bundle is installed; relaunch into it. Nothing runs past here. A relaunch that
  // fails leaves the installed bundle waiting for the next start, which its line says.
  try {
    await restartApp();
  } catch (err) {
    releaseDeviceLink("update");
    showError(t().status.updateRestartFailed(errorText(err)));
  }
}

void boot();
